import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Article,
  Add,
  Edit,
  Delete,
  Visibility,
  ContentCopy,
  Close,
  PlayArrow,
  ShoppingCart,
  LocalOffer,
  Title,
  TextFields,
  Image,
  KeyboardArrowUp,
  KeyboardArrowDown,
  DragIndicator,
  UnfoldMore,
  UnfoldLess,
  ArrowUpward,
  ArrowDownward,
  North,
  South,
  OpenInNew,
  ViewList,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  ExpandLess,
  FormatListBulleted
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { uploadToS3WithProgress, fallbackToServerUpload } from '../services/s3UploadService';
import { toast } from 'react-toastify';
import BlogPresetSelector from '../components/BlogPresetSelector';
import MobileBlogPreview from '../components/MobileBlogPreview';
import ButtonPresetSelector from '../components/ButtonPresetSelector';
import CenteredVideoPlayer from '../components/CenteredVideoPlayer';
import BlogPostDialog from '../components/BlogPostDialog';
import DeleteBlogDialog from '../components/DeleteBlogDialog';
import DeletePageDialog from '../components/DeletePageDialog';
import ExpandedStatsDialog from '../components/ExpandedStatsDialog';
import { getFramePreset, getPreset, getButtonPreset } from '../textStyle';
import { SmartButton, Link } from '@mui/icons-material';
import { config } from '../config';
import {
  BLOG_ELEMENT_PADDING,
  MOBILE_PHONE_WIDTH,
  MOBILE_PHONE_HEIGHT
} from '../utils/blogConstants';
import {
  calculateMediaHeight,
  calculateElementHeight,
  calculatePageHeight,
  getMaxPageHeight,
  checkPageOverflow
} from '../utils/blogLayoutCalculations';
import {
  generateOfferElementHTML,
  generateAffiliateElementHTML,
  getTotalPages,
  convertOfferElementToBlogElements,
  generateElementHTML
} from '../utils/blogHtmlGenerators';
import { useBlogElementMutations } from '../utils/useBlogElementMutations';

// Helper: compute a reasonable bottom offset for the video overlay button
// so it sits just above the bottom bar in the phone frame. Mirrors (but
// slightly simplifies) the logic used in BlogElementRenderer.
const getOverlayButtonBottomForEditor = (styles = {}) => {
  const ratio = styles.aspectRatio || '16/9';
  const [w, h] = ratio.split('/').map(Number);
  const isVertical = w && h && h > w;

  let dynamicBottom = 40; // px, for horizontal / square videos
  if (isVertical) {
    const ratioValue = h / w;
    if (ratioValue > 2) {
      dynamicBottom = 90; // very tall
    } else if (ratioValue > 1.5) {
      dynamicBottom = 70; // 9:16
    } else if (ratioValue > 1) {
      dynamicBottom = 60; // other portrait
    }
  }
  return `${dynamicBottom}px`;
};

// Utility function to map MongoDB IDs to 5-digit strings
const mapIdToShortString = (id) => {
  if (!id || id === 'N/A') return 'N/A';

  // Convert to string if it's an object
  const idStr = typeof id === 'object' ? (id._id || id).toString() : id.toString();

  // Simple hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < idStr.length; i++) {
    const char = idStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Get positive 5-digit number (10000-99999)
  const shortId = Math.abs(hash) % 90000 + 10000;
  return shortId.toString();
};

// Calendar helper functions
const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month, 1).getDay();
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return 'Select date range';
  if (!startDate) return `Until ${formatDateDisplay(endDate)}`;
  if (!endDate) return `From ${formatDateDisplay(startDate)}`;
  return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  // Parse date string as local date (YYYY-MM-DD format)
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  // Compare date strings directly (YYYY-MM-DD format)
  return date1 === date2;
};

const isDateInRange = (date, startDate, endDate) => {
  if (!startDate && !endDate) return false;
  // Compare date strings directly (YYYY-MM-DD format) - lexicographic comparison works for ISO dates
  if (startDate && endDate) {
    return date >= startDate && date <= endDate;
  } else if (startDate) {
    return date >= startDate;
  } else if (endDate) {
    return date <= endDate;
  }
  return false;
};

const BlogsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offersLoading, setOffersLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Blog creation form state
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '<h1>Headline 1</h1>\n<h2>Headline 2</h2>\n',
    tags: [],
    featuredImage: '',
    published: true,
    mediaTitle: '',
    mediaDescription: ''
  });

  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [createdBlogData, setCreatedBlogData] = useState(null);

  // Unified dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'view', 'edit'
  const [selectedBlogPost, setSelectedBlogPost] = useState(null);
  const [editing, setEditing] = useState(false);

  // Delete blog/page dialog state
  const [deletePageDialogOpen, setDeletePageDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deleteBlogDialogOpen, setDeleteBlogDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [listPublishBusyId, setListPublishBusyId] = useState(null);

  // File upload state
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Saved offer elements state
  const [savedElements, setSavedElements] = useState([]);
  const [elementsLoading, setElementsLoading] = useState(false);
  const [selectedOfferForElements, setSelectedOfferForElements] = useState(null);
  const [selectedElementHtml, setSelectedElementHtml] = useState('');
  const [hoveredElement, setHoveredElement] = useState(null);
  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [currentElementIndex, setCurrentElementIndex] = useState(0);
  const [currentOfferElementPageIndex, setCurrentOfferElementPageIndex] = useState(0);

  // Enhanced blog editor state - Independent Pages System
  const [pages, setPages] = useState([{ id: 1, elements: [] }]); // Start with one page
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [nextElementId, setNextElementId] = useState(1);
  const [nextPageId, setNextPageId] = useState(2);
  const [mobilePreviewMode, setMobilePreviewMode] = useState(true); // Default to mobile preview
  const [selectedElementIndex, setSelectedElementIndex] = useState(null); // Track which element is expanded for editing

  // Helper functions for page management
  const getCurrentPage = () => pages[currentPageIndex];
  const getCurrentPageElements = () => getCurrentPage()?.elements || [];
  const getAllElements = () => {
    const allElements = pages.flatMap(page => page.elements);
    return allElements;
  };

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Overflow alert state
  const [showOverflowAlert, setShowOverflowAlert] = useState(false);
  const [overflowElement, setOverflowElement] = useState(null);
  const [overflowElementIndex, setOverflowElementIndex] = useState(null);

  // Pre-built blog state
  const [preBuiltCategory, setPreBuiltCategory] = useState('');
  const [preBuiltSubcategory, setPreBuiltSubcategory] = useState('');
  const [preBuiltBlogLimit, setPreBuiltBlogLimit] = useState(3);
  const [preBuiltBlogs, setPreBuiltBlogs] = useState([]);
  const [preBuiltLoading, setPreBuiltLoading] = useState(false);
  const [concatenatedBlog, setConcatenatedBlog] = useState(null);
  const [availableAffiliates, setAvailableAffiliates] = useState([]);
  const [selectedAffiliates, setSelectedAffiliates] = useState([]);
  const [headAffiliate, setHeadAffiliate] = useState('');
  // Removed blogMergeLimit - all blogs in subcategory are now included for management
  const [rankingCriteria, setRankingCriteria] = useState('views'); // 'views' or 'revenuePerView'
  const [loadingAffiliates, setLoadingAffiliates] = useState(false);

  // Merged blogs management state
  const [mergedBlogs, setMergedBlogs] = useState([]);
  const [loadingMergedBlogs, setLoadingMergedBlogs] = useState(false);
  const [blogPerformanceData, setBlogPerformanceData] = useState({}); // Map of blogId -> performance data
  const [mergedBlogPerformanceData, setMergedBlogPerformanceData] = useState({}); // Map of mergedBlogId -> performance data
  const [expandedMergedBlog, setExpandedMergedBlog] = useState(null);
  const [blogPageStats, setBlogPageStats] = useState({}); // Map of blogId -> page-level stats array
  const [sourceBlogMap, setSourceBlogMap] = useState({}); // Map of blogId -> full blog object with content // ID of expanded merged blog
  const [expandedBlogStats, setExpandedBlogStats] = useState({}); // Map of blogId -> boolean for expanded stats
  const [pausedBlogs, setPausedBlogs] = useState({}); // Map of mergedBlogId -> Set of paused blog IDs
  const [expandedStatsDialog, setExpandedStatsDialog] = useState(null); // { blogId, blogTitle, offerStats, views, clicks, sales, revenue } or null
  const [mergedBlogsLimit, setMergedBlogsLimit] = useState(3); // Limit for merged blogs display (horizontal)
  const [blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit] = useState({}); // Map of mergedBlogId -> limit for blogs within that merged blog (vertical)
  const [mergedBlogsStartDate, setMergedBlogsStartDate] = useState(''); // Start date for filtering merged blogs
  const [mergedBlogsEndDate, setMergedBlogsEndDate] = useState(''); // End date for filtering merged blogs
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [dateRangePickerAnchor, setDateRangePickerAnchor] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date());
  const [includeToday, setIncludeToday] = useState(false);

  // Load categories and brands on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await apiService.getCategoriesForSelect();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
        setError('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    const loadBrands = async () => {
      try {
        const brandsData = await apiService.getBrands();
        setBrands(brandsData.brands || brandsData || []);
      } catch (error) {
        console.error('Error loading brands:', error);
        setError('Failed to load brands');
      }
    };

    loadCategories();
    loadBrands();
  }, []);

  // Load blog posts
  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        console.log('Loading blog posts...');
        const postsData = await apiService.getMyBlogPosts({ limit: 100 });
        console.log('Blog posts response:', postsData);
        setBlogPosts(postsData.posts || []);
      } catch (error) {
        console.error('Error loading blog posts:', error);
        setError('Failed to load blog posts: ' + error.message);
      }
    };

    loadBlogPosts();
  }, []);

  // Restore view state from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const viewBlogId = urlParams.get('view');
    const editBlogId = urlParams.get('edit');

    // Only restore state if we have URL parameters and we're not in the middle of creating a new blog
    if ((viewBlogId || editBlogId) && blogPosts.length > 0 && !isEditMode && !isViewMode) {
      if (viewBlogId) {
        const post = blogPosts.find(p => p._id === viewBlogId);
        if (post) {
          console.log('Restoring view state for blog:', post);
          handleViewBlog(post, false); // false = don't update URL
        }
      } else if (editBlogId) {
        const post = blogPosts.find(p => p._id === editBlogId);
        if (post) {
          console.log('Restoring edit state for blog:', post);
          handleEditBlog(post, false); // false = don't update URL
        }
      }
    }
  }, [blogPosts, location.search, isEditMode, isViewMode]);

  // Load offers when brand, category, or subcategory changes
  useEffect(() => {
    const loadOffers = async () => {
      if (selectedBrand && selectedCategory && selectedSubcategory) {
        setOffersLoading(true);
        try {
          // First get offers by category and subcategory (expired offers only when editing an existing blog)
          const offersData = await apiService.getOffersByCategory(selectedCategory, selectedSubcategory, { includeExpired: editingBlogPost ? 'true' : 'false' });
          let filteredOffers = offersData.offers || [];

          // Then filter by brand
          filteredOffers = filteredOffers.filter(offer => offer.brand?._id === selectedBrand);
          console.log(`Found ${filteredOffers.length} offers for brand ${selectedBrand}`);

          // Filter out offers that don't have saved offer elements
          const offersWithElements = [];
          for (const offer of filteredOffers) {
            try {
              const elementsData = await apiService.getSavedOfferElementsByOffer(selectedBrand, offer._id);
              const elements = elementsData.elements || elementsData || [];
              console.log(`Offer ${offer._id} has ${elements.length} elements`);
              if (elements.length > 0) {
                offersWithElements.push(offer);
              }
            } catch (error) {
              console.log(`No elements found for offer ${offer._id}:`, error.message);
            }
          }

          console.log(`Final result: ${offersWithElements.length} offers with elements`);
          setOffers(offersWithElements);
        } catch (error) {
          console.error('Error loading offers:', error);
          setOffers([]);
        } finally {
          setOffersLoading(false);
        }
      } else {
        setOffers([]);
      }
    };

    loadOffers();
  }, [selectedBrand, selectedCategory, selectedSubcategory]);

  const handleCategoryChange = async (event) => {
    const categoryId = event.target.value;
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
    setSelectedBrand('');
    setOffers([]);
    setSelectedOffer(null);
    setSelectedOfferForElements(null);
    setSavedElements([]);

    // Reset to all brands when category changes
    try {
      const brandsData = await apiService.getBrands();
      setBrands(brandsData.brands || brandsData || []);
    } catch (error) {
      console.error('Error loading all brands:', error);
      setBrands([]);
    }
  };

  const handleSubcategoryChange = async (event) => {
    const subcategoryId = event.target.value;
    setSelectedSubcategory(subcategoryId);
    setSelectedBrand('');
    setOffers([]);
    setSelectedOffer(null);
    setSelectedOfferForElements(null);
    setSavedElements([]);

    // Load brands that have offers with saved elements for this category/subcategory
    if (selectedCategory && subcategoryId) {
      try {
        // Expired offers only count when editing an existing blog; new blogs can't use them
        const brandsData = await apiService.getBrandsWithOffers(selectedCategory, subcategoryId, { includeExpired: editingBlogPost ? 'true' : 'false' });
        const allBrands = brandsData.brands || [];

        console.log(`Found ${allBrands.length} brands with offers in subcategory ${subcategoryId}`);

        // Filter brands to only include those that have offers with saved elements
        const brandsWithElements = [];
        for (const brand of allBrands) {
          try {
            // Get offers for this brand in this category/subcategory
            const offersData = await apiService.getOffersByCategory(selectedCategory, subcategoryId, { includeExpired: editingBlogPost ? 'true' : 'false' });
            const brandOffers = offersData.offers?.filter(offer => offer.brand?._id === brand._id) || [];

            console.log(`Brand ${brand.name} (${brand._id}) has ${brandOffers.length} offers in this subcategory`);

            // Check if any of these offers have saved elements
            let hasOffersWithElements = false;
            for (const offer of brandOffers) {
              try {
                const elementsData = await apiService.getSavedOfferElementsByOffer(brand._id, offer._id);
                const elements = elementsData.elements || elementsData || [];
                if (elements && elements.length > 0) {
                  console.log(`Brand ${brand.name} has ${elements.length} saved elements for offer ${offer._id}`);
                  hasOffersWithElements = true;
                  break;
                }
              } catch (error) {
                console.log(`Error checking elements for brand ${brand._id}, offer ${offer._id}:`, error.message);
                // Continue checking other offers
              }
            }

            if (hasOffersWithElements) {
              brandsWithElements.push(brand);
              console.log(`✅ Added brand ${brand.name} to list (has offer elements)`);
            } else {
              console.log(`⏭️ Skipped brand ${brand.name} (no saved elements found)`);
            }
          } catch (error) {
            console.error(`Error checking brand ${brand._id} (${brand.name}):`, error.message);
          }
        }

        console.log(`Found ${brandsWithElements.length} brands with offer elements out of ${allBrands.length} total brands`);
        setBrands(brandsWithElements);
      } catch (error) {
        console.error('Error loading brands with offer elements:', error);
        setBrands([]);
      }
    } else {
      // Reset to all brands if no category/subcategory selected
      try {
        const brandsData = await apiService.getBrands();
        setBrands(brandsData.brands || brandsData || []);
      } catch (error) {
        console.error('Error loading all brands:', error);
        setBrands([]);
      }
    }
  };

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    setSelectedBrand(brandId);
    setSelectedOffer(null);
    setSelectedOfferForElements(null);
    setSavedElements([]);
  };

  // Load saved offer elements for a specific brand
  const loadSavedElements = async (brandId) => {
    if (!brandId) {
      setSavedElements([]);
      return;
    }

    setElementsLoading(true);
    try {
      const elementsData = await apiService.getSavedOfferElementsByBrand(brandId);
      setSavedElements(elementsData.elements || []);
    } catch (error) {
      console.error('Error loading saved elements:', error);
      setSavedElements([]);
    } finally {
      setElementsLoading(false);
    }
  };

  // Load saved offer elements for a specific brand and offer
  const loadSavedElementsByOffer = async (brandId, offerId) => {
    if (!brandId || !offerId) {
      setSavedElements([]);
      return;
    }

    setElementsLoading(true);
    try {
      const elementsData = await apiService.getSavedOfferElementsByOffer(brandId, offerId);
      setSavedElements(elementsData.elements || []);
    } catch (error) {
      console.error('Error loading saved elements by offer:', error);
      setSavedElements([]);
    } finally {
      setElementsLoading(false);
    }
  };

  // Reset blog editor state
  const resetBlogEditorState = () => {
    setPages([{ id: 1, elements: [] }]);
    setCurrentPageIndex(0);
    setNextElementId(1);
    setNextPageId(2);
    setIsEditMode(false);
    setIsViewMode(false);
    setEditingBlogPost(null);
    setEditLoading(false);
    setSelectedBlogPost(null);
    setCreatedBlogData(null);
    setBlogForm({
      title: '',
      content: '<h1>Headline 1</h1>\n<h2>Headline 2</h2>\n',
      featuredImage: '',
      tags: [],
      mediaTitle: '',
      mediaDescription: ''
    });
    // Clear URL parameters
    navigate('/blogs', { replace: true });
  };

  // Load saved elements when brand changes
  useEffect(() => {
    if (selectedBrand) {
      loadSavedElements(selectedBrand);
    } else {
      setSavedElements([]);
      setSelectedOfferForElements(null);
    }
  }, [selectedBrand]);

  // Handle offer selection for viewing elements (auto-load elements when offer is selected)
  const handleOfferSelectForElements = async (offer) => {
    setSelectedOfferForElements(offer);
    setSelectedElementHtml(''); // Reset selected element HTML
    setSelectedElement(null); // Reset selected element

    // Load elements specific to this offer
    const brandId = selectedBrand || offer.brand?._id;
    if (brandId && offer._id) {
      await loadSavedElementsByOffer(brandId, offer._id);
    } else {
      setSavedElements([]);
    }
  };

  // Handle element selection and blog creation
  const handleElementSelect = (element, offer) => {
    setSelectedOffer(offer);
    setSelectedElement(element);
    // Generate the actual affiliate HTML from the element data
    const affiliateHtml = generateAffiliateElementHTML(element, offer, generateAffiliateLink);
    setSelectedElementHtml(affiliateHtml);

    // Only reset form if we're starting fresh (no existing blog content)
    // This allows adding multiple offer elements to the same blog
    if (blogForm.title === '' && getAllElements().length === 0) {
      setBlogForm({
        title: '',
        content: '<h1>Headline 1</h1>\n<h2>Headline 2</h2>\n',
        featuredImage: '',
        tags: [],
        mediaTitle: '',
        mediaDescription: ''
      });
    }
  };

  // Page management functions
  const addPage = () => {
    const newPage = { id: nextPageId, elements: [] };
    setPages(prev => {
      const newPages = [...prev, newPage];
      setCurrentPageIndex(newPages.length - 1); // Switch to the new page (last index)
      return newPages;
    });
    setNextPageId(prev => prev + 1);
  };

  const removePage = (pageIndex) => {
    if (pages.length > 1) {
      setPages(prev => prev.filter((_, i) => i !== pageIndex));
      if (currentPageIndex >= pageIndex && currentPageIndex > 0) {
        setCurrentPageIndex(currentPageIndex - 1);
      }
    }
  };

  const handleDeletePageClick = (pageIndex) => {
    if (pages.length > 1) {
      setPageToDelete(pageIndex);
      setDeletePageDialogOpen(true);
    }
  };

  const handleDeletePageConfirm = () => {
    if (pageToDelete !== null) {
      removePage(pageToDelete);
      setDeletePageDialogOpen(false);
      setPageToDelete(null);
    }
  };

  const handleDeletePageCancel = () => {
    setDeletePageDialogOpen(false);
    setPageToDelete(null);
  };

  const switchToPage = (pageIndex) => {
    setCurrentPageIndex(pageIndex);
  };

  // Handle page reordering via drag and drop
  const handlePageDragEnd = (result) => {
    if (!result.destination) {
      return; // Dropped outside the list
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return; // No change in position
    }

    // Reorder pages
    setPages(prev => {
      const newPages = Array.from(prev);
      const [removed] = newPages.splice(sourceIndex, 1);
      newPages.splice(destinationIndex, 0, removed);
      return newPages;
    });

    // Update current page index if needed
    if (currentPageIndex === sourceIndex) {
      setCurrentPageIndex(destinationIndex);
    } else if (currentPageIndex > sourceIndex && currentPageIndex <= destinationIndex) {
      setCurrentPageIndex(currentPageIndex - 1);
    } else if (currentPageIndex < sourceIndex && currentPageIndex >= destinationIndex) {
      setCurrentPageIndex(currentPageIndex + 1);
    }
  };

  // Element mutation operations extracted to utils/useBlogElementMutations.
  // getDefaultStyles and generateAffiliateLink are defined further down in
  // this component, so they're passed as arrow wrappers: the closure captures
  // the binding now, but the lookup is deferred until call time (by which point
  // the const declarations have been initialized).
  const {
    addElement,
    removeElement,
    duplicateElement,
    moveElement,
    updateElementContent,
    updateElementStyle,
    updateOfferElementHtml,
  } = useBlogElementMutations({
    pages,
    currentPageIndex,
    nextElementId,
    nextPageId,
    selectedElement,
    selectedOffer,
    selectedElementHtml,
    setPages,
    setNextElementId,
    setNextPageId,
    setCurrentPageIndex,
    setSelectedElementIndex,
    getCurrentPage,
    getCurrentPageElements,
    getDefaultStyles: (type) => getDefaultStyles(type),
    generateAffiliateLink: (offer) => generateAffiliateLink(offer),
    getPreset,
    getButtonPreset,
    toast,
  });

  const handleMediaUploadForElement = async (elementIndex, file) => {
    try {
      setUploading(true);
      console.log('🚀 Starting media upload for element:', file.name);

      const uploadedUrl = await uploadToS3WithProgress(file, 'blog-uploads', (progress) => {
        console.log(`Media upload progress: ${progress}%`);
      });
      console.log('✅ Media upload successful:', uploadedUrl);

      const currentPage = getCurrentPage();
      const currentPageElements = currentPage.elements;

      // OVERFLOW DETECTION DISABLED - Users control pagination visually through live preview
      // Update the element in place regardless of calculated height
      setPages(prev => prev.map((page, pageIndex) =>
        pageIndex === currentPageIndex
          ? {
            ...page,
            elements: page.elements.map((element, i) =>
              i === elementIndex
                ? { ...element, mediaUrl: uploadedUrl }
                : element
            )
          }
          : page
      ));

    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Upload failed. Please try again later.');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUploadForElement = async (elementIndex, file) => {
    try {
      setUploading(true);
      console.log('🚀 Starting thumbnail upload for element:', file.name);

      const uploadedUrl = await uploadToS3WithProgress(file, 'blog-uploads/thumbnails', (progress) => {
        console.log(`Thumbnail upload progress: ${progress}%`);
      });
      console.log('✅ Thumbnail upload successful:', uploadedUrl);

      setPages(prev => prev.map((page, pageIndex) =>
        pageIndex === currentPageIndex
          ? {
            ...page,
            elements: page.elements.map((element, i) =>
              i === elementIndex
                ? { ...element, thumbnailUrl: uploadedUrl }
                : element
            )
          }
          : page
      ));

    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error('Upload failed. Please try again later.');
    } finally {
      setUploading(false);
    }
  };



  // Handle overflow alert actions
  const handleOverflowMoveToNewPage = () => {
    if (!overflowElement || overflowElementIndex === null) return;

    const currentPage = getCurrentPage();
    const currentPageElements = currentPage.elements;

    console.log('📦 Moving element to new page:');
    console.log('📦 Overflow element:', overflowElement);
    console.log('📦 Overflow element index:', overflowElementIndex);
    console.log('📦 Overflow element type:', overflowElement?.type);
    console.log('📦 Current page elements:', currentPageElements.map((el, i) => ({ index: i, type: el.type, id: el.id })));

    // Move the overflow element to a new page
    const elementToMove = overflowElement;
    const remainingElements = currentPageElements.filter((_, i) => i !== overflowElementIndex);

    console.log('📦 Element to move:', { type: elementToMove.type, id: elementToMove.id });
    console.log('📦 Remaining elements:', remainingElements.map((el, i) => ({ index: i, type: el.type, id: el.id })));

    // Create new page with the moved element
    const newPage = { id: nextPageId, elements: [elementToMove] };

    setPages(prev => {
      const newPages = [...prev];
      // Update current page with remaining elements
      newPages[currentPageIndex] = { ...newPages[currentPageIndex], elements: remainingElements };

      // Insert the new page at the next position (currentPageIndex + 1)
      const insertIndex = currentPageIndex + 1;
      newPages.splice(insertIndex, 0, newPage);

      // Switch to the new page
      setCurrentPageIndex(insertIndex);
      return newPages;
    });

    setNextPageId(prev => prev + 1);

    // Close the alert
    setShowOverflowAlert(false);
    setOverflowElement(null);
    setOverflowElementIndex(null);

    toast.info(`Element moved to new page ${pages.length + 1}`);
  };

  const handleOverflowKeepOnCurrentPage = () => {
    if (!overflowElement || overflowElementIndex === null) return;

    // Update the element content on current page (user chose to keep it despite overflow)
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) =>
            i === overflowElementIndex ? overflowElement : element
          )
        }
        : page
    ));

    // Close the alert
    setShowOverflowAlert(false);
    setOverflowElement(null);
    setOverflowElementIndex(null);

    toast.warning('Element kept on current page - content may be cut off on mobile');
  };

  const handleOverflowCancel = () => {
    // Close the alert without making any changes
    setShowOverflowAlert(false);
    setOverflowElement(null);
    setOverflowElementIndex(null);
  };


  const applyTextPreset = (index, presetName, preset) => {
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) =>
            i === index
              ? {
                ...element,
                styles: {
                  // Apply font-related preset styles only
                  // Do not override padding - let the rendering components handle it
                  ...preset,
                  // Force left alignment for title and text elements
                  textAlign: 'left',
                },
                presetName: presetName
              }
              : element
          )
        }
        : page
    ));
  };

  const applyFramePreset = (index, presetName, preset) => {
    console.log('🖼️ applyFramePreset called:', { index, presetName, preset });

    setPages(prev => {
      const updatedPages = prev.map((page, pageIndex) =>
        pageIndex === currentPageIndex
          ? {
            ...page,
            elements: page.elements.map((element, i) =>
              i === index
                ? {
                  ...element,
                  styles: {
                    ...element.styles,
                    ...preset,
                    mediaBorderRadius: preset.borderRadius || '0px'
                  },
                  framePresetName: presetName
                }
                : element
            )
          }
          : page
      );

      // OVERFLOW DETECTION DISABLED - Users control pagination visually through live preview
      // Frame preset applied successfully without overflow checks
      console.log('🖼️ Frame preset applied successfully (overflow detection disabled)');

      return updatedPages;
    });

    console.log('🖼️ Frame preset applied successfully');
  };

  const applyTextAlignment = (index, alignment) => {
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) =>
            i === index
              ? {
                ...element,
                styles: { ...element.styles, textAlign: alignment }
              }
              : element
          )
        }
        : page
    ));
  };

  const toggleVideoOverlay = (index) => {
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) => {
            if (i === index) {
              const isCurrentlyEnabled = element.showOverlayButton || element.type === 'video_with_button_overlay';
              const nextState = !isCurrentlyEnabled;

              console.log('🎬 Toggling Video Overlay. Next State:', nextState);

              // Standard link detection for recovery
              const looksLikeURL = (s) => typeof s === 'string' && s.length > 5 && s.includes('.') && s !== 'Click Here' && !s.includes(' ');
              const recoveredUrl = looksLikeURL(element.videoFile) ? element.videoFile :
                (looksLikeURL(element.mediaUrl) ? element.mediaUrl :
                  (looksLikeURL(element.videoSrc) ? element.videoSrc :
                    (looksLikeURL(element.content) ? element.content : (element.mediaUrl || element.videoFile || ''))));

              // Solid presets use backgroundColor, Gradients use background.
              // We must clear the opposite to avoid CSS conflicts.
              const cleanStyles = { ...element.styles };
              if (nextState && !element.styles?.presetStyle) {
                const gradientPreset = getButtonPreset('buttonGradient') || {};
                Object.assign(cleanStyles, gradientPreset);
                cleanStyles.presetStyle = 'buttonGradient';
                delete cleanStyles.backgroundColor; // Clear solid color if applying gradient
              }

              return {
                ...element,
                type: 'media', // Always normalize to media
                showOverlayButton: nextState,
                // Ensure button defaults
                content: element.content || 'Click Here',
                url: element.url || '#',
                // Ensure media defaults
                mediaUrl: recoveredUrl,
                videoFile: recoveredUrl,
                styles: cleanStyles
              };
            }
            return element;
          })
        }
        : page
    ));
  };

  const handleVideoOverlayStyleSelect = (index, presetName, preset) => {
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) => {
            if (i === index) {
              const newStyles = {
                ...element.styles,
                ...preset,
                presetStyle: presetName
              };

              // Prevent conflict between solid backgroundColor and gradient background
              if (preset.background) {
                delete newStyles.backgroundColor;
              } else if (preset.backgroundColor) {
                delete newStyles.background;
              }

              // If switching away from 3D preset, clear any leftover 3D-specific shadow
              // so standard/gradient presets don't keep the 3D boxShadow.
              if (presetName !== 'button3D' && !preset.boxShadow && newStyles.boxShadow) {
                delete newStyles.boxShadow;
              }

              return {
                ...element,
                styles: newStyles
              };
            }
            return element;
          })
        }
        : page
    ));
  };

  const getDefaultStyles = (type) => {
    switch (type) {
      case 'title':
        return {
          fontSize: '24px',
          fontWeight: 'bold',
          textAlign: 'left'
        };
      case 'text':
        return {
          fontSize: '16px',
          textAlign: 'left'
        };
      case 'media':
        // Apply default 'landscape' frame preset so aspect ratio is set from the start
        const defaultFramePreset = getFramePreset('landscape');
        return {
          ...defaultFramePreset,
          mediaBorderRadius: defaultFramePreset.borderRadius || '0px',
          frame: 'landscape',
          size: 'medium'
        };
      case 'bullets':
        return {
          backgroundColor: '#ffffff',
          color: '#101022',
          fontFamily: 'Inter, Arial, sans-serif',
          fontSize: '1.25rem',
          borderRadius: '12px',
          padding: '24px',
          margin: '0 0 8px 0'
        };
      case 'blackbar':
        return { height: '60px', backgroundColor: '#000000', buttons: [] };
      default:
        return {};
    }
  };

  // Render page thumbnail
  const renderPageThumbnail = (page, pageIndex) => {
    const isActive = pageIndex === currentPageIndex;
    const thumbnailWidth = 80;
    const thumbnailHeight = (thumbnailWidth * MOBILE_PHONE_HEIGHT) / MOBILE_PHONE_WIDTH;

    return (
      <Draggable key={page.id} draggableId={`page-${page.id}`} index={pageIndex}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={{
              width: thumbnailWidth,
              height: thumbnailHeight,
              border: isActive ? '3px solid #1976d2' : '2px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white',
              cursor: snapshot.isDragging ? 'grabbing' : 'grab',
              mb: 1,
              position: 'relative',
              overflow: 'hidden',
              opacity: snapshot.isDragging ? 0.8 : 1,
              boxShadow: snapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.3)' : 'none',
              '&:hover': {
                borderColor: '#1976d2',
                transform: snapshot.isDragging ? 'none' : 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
                '& .delete-button': {
                  opacity: 1,
                  visibility: 'visible'
                },
                '& .drag-handle': {
                  opacity: 1,
                  visibility: 'visible'
                }
              }
            }}
            onClick={(e) => {
              // Only switch page if not dragging
              if (!snapshot.isDragging) {
                setCurrentPageIndex(pageIndex);
              }
            }}
          >
            {/* Drag handle indicator - visual only, dragging works on entire box */}
            <Box
              className="drag-handle"
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute',
                top: 2,
                left: 2,
                opacity: 1,
                visibility: 'visible',
                zIndex: 3,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '4px'
              }}
            >
              <DragIndicator sx={{ fontSize: '12px', color: '#666' }} />
            </Box>

            {/* Page number */}
            <Box sx={{
              position: 'absolute',
              top: 2,
              left: 24,
              backgroundColor: isActive ? '#1976d2' : '#666',
              color: 'white',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              zIndex: 1
            }}>
              {pageIndex + 1}
            </Box>

            {/* Delete button - shown on hover */}
            {pages.length > 1 && (
              <IconButton
                className="delete-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent page selection
                  handleDeletePageClick(pageIndex);
                }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  backgroundColor: 'error.main',
                  color: 'white',
                  width: 16,
                  height: 16,
                  opacity: 0,
                  visibility: 'hidden',
                  transition: 'all 0.2s ease-in-out',
                  zIndex: 2,
                  '&:hover': {
                    backgroundColor: 'error.dark',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <Delete sx={{ fontSize: '10px' }} />
              </IconButton>
            )}

            {/* Thumbnail content */}
            <Box sx={{
              width: '100%',
              height: '100%',
              p: 0.5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              fontSize: '6px',
              lineHeight: 1.2,
              overflow: 'hidden'
            }}>
              {page.elements.slice(0, 3).map((element, elementIndex) => (
                <Box key={elementIndex} sx={{ width: '100%', mb: 0.5 }}>
                  {element.type === 'title' && element.content && (
                    <Box sx={{
                      backgroundColor: '#f0f0f0',
                      borderRadius: 0.5,
                      p: 0.5,
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: '5px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {element.content.substring(0, 20)}...
                    </Box>
                  )}

                  {element.type === 'text' && element.content && (
                    <Box sx={{
                      backgroundColor: '#f8f8f8',
                      borderRadius: 0.5,
                      p: 0.5,
                      fontSize: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {element.content.substring(0, 30)}...
                    </Box>
                  )}

                  {element.type === 'media' && (
                    <Box sx={{
                      backgroundColor: '#e3f2fd',
                      borderRadius: 0.5,
                      p: 0.5,
                      textAlign: 'center',
                      fontSize: '4px',
                      color: '#1976d2'
                    }}>
                      📷 Media
                    </Box>
                  )}

                  {element.type === 'offer' && (
                    <Box sx={{
                      backgroundColor: '#e8f5e8',
                      borderRadius: 0.5,
                      p: 0.5,
                      textAlign: 'center',
                      fontSize: '4px',
                      color: '#2e7d32'
                    }}>
                      🛒 Offer
                    </Box>
                  )}

                  {element.type === 'blackbar' && (
                    <Box sx={{
                      backgroundColor: '#000000',
                      borderRadius: 0.5,
                      p: 0.5,
                      textAlign: 'center',
                      fontSize: '4px',
                      color: 'white',
                      height: '8px'
                    }}>
                      ⚫ Bar
                    </Box>
                  )}
                </Box>
              ))}

              {page.elements.length > 3 && (
                <Box sx={{
                  fontSize: '4px',
                  color: '#666',
                  textAlign: 'center',
                  mt: 0.5
                }}>
                  +{page.elements.length - 3} more
                </Box>
              )}
            </Box>

            {/* Height indicator */}
            <Box sx={{
              position: 'absolute',
              bottom: 2,
              left: 2,
              right: 2,
              height: '4px',
              backgroundColor: '#e0e0e0',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <Box sx={{
                height: '100%',
                backgroundColor: (() => {
                  const pageHeight = calculatePageHeight(page.elements);
                  const maxHeight = getMaxPageHeight();
                  const percentage = (pageHeight / maxHeight) * 100;

                  if (percentage >= 90) return '#f44336'; // Red for near overflow
                  if (percentage >= 70) return '#ff9800'; // Orange for warning
                  return '#4caf50'; // Green for safe
                })(),
                width: `${Math.min(100, (calculatePageHeight(page.elements) / getMaxPageHeight()) * 100)}%`,
                transition: 'all 0.3s ease'
              }} />
            </Box>
          </Box>
        )}
      </Draggable>
    );
  };

  const handleCreateBlog = async () => {
    // Determine which offer to use based on context
    const offerToUse = selectedOfferForElements || selectedOffer;

    // Check if we have pages (for prebuilt/merged blogs) or elements
    const hasPages = pages && pages.length > 0;
    const hasElements = getAllElements().length > 0;
    const hasContent = hasPages || hasElements;

    console.log('Create blog validation - offerToUse:', offerToUse, 'title:', blogForm.title, 'pages:', pages.length, 'allElements:', getAllElements().length);

    // For prebuilt/merged blogs, offer is optional but title and content are required
    if (!blogForm.title || !hasContent) {
      toast.error('Please fill in all required fields (Blog Name and Blog Elements)');
      return;
    }

    // Offer is required for regular blogs, but optional for prebuilt/merged blogs
    if (!offerToUse && !hasPages) {
      toast.error('Please select an offer or use a prebuilt blog');
      return;
    }

    // Check for video elements without thumbnails
    // Skip this check for merged blogs (prebuilt/merged blogs) as they may have video elements from templates without thumbnails
    const isMergedBlog = concatenatedBlog !== null || hasPages;
    if (!isMergedBlog) {
      const videoElementsWithoutThumbnails = getAllElements().filter(element =>
        element.type === 'media' && element.mediaUrl && element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) && !element.thumbnailUrl
      );

      if (videoElementsWithoutThumbnails.length > 0) {
        toast.error(`Cannot create blog: ${videoElementsWithoutThumbnails.length} video element(s) are missing required thumbnails. Please upload thumbnails for all video elements before creating the blog.`);
        return;
      }
    }

    // Check if we're in edit mode
    if (isEditMode && editingBlogPost) {
      await handleUpdateBlog();
      return;
    }

    setCreating(true);
    try {
      // Calculate firstBlogPageCount for merged blogs
      let firstBlogPageCount = 0;
      if (concatenatedBlog && concatenatedBlog.blogs && concatenatedBlog.blogs.length > 0) {
        // Get the first blog from the concatenated blogs
        const firstBlog = concatenatedBlog.blogs[0];
        if (firstBlog && firstBlog.content) {
          try {
            let firstBlogContent = firstBlog.content;
            if (typeof firstBlogContent === 'string') {
              firstBlogContent = JSON.parse(firstBlogContent);
            }

            // Count pages in first blog
            if (Array.isArray(firstBlogContent)) {
              if (firstBlogContent[0]?.elements) {
                // It's a pages structure
                firstBlogPageCount = firstBlogContent.length;
              } else {
                // It's a flat elements array - single page
                firstBlogPageCount = 1;
              }
            }
          } catch (e) {
            console.error('Error calculating firstBlogPageCount:', e);
            firstBlogPageCount = 1; // Default to 1 page
          }
        }
      }

      // Prepare attachments from media elements
      const attachments = getAllElements()
        .filter(element => element.type === 'media' && element.mediaUrl)
        .map(element => ({
          fileName: element.mediaUrl.split('/').pop() || 'media-file',
          fileUrl: element.mediaUrl,
          fileType: element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) ? 'video/mp4' : 'image/jpeg',
          fileSize: 0, // We don't have file size info from S3 URLs
          uploadDate: new Date(),
          thumbnailUrl: element.thumbnailUrl || undefined
        }));

      // Automatically populate overlay button URLs based on the nearest preceding offer
      const finalPages = pages.map((page, pageIdx) => ({
        ...page,
        elements: page.elements.map((el, elIdx) => {
          if (el.type === 'video_with_button_overlay' || el.showOverlayButton) {
            return { ...el, url: getOfferLinkForElement(pageIdx, elIdx) };
          }
          return el;
        })
      }));

      // Store blog elements as structured data instead of HTML
      const blogData = {
        title: blogForm.title,
        content: JSON.stringify(finalPages), // Store updated pages structure to preserve pagination
        featuredImage: blogForm.featuredImage,
        tags: blogForm.tags,
        // Offer is optional for prebuilt/merged blogs
        offerId: offerToUse?._id || null,
        categoryId: selectedCategory || null,
        subcategoryId: selectedSubcategory || null,
        mediaTitle: blogForm.mediaTitle,
        mediaDescription: blogForm.mediaDescription,
        // Save the selected element ID for template selection
        selectedElementId: selectedElement?._id || null,
        attachments: attachments,
        // Mark as merged blog if it was created from concatenated content
        isMergedBlog: concatenatedBlog !== null,
        // Store source blog IDs if this is a merged blog
        sourceBlogIds: concatenatedBlog?.blogs?.map(b => b._id) || [],
        // Store number of pages from first blog (for reporting - head vs snake)
        firstBlogPageCount: concatenatedBlog ? firstBlogPageCount : 0,
        // Auto-publish merged blogs
        published: concatenatedBlog !== null,
        publishedAt: concatenatedBlog !== null ? new Date() : null
      };

      console.log('Creating blog post with data:', blogData);
      console.log('Blog elements being saved:', getAllElements());
      console.log('Current user:', user);

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const createdPost = await apiService.createBlogPost(blogData);
      console.log('Blog post created:', createdPost);

      // Check if there's a pre-built merged blog with the same category/subcategory to attach below
      if (selectedCategory && selectedSubcategory && !concatenatedBlog) {
        try {
          console.log('🔍 Looking for prebuilt merged blog for category/subcategory:', selectedCategory, selectedSubcategory);

          // Fetch the created blog post fresh from database to get its actual content
          // This ensures we're working with the exact content that was saved, avoiding any duplication
          let currentBlogPost = null;
          try {
            currentBlogPost = await apiService.getBlogPostById(createdPost._id);
            console.log('📄 Fetched created blog post from database');
          } catch (fetchError) {
            console.error('Error fetching created blog post:', fetchError);
            // Fall back to createdPost if fetch fails
            currentBlogPost = createdPost;
          }

          // Use the dedicated endpoint to fetch prebuilt merged blog by category/subcategory
          // This endpoint returns published prebuilt blogs regardless of author
          let matchingMergedBlog = null;
          try {
            matchingMergedBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
              selectedCategory,
              selectedSubcategory
            );
            console.log('✅ Found prebuilt merged blog:', matchingMergedBlog?._id);
          } catch (fetchError) {
            // 404 is expected if no prebuilt blog exists for this subcategory
            if (fetchError.response?.status === 404) {
              console.log('No prebuilt merged blog found for this subcategory');
            } else {
              console.error('Error fetching prebuilt merged blog:', fetchError);
            }
          }

          // Attach prebuilt merged blog if it exists (content is optional - generated dynamically)
          if (matchingMergedBlog && currentBlogPost) {
            console.log('✅ Found prebuilt merged blog to attach:', matchingMergedBlog._id, matchingMergedBlog.title);
            console.log('📝 Prebuilt blog has sourceBlogIds:', matchingMergedBlog.sourceBlogIds?.length || 0, 'blogs');

            // Store reference to prebuilt merged blog instead of copying content
            // This allows dynamic updates when admin changes the prebuilt merged blog
            // Content will be generated dynamically from sourceBlogIds when the blog is viewed
            const updatedBlogData = {
              attachedPrebuiltMergedBlogId: matchingMergedBlog._id
            };

            await apiService.updateBlogPost(createdPost._id, updatedBlogData);
            console.log('✅ Attached prebuilt merged blog reference (dynamic) to blog');

            toast.success(`Prebuilt merged blog "${matchingMergedBlog.title}" attached (updates automatically)`);
          } else {
            console.log('No prebuilt merged blog available for this category/subcategory');
          }
        } catch (error) {
          console.error('Error attaching merged blog:', error);
          // Don't fail the blog creation if attachment fails
        }
      }

      // Store the created blog data for potential publishing
      setCreatedBlogData({
        ...blogData,
        _id: createdPost._id,
        createdPost
      });


      // Close dialog if it was open (for dialog form)
      if (dialogOpen) {
        setDialogOpen(false);
        setSelectedOffer(null);
      }

      // Reload blog posts
      console.log('Reloading blog posts...');
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      console.log('Reloaded posts data:', postsData);
      setBlogPosts(postsData.posts || []);

      // If this is a merged blog, refresh the merged blogs list (if admin)
      if (concatenatedBlog !== null && isAdmin) {
        console.log('Refreshing merged blogs list after creating merged blog...');
        await fetchMergedBlogs();
        // Clear the concatenated blog state after successful creation
        setConcatenatedBlog(null);
        setPreBuiltBlogs([]);
      }
    } catch (error) {
      console.error('Error creating blog post:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create blog post';
      toast.error(errorMessage);
      setError('Failed to create blog post: ' + errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handlePublishCreatedBlog = async () => {
    if (!createdBlogData) {
      toast.error('No blog data available to publish');
      return;
    }

    setPublishing(true);
    try {
      // Generate a unique slug for the blog
      const slug = createdBlogData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') +
        '-' +
        Math.random().toString(36).substring(2, 15);

      // Update the existing blog to mark it as published
      const updateData = {
        slug: slug,
        published: true,
        publishedAt: new Date().toISOString()
      };

      console.log('Publishing existing blog:', createdBlogData._id, 'with data:', updateData);

      const publishedPost = await apiService.updateBlogPost(createdBlogData._id, updateData);
      console.log('Blog post published:', publishedPost);

      // Generate the public URL - use the same domain as the affiliate portal
      const getPublicDomain = () => {
        // If REACT_APP_PUBLIC_DOMAIN is set, use it
        if (process.env.REACT_APP_PUBLIC_DOMAIN) {
          return process.env.REACT_APP_PUBLIC_DOMAIN;
        }

        // Otherwise, use the current domain (same as affiliate portal)
        const currentOrigin = window.location.origin;

        // Ensure we're using HTTPS in production
        if (process.env.NODE_ENV === 'production' && currentOrigin.startsWith('http:')) {
          return currentOrigin.replace('http:', 'https:');
        }

        return currentOrigin;
      };

      const publicUrl = `${getPublicDomain()}/blog/${slug}`;

      // Show success message with the URL

      // Open the published blog in a new tab
      window.open(publicUrl, '_blank');

      // Copy the URL to clipboard
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.info('Public URL copied to clipboard!');
      } catch (clipboardError) {
        console.error('Failed to copy to clipboard:', clipboardError);
        // Show the URL in a toast instead
        toast.info(`Public URL: ${publicUrl}`);
      }

      // Reset created blog data
      setCreatedBlogData(null);

      // Reload blog posts
      console.log('Reloading blog posts...');
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      console.log('Reloaded posts data:', postsData);
      setBlogPosts(postsData.posts || []);
    } catch (error) {
      console.error('Error publishing blog post:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to publish blog post';
      toast.error(errorMessage);
      setError('Failed to publish blog post: ' + errorMessage);
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishBlog = async () => {
    // Check if we have created blog data to publish
    if (createdBlogData) {
      // Publish the existing created blog
      await handlePublishCreatedBlog();
      return;
    }

    // Check if we're in edit mode - publish the updated blog
    if (isEditMode && editingBlogPost) {
      await handleUpdateBlog();
      return;
    }

    // Check if we're viewing an existing unpublished blog
    if (isViewMode && selectedBlogPost && !selectedBlogPost.published) {
      await handlePublishExistingBlog();
      return;
    }

    // If none of the above conditions are met, show error
    toast.error('Please create a blog first before publishing');
    return;
  };

  const handlePublishExistingBlog = async () => {
    if (!selectedBlogPost) {
      toast.error('No blog selected to publish');
      return;
    }

    setPublishing(true);
    try {
      // Update the existing blog to mark it as published
      const updateData = {
        published: true,
        publishedAt: new Date().toISOString()
      };

      console.log('Publishing existing blog post:', selectedBlogPost._id);

      const publishedPost = await apiService.updateBlogPost(selectedBlogPost._id, updateData);
      console.log('Blog post published:', publishedPost);

      // Generate the public URL - use the same domain as the affiliate portal
      const getPublicDomain = () => {
        // If REACT_APP_PUBLIC_DOMAIN is set, use it
        if (process.env.REACT_APP_PUBLIC_DOMAIN) {
          return process.env.REACT_APP_PUBLIC_DOMAIN;
        }

        // Otherwise, use the current domain (same as affiliate portal)
        const currentOrigin = window.location.origin;

        // Ensure we're using HTTPS in production
        if (process.env.NODE_ENV === 'production' && currentOrigin.startsWith('http:')) {
          return currentOrigin.replace('http:', 'https:');
        }

        return currentOrigin;
      };

      // Use the blog's existing slug or generate one if it doesn't exist
      const slug = publishedPost.slug || selectedBlogPost.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') +
        '-' +
        Math.random().toString(36).substring(2, 15);

      const publicUrl = `${getPublicDomain()}/blog/${slug}`;

      // Show success message with the URL

      // Open the published blog in a new tab
      window.open(publicUrl, '_blank');

      // Copy the URL to clipboard
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.info('Public URL copied to clipboard!');
      } catch (clipboardError) {
        console.error('Failed to copy to clipboard:', clipboardError);
        // Show the URL in a toast instead
        toast.info(`Public URL: ${publicUrl}`);
      }

      // Update the selected blog post to reflect published status
      setSelectedBlogPost({ ...selectedBlogPost, published: true, publishedAt: updateData.publishedAt });

      // Reload blog posts to update the list
      console.log('Reloading blog posts...');
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      console.log('Reloaded posts data:', postsData);
      setBlogPosts(postsData.posts || []);
    } catch (error) {
      console.error('Error publishing blog post:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to publish blog post';
      toast.error(errorMessage);
      setError('Failed to publish blog post: ' + errorMessage);
    } finally {
      setPublishing(false);
    }
  };

  const handleViewBlog = async (post, updateUrl = true) => {
    console.log('Viewing blog post:', post);

    // If content is missing (e.g., excluded from list query for performance), fetch full blog post
    let fullPost = post;
    if (!post.content) {
      try {
        console.log('Content missing, fetching full blog post:', post._id);
        const fullPostData = await apiService.getBlogPostById(post._id);
        fullPost = fullPostData.blog || fullPostData;
        console.log('Fetched full blog post:', fullPost);
      } catch (error) {
        console.error('Error fetching full blog post:', error);
        toast.error('Failed to load blog content');
        return;
      }
    }

    // Reset only necessary state for view mode
    setPages([{ id: 1, elements: [] }]);
    setCurrentPageIndex(0);
    setNextElementId(1);
    setIsEditMode(false);
    setIsViewMode(true); // Set view mode
    setEditingBlogPost(null);
    setEditLoading(false);
    setCreatedBlogData(null);
    setSelectedBlogPost(fullPost);
    setTabValue(0); // Switch to Create Blog tab for view mode

    // Update URL to persist view state
    if (updateUrl) {
      navigate(`/blogs?view=${fullPost._id}`, { replace: true });
    }

    // Set up the form with existing blog data
    setBlogForm({
      title: fullPost.title,
      content: fullPost.content,
      featuredImage: fullPost.featuredImage || '',
      tags: fullPost.tags || [],
      mediaTitle: fullPost.mediaTitle || '',
      mediaDescription: fullPost.mediaDescription || '',
    });

    // Load blog elements from the stored content
    try {
      const parsedContent = JSON.parse(fullPost.content);
      console.log('Parsed blog content:', parsedContent);

      // Check if content is in new pages format or old elements format
      if (Array.isArray(parsedContent) && parsedContent.length > 0 && parsedContent[0].id && parsedContent[0].elements) {
        // New format: pages structure
        console.log('Loading pages structure:', parsedContent);
        const pagesWithIds = parsedContent.map(page => ({
          ...page,
          elements: page.elements.map((element, index) => ({
            ...element,
            id: element.id || `element-${index}-${Math.random().toString(36).substr(2, 9)}`
          }))
        }));
        setPages(pagesWithIds);
        setCurrentPageIndex(0);

        // Calculate next element ID
        const allElements = pagesWithIds.flatMap(page => page.elements);
        if (allElements.length > 0) {
          const maxId = Math.max(...allElements.map(el => el.id || 0));
          setNextElementId(maxId + 1);
        } else {
          setNextElementId(1);
        }
      } else if (Array.isArray(parsedContent)) {
        // Old format: flat elements array (backward compatibility)
        console.log('Loading legacy elements array:', parsedContent);
        const elementsWithIds = parsedContent.map((element, index) => ({
          ...element,
          id: element.id || `element-${index}-${Math.random().toString(36).substr(2, 9)}`
        }));
        console.log('Elements with IDs:', elementsWithIds);
        setPages([{ id: 1, elements: elementsWithIds }]);
        setCurrentPageIndex(0);

        if (elementsWithIds.length > 0) {
          const maxId = Math.max(...elementsWithIds.map(el => el.id || 0));
          setNextElementId(maxId + 1);
        } else {
          setNextElementId(1);
        }
      } else {
        console.log('Invalid content format, initializing empty pages');
        setPages([{ id: 1, elements: [] }]);
        setCurrentPageIndex(0);
        setNextElementId(1);
      }
    } catch (error) {
      console.error('Error parsing blog elements:', error);
      setPages([{ id: 1, elements: [] }]);
      setCurrentPageIndex(0);
      setNextElementId(1);
    }

    // Set the saved offer element HTML if it exists
    if (fullPost.offerElementHtml) {
      console.log('Loading saved offer element HTML from database for view');
      setSelectedElementHtml(fullPost.offerElementHtml);
    } else {
      console.log('No saved offer element HTML found for view');
      setSelectedElementHtml('');
    }

    // Load offer and category data if available
    try {
      if (fullPost.categoryId) {
        setSelectedCategory(typeof fullPost.categoryId === 'string' ? fullPost.categoryId : fullPost.categoryId?._id || fullPost.categoryId);

        // Load subcategories for the selected category
        const category = categories.find(c => c._id === fullPost.categoryId);
        if (category && category.subcategories) {
          if (fullPost.subcategoryId) {
            setSelectedSubcategory(typeof fullPost.subcategoryId === 'string' ? fullPost.subcategoryId : fullPost.subcategoryId?._id || fullPost.subcategoryId);

            // Load brands for the selected subcategory
            const subcategory = category.subcategories.find(s => s._id === fullPost.subcategoryId);
            if (subcategory && subcategory.brands && subcategory.brands.length > 0) {
              // Set the brand if available
              if (fullPost.brandId) {
                setSelectedBrand(fullPost.brandId);
              } else if (subcategory.brands.length === 1) {
                setSelectedBrand(subcategory.brands[0]._id);
              }
            }

            // Load offers for the selected category/subcategory
            const offersData = await apiService.getOffers({
              categoryId: fullPost.categoryId,
              subcategoryId: fullPost.subcategoryId
            });
            setOffers(offersData.offers || offersData || []);

            // Set the selected offer if available
            if (fullPost.offerId) {
              const selectedOffer = offersData.offers?.find(o => o._id === fullPost.offerId) ||
                offersData.find(o => o._id === fullPost.offerId);
              if (selectedOffer) {
                setSelectedOfferForElements(selectedOffer);
                setSelectedOffer(selectedOffer);

                // Load saved elements for this offer
                try {
                  const elementsData = await apiService.getSavedElements(selectedOffer._id);
                  const savedElementsList = elementsData.elements || elementsData || [];
                  setSavedElements(savedElementsList);

                  // Set the selected element if available
                  if (fullPost.selectedElementId) {
                    const foundElement = savedElementsList.find(el => el._id === fullPost.selectedElementId);
                    if (foundElement) {
                      setSelectedElement(foundElement);
                    }
                  }
                } catch (error) {
                  console.error('Error loading saved elements for view:', error);
                  setSavedElements([]);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading offer/category data for view:', error);
    }
  };

  const handleEditBlog = async (post, updateUrl = true) => {
    console.log('Editing blog post:', post);

    // If content is missing (e.g., excluded from list query for performance), fetch full blog post
    let fullPost = post;
    if (!post.content) {
      try {
        console.log('Content missing, fetching full blog post for edit:', post._id);
        const fullPostData = await apiService.getBlogPostById(post._id);
        fullPost = fullPostData.blog || fullPostData;
        console.log('Fetched full blog post for edit:', fullPost);
      } catch (error) {
        console.error('Error fetching full blog post for edit:', error);
        toast.error('Failed to load blog content');
        setEditLoading(false);
        return;
      }
    }

    // Reset only necessary state for edit mode
    setPages([{ id: 1, elements: [] }]);
    setCurrentPageIndex(0);
    setNextElementId(1);
    setIsEditMode(true);
    setIsViewMode(false); // Reset view mode
    setEditLoading(true);
    setEditingBlogPost(fullPost);
    setCreatedBlogData(null);
    setSelectedBlogPost(fullPost);
    setTabValue(0); // Switch to Create Blog tab

    // Update URL to persist edit state
    if (updateUrl) {
      navigate(`/blogs?edit=${fullPost._id}`, { replace: true });
    }

    // Set up the form with existing blog data
    console.log('EDIT: Setting blog form with post data:', {
      title: fullPost.title,
      content: fullPost.content,
      featuredImage: fullPost.featuredImage,
      tags: fullPost.tags,
      mediaTitle: fullPost.mediaTitle,
      mediaDescription: fullPost.mediaDescription
    });
    setBlogForm({
      title: fullPost.title,
      content: fullPost.content,
      featuredImage: fullPost.featuredImage || '',
      tags: fullPost.tags || [],
      mediaTitle: fullPost.mediaTitle || '',
      mediaDescription: fullPost.mediaDescription || '',
    });

    // Load blog elements from the stored content
    try {
      const parsedContent = JSON.parse(fullPost.content);
      console.log('Parsed blog content:', parsedContent);

      // Check if content is in new pages format or old elements format
      if (Array.isArray(parsedContent) && parsedContent.length > 0 && parsedContent[0].id && parsedContent[0].elements) {
        // New format: pages structure
        console.log('Loading pages structure:', parsedContent);
        const pagesWithIds = parsedContent.map(page => ({
          ...page,
          elements: page.elements.map((element, index) => ({
            ...element,
            id: element.id || `element-${index}-${Math.random().toString(36).substr(2, 9)}`
          }))
        }));
        setPages(pagesWithIds);
        setCurrentPageIndex(0);

        // Calculate next element ID
        const allElements = pagesWithIds.flatMap(page => page.elements);
        if (allElements.length > 0) {
          const maxId = Math.max(...allElements.map(el => el.id || 0));
          setNextElementId(maxId + 1);
        } else {
          setNextElementId(1);
        }
      } else if (Array.isArray(parsedContent)) {
        // Old format: flat elements array (backward compatibility)
        console.log('Loading legacy elements array:', parsedContent);
        const elementsWithIds = parsedContent.map((element, index) => ({
          ...element,
          id: element.id || `element-${index}-${Math.random().toString(36).substr(2, 9)}`
        }));
        console.log('Elements with IDs:', elementsWithIds);
        setPages([{ id: 1, elements: elementsWithIds }]);
        setCurrentPageIndex(0);

        if (elementsWithIds.length > 0) {
          const maxId = Math.max(...elementsWithIds.map(el => el.id || 0));
          setNextElementId(maxId + 1);
        } else {
          setNextElementId(1);
        }
      } else {
        console.log('Invalid content format, initializing empty pages');
        setPages([{ id: 1, elements: [] }]);
        setCurrentPageIndex(0);
        setNextElementId(1);
      }
    } catch (error) {
      console.error('Error parsing blog elements:', error);
      setPages([{ id: 1, elements: [] }]);
      setCurrentPageIndex(0);
      setNextElementId(1);
    }

    // Set the saved offer element HTML if it exists
    if (fullPost.offerElementHtml) {
      console.log('Loading saved offer element HTML from database for edit');
      setSelectedElementHtml(fullPost.offerElementHtml);
    } else {
      console.log('No saved offer element HTML found for edit');
      setSelectedElementHtml('');
    }

    // Load offer and category data if available
    try {
      if (fullPost.categoryId) {
        setSelectedCategory(typeof fullPost.categoryId === 'string' ? fullPost.categoryId : fullPost.categoryId?._id || fullPost.categoryId);

        // Load subcategories for the selected category
        const category = categories.find(c => c._id === fullPost.categoryId);
        if (category && category.subcategories) {
          if (fullPost.subcategoryId) {
            setSelectedSubcategory(typeof fullPost.subcategoryId === 'string' ? fullPost.subcategoryId : fullPost.subcategoryId?._id || fullPost.subcategoryId);

            // Load brands for the selected subcategory
            const subcategory = category.subcategories.find(s => s._id === fullPost.subcategoryId);
            if (subcategory && subcategory.brands && subcategory.brands.length > 0) {
              // Set the brand if available
              if (fullPost.brandId) {
                setSelectedBrand(fullPost.brandId);
              } else if (subcategory.brands.length === 1) {
                setSelectedBrand(subcategory.brands[0]._id);
              }
            }

            // Load offers for the selected category/subcategory (regardless of brands)
            console.log('Loading offers for edit mode:', { categoryId: fullPost.categoryId, subcategoryId: fullPost.subcategoryId });
            const offersData = await apiService.getOffers({
              categoryId: fullPost.categoryId,
              subcategoryId: fullPost.subcategoryId
            });
            console.log('Offers loaded for edit:', offersData);
            setOffers(offersData.offers || offersData || []);

            // Set the selected offer if available
            if (fullPost.offerId) {
              const selectedOffer = offersData.offers?.find(o => o._id === fullPost.offerId) ||
                offersData.find(o => o._id === fullPost.offerId);
              if (selectedOffer) {
                setSelectedOfferForElements(selectedOffer);
                setSelectedOffer(selectedOffer);

                // Load saved elements for this offer
                try {
                  const elementsData = await apiService.getSavedElements(selectedOffer._id);
                  const savedElementsList = elementsData.elements || elementsData || [];
                  setSavedElements(savedElementsList);

                  // Set the selected element if available
                  if (fullPost.selectedElementId) {
                    const foundElement = savedElementsList.find(el => el._id === fullPost.selectedElementId);
                    if (foundElement) {
                      setSelectedElement(foundElement);
                    }
                  }
                } catch (error) {
                  console.error('Error loading saved elements for edit:', error);
                  setSavedElements([]);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading offer/category data for edit:', error);
    }

    // Fallback: If we have an offerId but no offers loaded, try to load offers directly
    if (fullPost.offerId && offers.length === 0) {
      console.log('Fallback: Loading offers directly for offerId:', fullPost.offerId);
      try {
        // Try to get the offer directly
        const offerResponse = await apiService.getOfferById(fullPost.offerId);
        if (offerResponse) {
          setOffers([offerResponse]);
          setSelectedOfferForElements(offerResponse);
          setSelectedOffer(offerResponse);

          // Load saved elements for this offer
          const elementsData = await apiService.getSavedElements(offerResponse._id);
          const savedElementsList = elementsData.elements || elementsData || [];
          setSavedElements(savedElementsList);

          // Set the selected element if available
          if (fullPost.selectedElementId) {
            const foundElement = savedElementsList.find(el => el._id === fullPost.selectedElementId);
            if (foundElement) {
              setSelectedElement(foundElement);
            }
          }
        }
      } catch (error) {
        console.error('Error in fallback offer loading:', error);
      }
    }

    setEditLoading(false);
  };

  const handleUpdateBlog = async () => {
    console.log('Update blog validation - editingBlogPost:', editingBlogPost, 'title:', blogForm.title, 'getAllElements():', getAllElements());
    console.log('Validation details:');
    console.log('- editingBlogPost exists:', !!editingBlogPost);
    console.log('- editingBlogPost._id:', editingBlogPost?._id);
    console.log('- editingBlogPost.title:', editingBlogPost?.title);
    console.log('- isEditMode:', isEditMode);
    console.log('- isViewMode:', isViewMode);
    console.log('- selectedBlogPost:', selectedBlogPost);
    console.log('- blogForm.title:', blogForm.title, 'length:', blogForm.title?.length);
    console.log('- blogForm object:', blogForm);
    console.log('- getAllElements().length:', getAllElements().length);
    console.log('- getAllElements() content:', getAllElements());

    // Use selectedBlogPost as fallback if editingBlogPost is null
    const blogToUpdate = editingBlogPost || selectedBlogPost;

    if (!blogToUpdate || !blogForm.title) {
      toast.error('Please fill in all required fields (Blog Name)');
      return;
    }

    // Check for video elements without thumbnails
    // Skip this check for merged blogs (prebuilt/merged blogs) as they may have video elements from templates without thumbnails
    const isMergedBlog = concatenatedBlog !== null || (pages && pages.length > 0);
    if (!isMergedBlog) {
      const videoElementsWithoutThumbnails = getAllElements().filter(element =>
        element.type === 'media' && element.mediaUrl && element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) && !element.thumbnailUrl
      );

      if (videoElementsWithoutThumbnails.length > 0) {
        toast.error(`Cannot update blog: ${videoElementsWithoutThumbnails.length} video element(s) are missing required thumbnails. Please upload thumbnails for all video elements before updating the blog.`);
        return;
      }
    }

    // For edit mode, we allow updating even with empty getAllElements() since the blog already exists
    // The user might be updating just the title or other metadata
    if (getAllElements().length === 0) {
      console.log('Warning: No blog elements found, but allowing update in edit mode');
    }

    setEditing(true);
    try {
      // Prepare attachments from media elements
      const attachments = getAllElements()
        .filter(element => element.type === 'media' && element.mediaUrl)
        .map(element => ({
          fileName: element.mediaUrl.split('/').pop() || 'media-file',
          fileUrl: element.mediaUrl,
          fileType: element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) ? 'video/mp4' : 'image/jpeg',
          fileSize: 0, // We don't have file size info from S3 URLs
          uploadDate: new Date(),
          thumbnailUrl: element.thumbnailUrl || undefined
        }));

      // Store blog elements as structured data
      // Automatically populate overlay button URLs based on the nearest preceding offer
      const finalPages = pages.map((page, pageIdx) => ({
        ...page,
        elements: page.elements.map((el, elIdx) => {
          if (el.type === 'video_with_button_overlay' || el.showOverlayButton) {
            return { ...el, url: getOfferLinkForElement(pageIdx, elIdx) };
          }
          return el;
        })
      }));

      // Store blog elements as structured data
      const updateData = {
        title: blogForm.title,
        content: JSON.stringify(finalPages), // Store updated pages structure to preserve pagination
        featuredImage: blogForm.featuredImage,
        tags: blogForm.tags,
        mediaTitle: blogForm.mediaTitle,
        mediaDescription: blogForm.mediaDescription,
        attachments: attachments
      };

      // Only include ObjectId fields if they have valid values (not empty strings)
      if (selectedOfferForElements?._id) {
        updateData.offerId = selectedOfferForElements._id;
      }
      if (selectedCategory && selectedCategory.trim() !== '') {
        updateData.categoryId = selectedCategory;
      }
      if (selectedSubcategory && selectedSubcategory.trim() !== '') {
        updateData.subcategoryId = selectedSubcategory;
      }
      if (selectedElement?._id) {
        updateData.selectedElementId = selectedElement._id;
      }

      console.log('Updating blog post with data:', updateData);
      console.log('Blog elements being saved:', getAllElements());
      console.log('selectedOfferForElements:', selectedOfferForElements);
      console.log('selectedOfferForElements._id:', selectedOfferForElements?._id);
      console.log('selectedCategory:', selectedCategory);
      console.log('selectedSubcategory:', selectedSubcategory);
      console.log('selectedElement:', selectedElement);
      console.log('selectedElement._id:', selectedElement?._id);
      const updatedPost = await apiService.updateBlogPost(blogToUpdate._id, updateData);

      // Update the editingBlogPost state with the new data
      setEditingBlogPost(updatedPost);

      // Refresh the editor state with the updated content
      try {
        const parsedContent = JSON.parse(updatedPost.content);
        console.log('Refreshing editor with updated content:', parsedContent);

        // Check if content is in new pages format or old elements format
        if (Array.isArray(parsedContent) && parsedContent.length > 0 && parsedContent[0].id && parsedContent[0].elements) {
          // New format: pages structure
          const pagesWithIds = parsedContent.map(page => ({
            ...page,
            elements: page.elements.map((element, index) => ({
              ...element,
              id: element.id || `element-${index}-${Math.random().toString(36).substr(2, 9)}`
            }))
          }));
          setPages(pagesWithIds);

          // Calculate next element ID
          const allElements = pagesWithIds.flatMap(page => page.elements);
          if (allElements.length > 0) {
            const maxId = Math.max(...allElements.map(el => el.id || 0));
            setNextElementId(maxId + 1);
          } else {
            setNextElementId(1);
          }
        } else if (Array.isArray(parsedContent)) {
          // Old format: flat elements array (backward compatibility)
          const elementsWithIds = parsedContent.map((element, index) => ({
            ...element,
            id: element.id || `element-${index}-${Math.random().toString(36).substr(2, 9)}`
          }));
          setPages([{ id: 1, elements: elementsWithIds }]);

          if (elementsWithIds.length > 0) {
            const maxId = Math.max(...elementsWithIds.map(el => el.id || 0));
            setNextElementId(maxId + 1);
          } else {
            setNextElementId(1);
          }
        }

        // Update form with the latest data
        setBlogForm({
          title: updatedPost.title,
          content: updatedPost.content,
          featuredImage: updatedPost.featuredImage || '',
          tags: updatedPost.tags || [],
          mediaTitle: updatedPost.mediaTitle || '',
          mediaDescription: updatedPost.mediaDescription || '',
        });

        // Update selected element HTML if it exists
        if (updatedPost.offerElementHtml) {
          setSelectedElementHtml(updatedPost.offerElementHtml);
        }

      } catch (error) {
        console.error('Error refreshing editor state:', error);
      }

      // Reload blog posts to update the list
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      setBlogPosts(postsData.posts || []);

      toast.success('Blog updated successfully!');

    } catch (error) {
      console.error('Error updating blog post:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update blog post';
      toast.error(errorMessage);
    } finally {
      setEditing(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBlogPost(null);
    setSelectedFile(null);
    setSelectedElementHtml(''); // Clear the selected element HTML
    setSelectedElement(null); // Clear the selected element
    // Reset form for next use
    setBlogForm({
      title: '',
      content: '',
      featuredImage: '',
      tags: [],
      mediaTitle: '',
      mediaDescription: ''
    });
  };

  // Called from BlogPostDialog when the user clicks "Create New Blog" in view mode.
  // Resets the editor state so the dialog can be used to author a new blog from scratch.
  const handleCreateNewFromDialog = () => {
    setDialogMode('create');
    setBlogForm({
      title: '',
      content: '',
      featuredImage: '',
      tags: [],
      mediaTitle: '',
      mediaDescription: ''
    });
    setPages([{ id: 1, elements: [] }]);
    setCurrentPageIndex(0);
    setNextElementId(1);
    setSelectedElementHtml('');
    setSelectedElement(null);
    setSelectedOfferForElements(null);
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedBrand('');
    setOffers([]);
    setSelectedOffer(null);
    setSavedElements([]);
  };

  const handleSwitchToEdit = () => {
    setDialogMode('edit');
  };

  const handleDeleteBlog = async () => {
    if (!blogToDelete) return;

    try {
      await apiService.deleteBlogPost(blogToDelete._id);

      // Reload blog posts
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      setBlogPosts(postsData.posts || []);
      toast.success('Blog post deleted successfully');
      setDeleteBlogDialogOpen(false);
      setBlogToDelete(null);
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast.error('Failed to delete blog post');
    }
  };

  const handleListPublishToggle = async (post) => {
    const nextPublished = !post.published;
    setListPublishBusyId(post._id);
    try {
      const updateData = nextPublished
        ? { published: true, publishedAt: new Date().toISOString() }
        : { published: false };
      await apiService.updateBlogPost(post._id, updateData);
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      const refreshed = postsData.posts || [];
      setBlogPosts(refreshed);
      if (selectedBlogPost && selectedBlogPost._id === post._id) {
        const updated = refreshed.find((p) => p._id === post._id);
        if (updated) {
          setSelectedBlogPost((prev) => (prev ? { ...prev, ...updated } : updated));
        }
      }
      toast.success(nextPublished ? 'Blog re-published' : 'Blog unpublished');
    } catch (error) {
      console.error('Error updating publish status:', error);
      toast.error(nextPublished ? 'Failed to re-publish blog' : 'Failed to unpublish blog');
    } finally {
      setListPublishBusyId(null);
    }
  };

  // Pre-built blog handlers
  const handlePreBuiltCategoryChange = async (event) => {
    const categoryId = event.target.value;
    setPreBuiltCategory(categoryId);
    setPreBuiltSubcategory('');
    setPreBuiltBlogs([]);
    setConcatenatedBlog(null);
    setSelectedAffiliates([]);
    setHeadAffiliate('');
    setAvailableAffiliates([]);

    // If "All" is selected, load affiliates from all categories
    if (categoryId === 'all') {
      await loadAvailableAffiliatesForAllCategories();
    }
  };

  const handlePreBuiltSubcategoryChange = async (event) => {
    const subcategoryId = event.target.value;
    setPreBuiltSubcategory(subcategoryId);
    setPreBuiltBlogs([]);
    setConcatenatedBlog(null);
    setSelectedAffiliates([]);
    setHeadAffiliate('');
    setAvailableAffiliates([]);

    // Fetch available affiliates for this subcategory or all subcategories
    if (subcategoryId && preBuiltCategory && preBuiltCategory !== 'all') {
      if (subcategoryId === 'all') {
        await loadAvailableAffiliatesForAllSubcategories(preBuiltCategory);
      } else {
        await loadAvailableAffiliates(preBuiltCategory, subcategoryId);
      }
    } else if (subcategoryId && preBuiltCategory === 'all') {
      // If category is "All", load affiliates from all categories
      await loadAvailableAffiliatesForAllCategories();
    }
  };

  const loadAvailableAffiliates = async (categoryId, subcategoryId) => {
    setLoadingAffiliates(true);
    try {
      // Fetch blogs to get unique affiliates
      const response = await apiService.getBlogsBySubcategory(categoryId, subcategoryId, { limit: 100 });
      const blogs = response.posts || [];

      // Extract unique affiliates from blogs
      const affiliateMap = new Map();
      blogs.forEach(blog => {
        if (blog.author) {
          const authorId = blog.author._id || blog.author;
          const authorName = blog.author.name || blog.author.email || 'Unknown';
          const authorEmail = blog.author.email || '';

          if (!affiliateMap.has(authorId)) {
            affiliateMap.set(authorId, {
              id: authorId,
              name: authorName,
              email: authorEmail,
              blogCount: 0
            });
          }
          affiliateMap.get(authorId).blogCount++;
        }
      });

      const affiliates = Array.from(affiliateMap.values()).sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email)
      );

      setAvailableAffiliates(affiliates);
      console.log(`Found ${affiliates.length} unique affiliates with blogs in this subcategory`);
    } catch (error) {
      console.error('Error loading available affiliates:', error);
      toast.error('Failed to load available affiliates');
      setAvailableAffiliates([]);
    } finally {
      setLoadingAffiliates(false);
    }
  };

  const loadAvailableAffiliatesForAllSubcategories = async (categoryId) => {
    setLoadingAffiliates(true);
    try {
      // If admin, load all affiliates
      if (isAdmin) {
        try {
          const allAffiliates = await apiService.getAllAffiliates();
          const affiliateList = allAffiliates.map(aff => ({
            id: aff._id,
            name: aff.name || aff.email,
            email: aff.email,
            blogCount: 0 // Will be updated if needed
          }));
          setAvailableAffiliates(affiliateList);
          setLoadingAffiliates(false);
          return;
        } catch (error) {
          console.error('Error loading all affiliates:', error);
          // Fall back to blog-based loading
        }
      }

      const category = categories.find(c => c._id === categoryId);
      if (!category || !category.subcategories) {
        setAvailableAffiliates([]);
        return;
      }

      // Fetch blogs from all subcategories and aggregate affiliates
      const affiliateMap = new Map();

      for (const subcategory of category.subcategories) {
        try {
          const response = await apiService.getBlogsBySubcategory(categoryId, subcategory._id, { limit: 100 });
          const blogs = response.posts || [];

          blogs.forEach(blog => {
            if (blog.author) {
              const authorId = blog.author._id || blog.author;
              const authorName = blog.author.name || blog.author.email || 'Unknown';
              const authorEmail = blog.author.email || '';

              if (!affiliateMap.has(authorId)) {
                affiliateMap.set(authorId, {
                  id: authorId,
                  name: authorName,
                  email: authorEmail,
                  blogCount: 0
                });
              }
              affiliateMap.get(authorId).blogCount++;
            }
          });
        } catch (error) {
          console.error(`Error loading blogs for subcategory ${subcategory._id}:`, error);
        }
      }

      const affiliates = Array.from(affiliateMap.values()).sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email)
      );

      setAvailableAffiliates(affiliates);
      console.log(`Found ${affiliates.length} unique affiliates with blogs across all subcategories`);
    } catch (error) {
      console.error('Error loading available affiliates from all subcategories:', error);
      toast.error('Failed to load available affiliates');
      setAvailableAffiliates([]);
    } finally {
      setLoadingAffiliates(false);
    }
  };

  const loadAvailableAffiliatesForAllCategories = async () => {
    setLoadingAffiliates(true);
    try {
      // If admin, load all affiliates
      if (isAdmin) {
        try {
          const allAffiliates = await apiService.getAllAffiliates();
          const affiliateList = allAffiliates.map(aff => ({
            id: aff._id,
            name: aff.name || aff.email,
            email: aff.email,
            blogCount: 0 // Will be updated if needed
          }));
          setAvailableAffiliates(affiliateList);
          setLoadingAffiliates(false);
          return;
        } catch (error) {
          console.error('Error loading all affiliates:', error);
          // Fall back to blog-based loading
        }
      }

      // Fetch blogs from all categories and all their subcategories, aggregate affiliates
      const affiliateMap = new Map();

      for (const category of categories) {
        if (!category.subcategories || category.subcategories.length === 0) {
          continue;
        }

        for (const subcategory of category.subcategories) {
          try {
            const response = await apiService.getBlogsBySubcategory(category._id, subcategory._id, { limit: 100 });
            const blogs = response.posts || [];

            blogs.forEach(blog => {
              if (blog.author) {
                const authorId = blog.author._id || blog.author;
                const authorName = blog.author.name || blog.author.email || 'Unknown';
                const authorEmail = blog.author.email || '';

                if (!affiliateMap.has(authorId)) {
                  affiliateMap.set(authorId, {
                    id: authorId,
                    name: authorName,
                    email: authorEmail,
                    blogCount: 0
                  });
                }
                affiliateMap.get(authorId).blogCount++;
              }
            });
          } catch (error) {
            console.error(`Error loading blogs for category ${category._id}, subcategory ${subcategory._id}:`, error);
          }
        }
      }

      const affiliates = Array.from(affiliateMap.values()).sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email)
      );

      setAvailableAffiliates(affiliates);
      console.log(`Found ${affiliates.length} unique affiliates with blogs across all categories`);
    } catch (error) {
      console.error('Error loading available affiliates from all categories:', error);
      toast.error('Failed to load available affiliates');
      setAvailableAffiliates([]);
    } finally {
      setLoadingAffiliates(false);
    }
  };

  const handleAffiliateToggle = (affiliateId) => {
    setSelectedAffiliates(prev => {
      if (prev.includes(affiliateId)) {
        return prev.filter(id => id !== affiliateId);
      } else {
        return [...prev, affiliateId];
      }
    });
  };

  const handleHeadAffiliateChange = (event) => {
    const affiliateId = event.target.value;
    setHeadAffiliate(affiliateId);
    // Remove head affiliate from selected affiliates if it was there
    setSelectedAffiliates(prev => prev.filter(id => id !== affiliateId));
  };

  const handleSelectAllAffiliates = () => {
    // Exclude head affiliate from available affiliates for selection
    const availableForSelection = availableAffiliates.filter(aff => aff.id !== headAffiliate);
    if (selectedAffiliates.length === availableForSelection.length) {
      setSelectedAffiliates([]);
    } else {
      setSelectedAffiliates(availableForSelection.map(aff => aff.id));
    }
  };

  // Helper function to sort blogs by performance
  const sortBlogsByPerformance = async (blogs, affiliateId, criteria = 'views') => {
    try {
      // Fetch performance data for this affiliate
      const performanceData = await apiService.getBlogPerformanceStats(affiliateId);
      const performanceMap = new Map();

      // Create a map of blogPostId -> performance metrics
      // Normalize IDs to strings for consistent comparison
      if (performanceData?.stats?.byBlog) {
        performanceData.stats.byBlog.forEach(blogStat => {
          // Handle both ObjectId and string formats
          let blogId = blogStat.blogPostId;
          if (blogId && typeof blogId !== 'string') {
            blogId = blogId.toString();
          }
          if (blogId) {
            const views = blogStat.totalViews || 0;
            const revenue = blogStat.totalRevenue || blogStat.revenue || 0;
            const revenuePerView = views > 0 ? revenue / views : 0;

            performanceMap.set(blogId, {
              views,
              revenue,
              revenuePerView
            });
          }
        });
      }

      // Sort blogs by performance based on criteria
      return blogs.sort((a, b) => {
        // Normalize blog IDs to strings for comparison
        let aId = a._id;
        let bId = b._id;
        if (aId && typeof aId !== 'string') {
          aId = aId.toString();
        }
        if (bId && typeof bId !== 'string') {
          bId = bId.toString();
        }

        const aStats = performanceMap.get(aId);
        const bStats = performanceMap.get(bId);

        // If both have performance data, sort by selected criteria (descending)
        if (aStats && bStats) {
          if (criteria === 'revenuePerView') {
            return bStats.revenuePerView - aStats.revenuePerView;
          } else {
            // Default to views
            return bStats.views - aStats.views;
          }
        }
        // If only one has performance data, prioritize it
        if (aStats && !bStats) return -1;
        if (!aStats && bStats) return 1;

        // If neither has performance data, randomize
        return Math.random() - 0.5;
      });
    } catch (error) {
      console.error('Error fetching blog performance, randomizing instead:', error);
      // If error fetching performance, randomize
      return blogs.sort(() => Math.random() - 0.5);
    }
  };

  const generatePreBuiltBlog = async () => {
    if (!preBuiltCategory || !preBuiltSubcategory) {
      toast.error('Please select both category and subcategory');
      return;
    }

    setPreBuiltLoading(true);
    try {
      // Variable to hold the final selected blogs
      let selectedBlogs = [];
      let categoriesProcessed = 0;
      let subcategoriesProcessed = 0;

      // Determine which categories to process
      let categoriesToProcess = [];
      if (preBuiltCategory === 'all') {
        // Process all categories
        categoriesToProcess = categories;
      } else {
        // Process single category
        const category = categories.find(c => c._id === preBuiltCategory);
        if (category) {
          categoriesToProcess = [category];
        }
      }

      if (categoriesToProcess.length === 0) {
        toast.error('No categories found');
        setPreBuiltBlogs([]);
        setConcatenatedBlog(null);
        return;
      }

      // Process each category
      for (const category of categoriesToProcess) {
        if (!category.subcategories || category.subcategories.length === 0) {
          continue;
        }

        // Determine which subcategories to process for this category
        let subcategoriesToProcess = [];
        if (preBuiltSubcategory === 'all') {
          // Process all subcategories of this category
          subcategoriesToProcess = category.subcategories;
        } else {
          // Process specific subcategory if it exists in this category
          const subcategory = category.subcategories.find(s => s._id === preBuiltSubcategory);
          if (subcategory) {
            subcategoriesToProcess = [subcategory];
          } else {
            // Skip this category if the selected subcategory doesn't exist in it
            continue;
          }
        }

        if (subcategoriesToProcess.length === 0) {
          continue;
        }

        categoriesProcessed++;

        // Process each subcategory
        for (const subcategory of subcategoriesToProcess) {
          try {
            // Fetch ALL blogs by subcategory from all affiliates (no limit, get all)
            const response = await apiService.getBlogsBySubcategory(category._id, subcategory._id, { limit: 100 });
            let allBlogs = response.posts || [];

            if (allBlogs.length === 0) {
              console.log(`No blogs found for category: ${category.name}, subcategory: ${subcategory.name}`);
              continue;
            }

            subcategoriesProcessed++;

            // Get all unique affiliate IDs from the blogs
            const affiliateIds = new Set();
            allBlogs.forEach(blog => {
              const authorId = blog.author?._id || blog.author;
              if (authorId) {
                affiliateIds.add(authorId.toString());
              }
            });

            // Sort blogs by performance for each affiliate, then combine
            const sortedBlogsByAffiliate = [];
            const zeroTrafficBlogs = []; // Blogs with no views

            for (const affiliateId of affiliateIds) {
              const affiliateBlogs = allBlogs.filter(blog => {
                const authorId = blog.author?._id || blog.author;
                return authorId?.toString() === affiliateId;
              });

              if (affiliateBlogs.length > 0) {
                // Fetch performance data first to identify which blogs have views
                let performanceMap = new Map();
                try {
                  const performanceData = await apiService.getBlogPerformanceStats(affiliateId);

                  if (performanceData?.stats?.byBlog) {
                    performanceData.stats.byBlog.forEach(blogStat => {
                      let blogId = blogStat.blogPostId;
                      if (blogId && typeof blogId !== 'string') {
                        blogId = blogId.toString();
                      }
                      if (blogId) {
                        performanceMap.set(blogId, blogStat.totalViews || 0);
                      }
                    });
                  }
                } catch (error) {
                  console.warn(`Error fetching performance for affiliate ${affiliateId}:`, error);
                }

                // Separate blogs into those with views and those without
                const blogsWithViews = [];
                const blogsWithoutViews = [];

                affiliateBlogs.forEach(blog => {
                  const blogId = (blog._id?.toString() || blog._id)?.toString();
                  const views = performanceMap.get(blogId) || 0;
                  if (views > 0) {
                    blogsWithViews.push(blog);
                  } else {
                    blogsWithoutViews.push(blog);
                  }
                });

                // Sort only the blogs with views by performance
                if (blogsWithViews.length > 0) {
                  const sortedBlogs = await sortBlogsByPerformance(blogsWithViews, affiliateId, rankingCriteria);
                  sortedBlogsByAffiliate.push(...sortedBlogs);
                }

                // Add blogs without views to zero-traffic list
                if (blogsWithoutViews.length > 0) {
                  zeroTrafficBlogs.push(...blogsWithoutViews);
                }

                console.log(`Affiliate ${affiliateId} blog separation:`, {
                  totalBlogs: affiliateBlogs.length,
                  blogsWithViews: blogsWithViews.length,
                  blogsWithoutViews: blogsWithoutViews.length,
                  performanceBlogs: sortedBlogsByAffiliate.length,
                  zeroTrafficBlogs: zeroTrafficBlogs.length,
                  performanceMapSize: performanceMap.size
                });
              }
            }

            console.log(`Blog selection for subcategory ${subcategory.name}:`, {
              totalBlogs: allBlogs.length,
              performanceBlogs: sortedBlogsByAffiliate.length,
              zeroTrafficBlogs: zeroTrafficBlogs.length,
              note: 'All blogs will be included (no limit)'
            });

            // Strategy: Include all performance blogs first, then add all zero-traffic blogs
            // Insert zero-traffic blogs every 3 performance blogs for distribution
            // No limit - include all blogs for management
            const finalBlogsForSubcategory = [];
            let zeroTrafficIndex = 0;

            // If we have performance-ranked blogs, use them and insert zero-traffic blogs
            if (sortedBlogsByAffiliate.length > 0) {
              // Use all performance blogs (no limit)
              for (let i = 0; i < sortedBlogsByAffiliate.length; i++) {
                finalBlogsForSubcategory.push(sortedBlogsByAffiliate[i]);

                // Insert zero traffic blog every 3 blogs (after 3rd, 6th, 9th, etc.)
                if ((i + 1) % 3 === 0 && zeroTrafficBlogs.length > 0) {
                  const zeroTrafficBlog = zeroTrafficBlogs[zeroTrafficIndex % zeroTrafficBlogs.length];
                  finalBlogsForSubcategory.push(zeroTrafficBlog);
                  zeroTrafficIndex++;
                }
              }

              // Add all remaining zero-traffic blogs
              while (zeroTrafficIndex < zeroTrafficBlogs.length) {
                const zeroTrafficBlog = zeroTrafficBlogs[zeroTrafficIndex];
                // Avoid duplicates - check if this blog is already in the list
                const alreadyIncluded = finalBlogsForSubcategory.some(b =>
                  (b._id || b.id) === (zeroTrafficBlog._id || zeroTrafficBlog.id)
                );
                if (!alreadyIncluded) {
                  finalBlogsForSubcategory.push(zeroTrafficBlog);
                }
                zeroTrafficIndex++;
              }
            } else {
              // If no performance-ranked blogs, use all zero-traffic blogs
              finalBlogsForSubcategory.push(...zeroTrafficBlogs);
            }

            // Include all blogs - no limit
            const finalBlogs = finalBlogsForSubcategory;

            console.log(`Final blogs for subcategory ${subcategory.name}:`, {
              count: finalBlogs.length,
              performanceBlogs: finalBlogs.filter(b => sortedBlogsByAffiliate.includes(b)).length,
              zeroTrafficBlogs: finalBlogs.filter(b => zeroTrafficBlogs.includes(b)).length,
              blogIds: finalBlogs.map(b => b._id || b.id)
            });

            // Add final blogs to selectedBlogs
            selectedBlogs.push(...finalBlogs);
          } catch (error) {
            console.error(`Error processing category ${category.name}, subcategory: ${subcategory.name}:`, error);
            // Continue with next subcategory even if one fails
          }
        }
      }

      if (selectedBlogs.length === 0) {
        toast.error('No blogs found for the selected category and subcategory');
        setPreBuiltBlogs([]);
        setConcatenatedBlog(null);
        return;
      }

      // Set the selected blogs
      setPreBuiltBlogs(selectedBlogs);

      console.log('Selected blogs for merging:', {
        totalSelected: selectedBlogs.length,
        blogs: selectedBlogs.map(b => ({
          id: b._id || b.id,
          title: b.title,
          hasContent: !!b.content,
          author: b.author?.email || b.author?.name || b.author?._id || 'Unknown'
        }))
      });

      // Log detailed breakdown
      console.log('Blog selection breakdown:', {
        totalBlogs: selectedBlogs.length,
        uniqueAuthors: [...new Set(selectedBlogs.map(b => {
          const authorId = b.author?._id || b.author;
          return authorId?.toString() || 'Unknown';
        }))].length,
        blogsByAuthor: selectedBlogs.reduce((acc, blog) => {
          const authorId = (blog.author?._id || blog.author)?.toString() || 'Unknown';
          acc[authorId] = (acc[authorId] || 0) + 1;
          return acc;
        }, {})
      });

      // Check content size and reduce blogs if needed
      const maxSize = 14 * 1024 * 1024; // 14MB (leave buffer below 15MB limit)
      let blogsToInclude = [...selectedBlogs];
      let concatenatedContent = '';
      let contentSize = 0;

      // Helper function to calculate UTF-8 byte length (matches backend calculation)
      const getByteLength = (str) => {
        if (typeof str !== 'string') return 0;
        // Use TextEncoder for accurate UTF-8 byte length
        return new TextEncoder().encode(str).length;
      };

      console.log('Starting content size check with', blogsToInclude.length, 'blogs');

      // Try to include all blogs, but reduce if content is too large
      while (blogsToInclude.length > 0) {
        concatenatedContent = concatenateBlogContents(blogsToInclude);
        contentSize = getByteLength(concatenatedContent);

        const sizeMB = (contentSize / (1024 * 1024)).toFixed(2);
        console.log(`Content size check: ${blogsToInclude.length} blogs = ${sizeMB}MB (max: ${(maxSize / (1024 * 1024)).toFixed(2)}MB)`);

        if (contentSize <= maxSize) {
          console.log(`Content fits! Using ${blogsToInclude.length} blogs`);
          break; // Content fits, use all blogs
        }

        // Remove the last blog and try again
        console.log(`Content too large (${sizeMB}MB), reducing blogs from ${blogsToInclude.length} to ${blogsToInclude.length - 1}`);
        blogsToInclude = blogsToInclude.slice(0, -1);
      }

      console.log('Final blogs to include:', {
        count: blogsToInclude.length,
        blogs: blogsToInclude.map(b => ({ id: b._id || b.id, title: b.title }))
      });

      if (blogsToInclude.length === 0) {
        toast.error('Unable to create merged blog: Even a single blog exceeds the size limit (15MB)');
        setPreBuiltBlogs([]);
        setConcatenatedBlog(null);
        return;
      }

      if (blogsToInclude.length < selectedBlogs.length) {
        const removedCount = selectedBlogs.length - blogsToInclude.length;
        toast.warning(`Content too large. Reduced from ${selectedBlogs.length} to ${blogsToInclude.length} blogs to fit within 15MB limit.`);
      }

      // Generate title based on selection
      let title = 'Pre-built Blog: ';
      if (preBuiltCategory === 'all') {
        title += 'All Categories';
      } else {
        const category = categories.find(c => c._id === preBuiltCategory);
        title += category?.name || 'Category';
      }

      if (preBuiltSubcategory === 'all') {
        title += ' - All Subcategories';
      } else if (preBuiltCategory !== 'all') {
        const category = categories.find(c => c._id === preBuiltCategory);
        const subcategoryName = category?.subcategories?.find(s => s._id === preBuiltSubcategory)?.name || 'Subcategory';
        title += ` - ${subcategoryName}`;
      }

      // Prepare source blog IDs
      const sourceBlogIdsArray = blogsToInclude.map(b => {
        const blogId = b._id || b.id;
        if (!blogId) {
          console.warn('Blog missing _id:', b);
        }
        return blogId;
      }).filter(id => id); // Remove any undefined/null IDs

      console.log('Creating merged blog:', {
        blogsToIncludeCount: blogsToInclude.length,
        sourceBlogIdsCount: sourceBlogIdsArray.length,
        sourceBlogIds: sourceBlogIdsArray,
        blogsToInclude: blogsToInclude.map(b => ({ id: b._id || b.id, title: b.title }))
      });

      if (sourceBlogIdsArray.length === 0) {
        toast.error('No valid blog IDs found. Cannot create merged blog.');
        setPreBuiltLoading(false);
        return;
      }

      // Automatically create the pre-built merged blog
      // Only create if we have a specific category and subcategory (not 'all')
      if (preBuiltCategory === 'all' || preBuiltSubcategory === 'all') {
        toast.error('Please select a specific category and subcategory to create a pre-built merged blog.');
        setPreBuiltLoading(false);
        return;
      }

      const blogData = {
        title: title,
        content: concatenatedContent,
        categoryId: preBuiltCategory,
        subcategoryId: preBuiltSubcategory,
        sourceBlogIds: sourceBlogIdsArray,
        firstBlogPageCount: 0, // Will be calculated if needed
        pageAffiliateMapping: {},
        pausedBlogIds: []
      };

      console.log('Creating pre-built merged blog with data:', {
        ...blogData,
        contentSize: `${(contentSize / (1024 * 1024)).toFixed(2)}MB`,
        blogCount: blogsToInclude.length,
        sourceBlogIdsCount: sourceBlogIdsArray.length
      });

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      try {
        const createdPost = await apiService.createPreBuiltMergedBlog(blogData);
        console.log('Pre-built merged blog created/updated:', createdPost);

        // Reload blog posts
        const postsData = await apiService.getMyBlogPosts({ limit: 100 });
        setBlogPosts(postsData.posts || []);

        // Refresh merged blogs list
        await fetchMergedBlogs();

        // Clear the concatenated blog state
        setConcatenatedBlog(null);
        setPreBuiltBlogs([]);

        const categoryText = categoriesProcessed === 1 ? 'category' : 'categories';
        const subcategoryText = subcategoriesProcessed === 1 ? 'subcategory' : 'subcategories';
        toast.success(`Created merged blog with ${blogsToInclude.length} blog posts from ${categoriesProcessed} ${categoryText} and ${subcategoriesProcessed} ${subcategoryText}`);
      } catch (createError) {
        // Handle specific 400 error for content too large
        if (createError.response?.status === 400 && createError.response?.data?.message) {
          const errorMessage = createError.response.data.message;
          console.error('Blog creation failed - content too large:', errorMessage);
          toast.error(errorMessage);
        } else {
          throw createError; // Re-throw to be caught by outer catch
        }
      }
    } catch (error) {
      console.error('Error generating pre-built blog:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error('Failed to create merged blog: ' + errorMessage);
    } finally {
      setPreBuiltLoading(false);
    }
  };

  // Reload a single merged blog
  const reloadMergedBlog = async (mergedBlogId) => {
    try {
      const mergedBlogIdStr = mergedBlogId?.toString();
      if (!mergedBlogIdStr) return;

      // Fetch the updated merged blog
      const updatedBlog = await apiService.getBlogPostById(mergedBlogIdStr);

      // Update the merged blog in state
      setMergedBlogs(prev => prev.map(mb =>
        mb._id?.toString() === mergedBlogIdStr ? updatedBlog : mb
      ));

      // Update paused blogs state from the fetched blog
      if (updatedBlog.pausedBlogIds && Array.isArray(updatedBlog.pausedBlogIds) && updatedBlog.pausedBlogIds.length > 0) {
        setPausedBlogs(prev => ({
          ...prev,
          [mergedBlogIdStr]: new Set(
            updatedBlog.pausedBlogIds.map(id => {
              const idStr = typeof id === 'object' ? id._id || id : id;
              return idStr?.toString();
            }).filter(Boolean)
          )
        }));
      } else {
        // Clear paused blogs for this merged blog if none are paused
        setPausedBlogs(prev => {
          const newState = { ...prev };
          if (newState[mergedBlogIdStr]) {
            delete newState[mergedBlogIdStr];
          }
          return newState;
        });
      }

      // Update source blog map if needed
      if (updatedBlog.sourceBlogIds && Array.isArray(updatedBlog.sourceBlogIds)) {
        const sourceBlogPromises = updatedBlog.sourceBlogIds.map(async (id) => {
          const blogId = typeof id === 'object' ? id._id || id : id;
          const blogIdStr = blogId?.toString();
          if (!blogIdStr) return null;

          // Check if we already have this blog in sourceBlogMap
          if (sourceBlogMap[blogIdStr]) {
            return null; // Already have it
          }

          try {
            const blog = await apiService.getBlogPostById(blogIdStr);
            return { blogId: blogIdStr, blog };
          } catch (error) {
            console.error(`Error fetching source blog ${blogIdStr}:`, error);
            return null;
          }
        });

        const sourceBlogResults = await Promise.all(sourceBlogPromises);
        const newSourceBlogMap = { ...sourceBlogMap };
        sourceBlogResults.forEach((result) => {
          if (result && result.blog) {
            newSourceBlogMap[result.blogId] = result.blog;
          }
        });

        if (Object.keys(newSourceBlogMap).length > Object.keys(sourceBlogMap).length) {
          setSourceBlogMap(newSourceBlogMap);
        }
      }
    } catch (error) {
      console.error('Error reloading merged blog:', error);
      toast.error('Failed to reload merged blog');
    }
  };

  // Fetch all merged blogs (both from BlogPost and PreBuiltMergedBlog collections)
  const fetchMergedBlogs = async () => {
    setLoadingMergedBlogs(true);
    try {
      // Fetch merged blogs from BlogPost collection (legacy)
      const postsData = await apiService.getMyBlogPosts({ limit: 100 });
      const mergedFromBlogPost = (postsData.posts || []).filter(post => post.isMergedBlog);

      // Fetch pre-built merged blogs from PreBuiltMergedBlog collection
      let preBuiltMerged = [];
      try {
        const preBuiltData = await apiService.getAllPreBuiltMergedBlogs();
        preBuiltMerged = preBuiltData.blogs || [];
        // Add a flag to identify these as pre-built blogs
        preBuiltMerged = preBuiltMerged.map(blog => ({
          ...blog,
          isMergedBlog: true,
          isPreBuiltMergedBlog: true
        }));
      } catch (error) {
        console.warn('Error fetching pre-built merged blogs:', error);
      }

      // Combine both sources
      const merged = [...mergedFromBlogPost, ...preBuiltMerged];
      setMergedBlogs(merged);

      // Initialize paused blogs state from merged blogs
      const initialPausedBlogs = {};
      merged.forEach(mergedBlog => {
        if (mergedBlog.pausedBlogIds && Array.isArray(mergedBlog.pausedBlogIds) && mergedBlog.pausedBlogIds.length > 0) {
          const mergedBlogIdStr = mergedBlog._id?.toString();
          initialPausedBlogs[mergedBlogIdStr] = new Set(
            mergedBlog.pausedBlogIds.map(id => {
              const idStr = typeof id === 'object' ? id._id || id : id;
              return idStr?.toString();
            }).filter(Boolean)
          );
        }
      });
      if (Object.keys(initialPausedBlogs).length > 0) {
        setPausedBlogs(initialPausedBlogs);
      }

      // Collect all unique source blog IDs
      const allSourceBlogIds = new Set();
      merged.forEach(mergedBlog => {
        if (mergedBlog.sourceBlogIds && Array.isArray(mergedBlog.sourceBlogIds)) {
          mergedBlog.sourceBlogIds.forEach(id => {
            const blogId = typeof id === 'object' ? id._id || id : id;
            if (blogId) allSourceBlogIds.add(blogId.toString());
          });
        }
      });

      // Fetch all source blog details
      const sourceBlogPromises = Array.from(allSourceBlogIds).map(async (blogId) => {
        try {
          const blog = await apiService.getBlogPostById(blogId);
          return { blogId, blog };
        } catch (error) {
          // Handle 404 errors gracefully (blog might have been deleted)
          if (error.response?.status === 404) {
            console.warn(`Source blog ${blogId} not found (may have been deleted)`);
          } else {
            console.error(`Error fetching source blog ${blogId}:`, error);
          }
          return { blogId, blog: null };
        }
      });

      const sourceBlogResults = await Promise.all(sourceBlogPromises);
      const newSourceBlogMap = {};
      sourceBlogResults.forEach(({ blogId, blog }) => {
        if (blog) {
          newSourceBlogMap[blogId] = blog;
        }
      });

      // Store source blog map in state for easy access
      setSourceBlogMap(newSourceBlogMap);

      // Update blogPosts state with source blogs for display
      const updatedBlogPosts = [...blogPosts];
      sourceBlogResults.forEach(({ blogId, blog }) => {
        if (blog && !updatedBlogPosts.find(bp => bp._id === blogId)) {
          updatedBlogPosts.push(blog);
        }
      });
      setBlogPosts(updatedBlogPosts);

      // Fetch performance data for each unique affiliate
      const affiliateIds = new Set();
      merged.forEach(mergedBlog => {
        if (mergedBlog.author) {
          const authorId = typeof mergedBlog.author === 'object' ? mergedBlog.author._id || mergedBlog.author : mergedBlog.author;
          if (authorId) affiliateIds.add(authorId.toString());
        }
      });

      // Also get affiliate IDs from source blogs
      sourceBlogResults.forEach(({ blog }) => {
        if (blog && blog.author) {
          const authorId = typeof blog.author === 'object' ? blog.author._id || blog.author : blog.author;
          if (authorId) affiliateIds.add(authorId.toString());
        }
      });

      // Fetch performance data for each affiliate
      const performancePromises = Array.from(affiliateIds).map(async (affiliateId) => {
        try {
          const perfData = await apiService.getBlogPerformanceStats(affiliateId);
          return { affiliateId, perfData };
        } catch (error) {
          console.error(`Error fetching performance for affiliate ${affiliateId}:`, error);
          return { affiliateId, perfData: null };
        }
      });

      const performanceResults = await Promise.all(performancePromises);
      const perfMap = {};
      const mergedBlogPerfMap = {};
      const pageStatsMap = {};

      performanceResults.forEach(({ affiliateId, perfData }) => {
        if (perfData && perfData.stats) {
          // Store blog-level stats
          if (perfData.stats.byBlog) {
            perfData.stats.byBlog.forEach(blogStat => {
              if (blogStat.blogPostId) {
                const blogIdStr = blogStat.blogPostId.toString();
                perfMap[blogIdStr] = blogStat;

                // Also store merged blog performance data
                const mergedBlog = merged.find(mb => mb._id?.toString() === blogIdStr);
                if (mergedBlog) {
                  mergedBlogPerfMap[mergedBlog._id] = blogStat;
                }
              }
            });
          }

          // Store page-level stats for offer aggregation
          if (perfData.stats.pageStatsByBlog && Array.isArray(perfData.stats.pageStatsByBlog)) {
            perfData.stats.pageStatsByBlog.forEach(blogPageStat => {
              if (blogPageStat.blogPostId && blogPageStat.pages) {
                const blogIdStr = blogPageStat.blogPostId.toString();
                pageStatsMap[blogIdStr] = blogPageStat.pages;
              }
            });
          }
        }
      });

      setBlogPerformanceData(perfMap);
      setMergedBlogPerformanceData(mergedBlogPerfMap);
      setBlogPageStats(pageStatsMap);
    } catch (error) {
      console.error('Error fetching merged blogs:', error);
      toast.error('Failed to load merged blogs');
    } finally {
      setLoadingMergedBlogs(false);
    }
  };

  // Load merged blogs when tab is switched to prebuilt blog section
  useEffect(() => {
    if (tabValue === 2 && isAdmin) {
      fetchMergedBlogs();
    }
  }, [tabValue, isAdmin]);

  const concatenateBlogContents = (blogs) => {
    // Check if we're in debug mode (development environment)
    const isDebugMode = process.env.NODE_ENV === 'development';

    // Create pages preserving original structure
    const pages = [];
    let pageId = 1;

    blogs.forEach((blog, blogIndex) => {
      try {
        // Try to parse as JSON (new format with pages/elements)
        let blogContent = blog.content;
        if (typeof blogContent === 'string') {
          try {
            blogContent = JSON.parse(blogContent);
          } catch (e) {
            // If not JSON, treat as plain text
            blogContent = [{ id: `text-${blogIndex}`, type: 'text', content: blogContent }];
          }
        }

        // Get affiliate user name for debug mode
        const affiliateName = blog.author?.name || blog.author?.email || 'Unknown Affiliate';

        // Handle pages structure
        if (Array.isArray(blogContent)) {
          // Check if it's a pages array (has id and elements)
          if (blogContent[0]?.elements) {
            // It's a pages structure - preserve each page separately
            blogContent.forEach((page, pageIndex) => {
              if (page.elements && Array.isArray(page.elements)) {
                const pageElements = [...page.elements];

                // Add debug title only to the first page of each blog
                if (isDebugMode && pageIndex === 0) {
                  pageElements.unshift({
                    id: `debug-title-${blogIndex}`,
                    type: 'title',
                    content: `[DEBUG] Blog by: ${affiliateName}`,
                    styles: {
                      fontSize: '0.875rem',
                      color: '#666',
                      fontStyle: 'italic',
                      marginBottom: '10px',
                      textAlign: 'left',
                      fontWeight: 400
                    }
                  });
                }

                // Create a new page preserving original structure
                pages.push({
                  id: pageId++,
                  elements: pageElements
                });
              }
            });
          } else {
            // It's a flat elements array - create a single page for this blog
            const blogElements = [...blogContent];

            // Add debug title at the start
            if (isDebugMode) {
              blogElements.unshift({
                id: `debug-title-${blogIndex}`,
                type: 'title',
                content: `[DEBUG] Blog by: ${affiliateName}`,
                styles: {
                  fontSize: '0.875rem',
                  color: '#666',
                  fontStyle: 'italic',
                  marginBottom: '10px',
                  textAlign: 'left',
                  fontWeight: 400
                }
              });
            }

            pages.push({
              id: pageId++,
              elements: blogElements
            });
          }
        } else {
          // Invalid content structure - create empty page with error message
          const errorElements = [];
          if (isDebugMode) {
            errorElements.push({
              id: `error-${blogIndex}`,
              type: 'text',
              content: `[Error: Invalid blog content structure for blog ${blog._id}]`,
              styles: { color: '#f44336' }
            });
          }
          pages.push({
            id: pageId++,
            elements: errorElements
          });
        }
      } catch (error) {
        console.error(`Error parsing blog ${blog._id}:`, error);
        // Still create a page even if parsing fails
        const errorElements = [];
        if (isDebugMode) {
          errorElements.push({
            id: `error-${blogIndex}`,
            type: 'text',
            content: `[Error parsing blog ${blog._id}: ${error.message}]`,
            styles: { color: '#f44336' }
          });
        }
        pages.push({
          id: pageId++,
          elements: errorElements
        });
      }
    });

    // Return as pages structure (preserving original page structure from each blog)
    return JSON.stringify(pages);
  };

  const generateAffiliateLink = (offer) => {
    if (!offer || !offer._id) {
      console.warn('Invalid offer provided to generateAffiliateLink:', offer);
      return '#';
    }

    // Use config for server URL (defaults to localhost:8080 for testing)
    const serverUrl = config.SERVER_URL || 'http://localhost:8080';

    // Don't generate click_id here - let the API generate it for proper tracking
    // This ensures each click gets a unique ID
    return `${serverUrl}/api/affiliate/redirect/${offer._id}?aff_id=${user.id}`;
  };

  /**
   * Helper to find the nearest offer link above a given element index
   * Searches backwards through all pages to find the most recent 'offer' element
   */
  const getOfferLinkForElement = (pageIdx, elIdx) => {
    const allElements = getAllElements();

    // Calculate absolute index in the flat list of all elements
    let absoluteIndex = 0;
    for (let i = 0; i < pageIdx; i++) {
      if (pages[i]) absoluteIndex += pages[i].elements.length;
    }
    absoluteIndex += elIdx;

    // 1. Search backwards from the absolute index for an offer element
    for (let i = absoluteIndex - 1; i >= 0; i--) {
      const el = allElements[i];
      if (el && el.type === 'offer') {
        const oId = el.offerId || el.id;
        if (oId) {
          return generateAffiliateLink({ _id: oId });
        }
      }
    }

    // 2. Fallback to currently selected offer state
    if (selectedOffer) {
      return generateAffiliateLink(selectedOffer);
    }

    // 3. Fallback to blog's global offerId
    const bOfferId = (editingBlogPost?.offerId?._id || editingBlogPost?.offerId) ||
      (blogForm?.offerId?._id || blogForm?.offerId);
    if (bOfferId) {
      return generateAffiliateLink({ _id: bOfferId });
    }

    return '';
  };

  /**
   * Helper to find the nearest offer name above a given element index
   */
  const getOfferNameForElement = (pageIdx, elIdx) => {
    const allElements = getAllElements();

    // Calculate absolute index in the flat list of all elements
    let absoluteIndex = 0;
    for (let i = 0; i < pageIdx; i++) {
      if (pages[i]) absoluteIndex += pages[i].elements.length;
    }
    absoluteIndex += elIdx;

    // 1. Search backwards from the absolute index for an offer element
    for (let i = absoluteIndex - 1; i >= 0; i--) {
      const el = allElements[i];
      if (el && el.type === 'offer') {
        return el.offerName || 'Unknown Offer';
      }
    }

    // 2. Fallback to currently selected offer state
    if (selectedOffer) {
      return selectedOffer.title || selectedOffer.name || 'Selected Offer';
    }

    // 3. Fallback to the offer linked to the blog being edited
    const bOffer = (editingBlogPost?.offerId) || (blogForm?.offerId);
    if (bOffer) {
      if (typeof bOffer === 'object') return bOffer.title || bOffer.name || 'Blog Offer';
      // Find the offer in the offers list if it's just an ID
      const offerFromList = (offers || []).find(o => (o._id === bOffer || o.id === bOffer));
      if (offerFromList) return offerFromList.title || offerFromList.name || 'Blog Offer';
    }

    return 'None';
  };

  // Function to extract readable text from blog content
  const extractReadableContent = (content) => {
    // Handle undefined/null content (e.g., when content is excluded for admin queries)
    if (!content) {
      return 'No content available';
    }

    try {
      // Try to parse as JSON (new format with blog elements)
      const elements = JSON.parse(content);
      if (Array.isArray(elements)) {
        const text = elements
          .filter(element => element.content)
          .map(element => element.content)
          .join(' ');
        return text ? text.substring(0, 100) : 'No content available';
      }
    } catch (error) {
      // If not JSON, treat as plain text
      if (typeof content === 'string') {
        return content.substring(0, 100);
      }
    }

    // Fallback
    if (typeof content === 'string') {
      return content.substring(0, 100);
    }
    return 'No content available';
  };

  // Reorder blogs within a merged blog
  const handleReorderBlogsInMerged = async (mergedBlogId, newOrder) => {
    try {
      const mergedBlog = mergedBlogs.find(mb => mb._id === mergedBlogId);
      if (!mergedBlog) {
        toast.error('Merged blog not found');
        return;
      }

      // Fetch the source blogs in the new order
      const sourceBlogs = [];
      for (const id of newOrder) {
        const blogId = typeof id === 'object' ? id._id || id : id;
        try {
          const blog = await apiService.getBlogPostById(blogId);
          sourceBlogs.push(blog);
        } catch (error) {
          console.error(`Error fetching source blog ${blogId}:`, error);
        }
      }

      if (sourceBlogs.length === 0) {
        toast.error('Failed to fetch blogs');
        return;
      }

      // Rebuild the concatenated content with new order
      const concatenatedContent = concatenateBlogContents(sourceBlogs);

      // Update the merged blog (preserve paused blog IDs)
      const updatedBlogData = {
        title: mergedBlog.title,
        content: concatenatedContent,
        sourceBlogIds: newOrder,
        pausedBlogIds: mergedBlog.pausedBlogIds || [],
        firstBlogPageCount: mergedBlog.firstBlogPageCount
      };

      await apiService.updateBlogPost(mergedBlogId, updatedBlogData);
      toast.success('Blog order updated');

      // Reload only this merged blog
      await reloadMergedBlog(mergedBlogId);
    } catch (error) {
      console.error('Error reordering blogs in merged blog:', error);
      toast.error('Failed to reorder blogs: ' + (error.message || 'Unknown error'));
    }
  };

  // Remove a blog from a merged blog
  const handleRemoveBlogFromMerged = async (mergedBlogId, sourceBlogId) => {
    try {
      const mergedBlog = mergedBlogs.find(mb => mb._id === mergedBlogId);
      if (!mergedBlog) {
        toast.error('Merged blog not found');
        return;
      }

      // Get the source blog to remove
      const sourceBlogIdStr = typeof sourceBlogId === 'object' ? sourceBlogId._id || sourceBlogId : sourceBlogId;
      const remainingSourceBlogIds = (mergedBlog.sourceBlogIds || []).filter(id => {
        const idStr = typeof id === 'object' ? id._id || id : id;
        return idStr !== sourceBlogIdStr;
      });

      if (remainingSourceBlogIds.length === 0) {
        toast.error('Cannot remove the last blog from a merged blog');
        return;
      }

      // Fetch the source blogs to rebuild content
      const sourceBlogs = [];
      for (const id of remainingSourceBlogIds) {
        const blogId = typeof id === 'object' ? id._id || id : id;
        try {
          const blog = await apiService.getBlogPostById(blogId);
          sourceBlogs.push(blog);
        } catch (error) {
          console.error(`Error fetching source blog ${blogId}:`, error);
        }
      }

      if (sourceBlogs.length === 0) {
        toast.error('Failed to fetch remaining blogs');
        return;
      }

      // Rebuild the concatenated content
      const concatenatedContent = concatenateBlogContents(sourceBlogs);

      // Preserve paused blog IDs (remove the one being deleted if it was paused)
      const currentPausedBlogIds = ((mergedBlog.pausedBlogIds || []).map(id => {
        const idStr = typeof id === 'object' ? id._id || id : id;
        return idStr?.toString();
      })).filter(id => id !== sourceBlogIdStr);

      // Update the merged blog
      const updatedBlogData = {
        title: mergedBlog.title,
        content: concatenatedContent,
        sourceBlogIds: remainingSourceBlogIds,
        pausedBlogIds: currentPausedBlogIds,
        firstBlogPageCount: mergedBlog.firstBlogPageCount // Keep the same for now
      };

      await apiService.updateBlogPost(mergedBlogId, updatedBlogData);
      toast.success('Blog removed from merged blog');

      // Reload only this merged blog
      await reloadMergedBlog(mergedBlogId);
    } catch (error) {
      console.error('Error removing blog from merged blog:', error);
      toast.error('Failed to remove blog: ' + (error.message || 'Unknown error'));
    }
  };


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        toast.error('Please select a valid image or video file');
        return;
      }

      // File size validation removed - allow any size

      setSelectedFile(file);
      console.log('File selected:', file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      console.log('🚀 Starting direct S3 upload for:', selectedFile.name);

      // Direct S3 upload
      const fileUrl = await uploadToS3WithProgress(selectedFile, 'blog-uploads', (progress) => {
        console.log(`Upload progress: ${progress}%`);
      });
      console.log('✅ S3 upload successful:', fileUrl);

      // Update the form with the uploaded file URL
      setBlogForm({
        ...blogForm,
        featuredImage: fileUrl
      });
      setSelectedFile(null);


    } catch (error) {
      console.error('Upload error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });

      toast.error('Upload failed. Please try again later.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Blogs
      </Typography>



      <Tabs value={tabValue} onChange={(e, newValue) => {
        setTabValue(newValue);
        // Reset blog editor state when switching to Create Blog tab (tab 0)
        if (newValue === 0 && !isEditMode && !isViewMode) {
          resetBlogEditorState();
        }
        // Clear URL parameters when switching tabs
        if (newValue === 1) { // My Blogs tab
          navigate('/blogs', { replace: true });
        }
        // If non-admin tries to access pre-built blog tab, redirect to tab 0
        if (newValue === 2 && !isAdmin) {
          setTabValue(0);
        }
      }} sx={{ mb: 3 }}>
        <Tab label={isEditMode ? "Edit Blog" : isViewMode ? "View Blog" : "Create Blog"} />
        <Tab label="My Blogs" />
        {isAdmin && <Tab label="Pre-built Blog" />}
      </Tabs>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Main Column - Offers and Elements */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {isEditMode ? 'Edit Blog Post' : isViewMode ? 'View Blog Post' : 'Select Category and Brand'}
                </Typography>
                {isEditMode && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {editLoading && (
                      <CircularProgress size={20} />
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsEditMode(false);
                        setIsViewMode(false);
                        setEditingBlogPost(null);
                        setBlogForm({
                          title: '',
                          content: '',
                          featuredImage: '',
                          tags: [],
                          mediaTitle: '',
                          mediaDescription: ''
                        });
                        setPages([{ id: 1, elements: [] }]);
                        setCurrentPageIndex(0);
                        setNextElementId(1);
                        setSelectedElementHtml('');
                        setSelectedElement(null);
                      }}
                    >
                      Cancel Edit
                    </Button>
                  </Box>
                )}
                {isViewMode && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsViewMode(false);
                        setIsEditMode(false);
                        setSelectedBlogPost(null);
                        setBlogForm({
                          title: '',
                          content: '',
                          featuredImage: '',
                          tags: [],
                          mediaTitle: '',
                          mediaDescription: ''
                        });
                        setPages([{ id: 1, elements: [] }]);
                        setCurrentPageIndex(0);
                        setNextElementId(1);
                        setSelectedElementHtml('');
                        setSelectedElement(null);
                        setSelectedOfferForElements(null);
                        setSelectedCategory('');
                        setSelectedSubcategory('');
                        setSelectedBrand('');
                        setOffers([]);
                        setSelectedOffer(null);
                        setSavedElements([]);
                      }}
                      startIcon={<Add />}
                    >
                      Create New Blog
                    </Button>
                  </Box>
                )}
              </Box>

              {!isViewMode && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={selectedCategory}
                        label="Category"
                        onChange={handleCategoryChange}
                      >
                        <MenuItem value="">
                          <em>Select a category</em>
                        </MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category._id} value={category._id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth disabled={!selectedCategory}>
                      <InputLabel>Subcategory</InputLabel>
                      <Select
                        value={selectedSubcategory}
                        label="Subcategory"
                        onChange={handleSubcategoryChange}
                      >
                        <MenuItem value="">
                          <em>Select a subcategory</em>
                        </MenuItem>
                        {selectedCategory && categories.find(c => c._id === selectedCategory)?.subcategories?.map((sub) => (
                          <MenuItem key={sub._id} value={sub._id}>
                            {sub.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth disabled={!selectedSubcategory}>
                      <InputLabel>Brand</InputLabel>
                      <Select
                        value={selectedBrand}
                        label="Brand"
                        onChange={handleBrandChange}
                      >
                        <MenuItem value="">
                          <em>
                            {selectedCategory && selectedSubcategory
                              ? 'Select a brand with offer elements'
                              : 'All brands'
                            }
                          </em>
                        </MenuItem>
                        {brands.length > 0 ? (
                          brands.map((brand) => (
                            <MenuItem key={brand._id} value={brand._id}>
                              {brand.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <em>
                              {selectedCategory && selectedSubcategory
                                ? 'No brands have offers with elements in this category/subcategory'
                                : 'Loading brands...'
                              }
                            </em>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {/* Clear Filters Button */}
              {(selectedCategory || selectedSubcategory || selectedBrand) && !isViewMode && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubcategory('');
                      setSelectedBrand('');
                      setOffers([]);
                      setSelectedOffer(null);
                      setSelectedOfferForElements(null);
                      setSavedElements([]);
                    }}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              )}

              {/* Offers and Elements Selection Row */}
              {((!isEditMode && !isViewMode && selectedCategory && selectedSubcategory && selectedBrand) || isEditMode || isViewMode) && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Select Offer and Offer Element
                  </Typography>

                  <Grid container spacing={2} alignItems="center">
                    {/* Offer Selection */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Select Offer</InputLabel>
                        <Select
                          value={selectedOfferForElements?._id || ''}
                          label="Select Offer"
                          onChange={(e) => {
                            const selectedOffer = offers.find(o => o._id === e.target.value);
                            if (selectedOffer) {
                              handleOfferSelectForElements(selectedOffer);
                            }
                          }}
                          disabled={offers.length === 0}
                        >
                          <MenuItem value="">
                            <em>
                              {offersLoading
                                ? 'Loading offers...'
                                : offers.length === 0
                                  ? 'No offers with elements available'
                                  : 'Choose an offer with elements'
                              }
                            </em>
                          </MenuItem>
                          {offers.map((offer) => (
                            <MenuItem key={offer._id} value={offer._id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {offer.brand?.logo && (
                                  <img
                                    src={offer.brand.logo}
                                    alt={offer.brand.name}
                                    style={{
                                      width: 20,
                                      height: 20,
                                      objectFit: 'contain',
                                      borderRadius: '4px'
                                    }}
                                  />
                                )}
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {offer.title}
                                  </Typography>
                                  {offer.discountPercentage && (
                                    <Typography variant="caption" color="error">
                                      {offer.discountPercentage}% OFF
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Offer Element Selection - Grid View */}
                    <Grid item xs={12}>
                      {selectedOfferForElements && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">
                              Select Offer Element
                            </Typography>
                            {selectedElement && (
                              <Chip
                                label={`Selected: ${selectedElement.name || 'Unnamed Element'}`}
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 'medium' }}
                              />
                            )}
                          </Box>
                          {elementsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <CircularProgress />
                            </Box>
                          ) : savedElements.length > 0 ? (
                            <Box>
                              {(() => {
                                const currentElement = savedElements[currentElementIndex];
                                const isSelected = selectedElement?._id === currentElement?._id;
                                const canGoPrevious = currentElementIndex > 0;
                                const canGoNext = currentElementIndex < savedElements.length - 1;

                                return (
                                  <Box key={currentElement?._id || currentElementIndex}>
                                    {/* Navigation Controls */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Button
                                        variant="outlined"
                                        startIcon={<ChevronLeft />}
                                        onClick={() => {
                                          if (canGoPrevious) {
                                            setCurrentElementIndex(currentElementIndex - 1);
                                            setCurrentOfferElementPageIndex(0); // Reset to first page when changing elements
                                          }
                                        }}
                                        disabled={!canGoPrevious}
                                      >
                                        Previous
                                      </Button>

                                      <Typography variant="body2" color="text.secondary">
                                        {currentElementIndex + 1} of {savedElements.length}
                                      </Typography>

                                      <Button
                                        variant="outlined"
                                        endIcon={<ChevronRight />}
                                        onClick={() => {
                                          if (canGoNext) {
                                            setCurrentElementIndex(currentElementIndex + 1);
                                            setCurrentOfferElementPageIndex(0); // Reset to first page when changing elements
                                          }
                                        }}
                                        disabled={!canGoNext}
                                      >
                                        Next
                                      </Button>
                                    </Box>

                                    {/* Full Screen Mobile Preview */}
                                    <Box sx={{
                                      display: 'flex',
                                      gap: 2,
                                      height: 'calc(100vh - 300px)',
                                      minHeight: `${MOBILE_PHONE_HEIGHT + 40 + 100}px`, // Phone frame height + header + padding
                                      width: '100%'
                                    }}>
                                      {/* Mobile Preview Card */}
                                      <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: isSelected ? 2 : 1,
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        borderRadius: 2,
                                        backgroundColor: 'background.paper',
                                        overflow: 'hidden',
                                        boxShadow: 2,
                                        minHeight: `${MOBILE_PHONE_HEIGHT + 40 + 100}px` // Phone frame + header + padding
                                      }}>
                                        {/* Element Name Header */}
                                        <Box sx={{
                                          p: 2,
                                          borderBottom: 1,
                                          borderColor: 'divider',
                                          backgroundColor: isSelected ? 'action.selected' : 'grey.50',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                        }}>
                                          <Typography
                                            variant="h6"
                                            fontWeight={isSelected ? 'bold' : 'normal'}
                                            sx={{
                                              color: isSelected ? 'primary.main' : 'text.primary',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {currentElement.name || `Template ${currentElementIndex + 1}`}
                                          </Typography>
                                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            {isSelected && (
                                              <Chip
                                                label="Selected"
                                                size="small"
                                                color="primary"
                                              />
                                            )}
                                            <Button
                                              variant="contained"
                                              color="primary"
                                              onClick={() => handleElementSelect(currentElement, selectedOfferForElements)}
                                            >
                                              Select
                                            </Button>
                                          </Box>
                                        </Box>

                                        {/* Mobile Preview Content - Phone Frame */}
                                        <Box sx={{
                                          flex: 1,
                                          overflow: 'auto',
                                          p: 2,
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'flex-start',
                                          backgroundColor: '#f5f5f5',
                                          minHeight: `${MOBILE_PHONE_HEIGHT + 40 + 16}px` // Phone frame height + padding
                                        }}>
                                          {/* Phone Frame - Matching Blog Preview */}
                                          <Box sx={{
                                            width: `${MOBILE_PHONE_WIDTH + 20}px`, // Add frame width
                                            height: `${MOBILE_PHONE_HEIGHT + 40}px`, // Add frame height
                                            backgroundColor: '#2c2c2c', // Dark phone frame color
                                            borderRadius: '25px', // Rounded corners like iPhone
                                            padding: '20px 10px', // Frame padding
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)', // Phone-like shadow
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            flexShrink: 0
                                          }}>
                                            <Box sx={{
                                              width: `${MOBILE_PHONE_WIDTH}px`,
                                              height: `${MOBILE_PHONE_HEIGHT}px`,
                                              position: 'relative',
                                              borderRadius: '15px', // Inner screen radius
                                              overflow: 'hidden',
                                              backgroundColor: '#000' // Screen bezel color
                                            }}>
                                              <MobileBlogPreview
                                                key={`offer-preview-${currentElement?._id || currentElementIndex}-${currentOfferElementPageIndex}`}
                                                blogElements={convertOfferElementToBlogElements(currentElement, selectedOfferForElements, currentOfferElementPageIndex, generateAffiliateLink)}
                                                onClose={() => { }}
                                                blogPostId={null}
                                                blogTitle={currentElement.name || `Template ${currentElementIndex + 1}`}
                                                onUpdateElementPadding={() => { }}
                                                onUpdateElementSize={() => { }}
                                                author={user}
                                              />
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Box>

                                      {/* Pagination Controls */}
                                      {(() => {
                                        const totalPages = getTotalPages(currentElement);
                                        const canGoPreviousPage = currentOfferElementPageIndex > 0;
                                        const canGoNextPage = currentOfferElementPageIndex < totalPages - 1;

                                        return (
                                          <Box sx={{
                                            width: '200px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            p: 2,
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            backgroundColor: 'background.paper',
                                            boxShadow: 1
                                          }}>
                                            <Typography variant="subtitle2" fontWeight="bold" textAlign="center">
                                              Pages
                                            </Typography>

                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }}>
                                              {Array.from({ length: totalPages }, (_, i) => (
                                                <Button
                                                  key={i}
                                                  variant={currentOfferElementPageIndex === i ? 'contained' : 'outlined'}
                                                  color={currentOfferElementPageIndex === i ? 'primary' : 'inherit'}
                                                  onClick={() => setCurrentOfferElementPageIndex(i)}
                                                  sx={{
                                                    justifyContent: 'flex-start',
                                                    textTransform: 'none',
                                                    fontWeight: currentOfferElementPageIndex === i ? 'bold' : 'normal'
                                                  }}
                                                >
                                                  Page {i + 1}
                                                </Button>
                                              ))}
                                            </Box>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                              <Button
                                                variant="outlined"
                                                startIcon={<ChevronLeft />}
                                                onClick={() => {
                                                  if (canGoPreviousPage) {
                                                    setCurrentOfferElementPageIndex(currentOfferElementPageIndex - 1);
                                                  }
                                                }}
                                                disabled={!canGoPreviousPage}
                                                fullWidth
                                              >
                                                Previous Page
                                              </Button>

                                              <Typography variant="body2" textAlign="center" color="text.secondary">
                                                {currentOfferElementPageIndex + 1} of {totalPages}
                                              </Typography>

                                              <Button
                                                variant="outlined"
                                                endIcon={<ChevronRight />}
                                                onClick={() => {
                                                  if (canGoNextPage) {
                                                    setCurrentOfferElementPageIndex(currentOfferElementPageIndex + 1);
                                                  }
                                                }}
                                                disabled={!canGoNextPage}
                                                fullWidth
                                              >
                                                Next Page
                                              </Button>
                                            </Box>
                                          </Box>
                                        );
                                      })()}
                                    </Box>
                                  </Box>
                                );
                              })()}
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No offer elements found for the selected offer
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Clear Filters Button */}
              {/* Message when category/subcategory/brand not selected */}
              {(!selectedCategory || !selectedSubcategory || !selectedBrand) && !isViewMode && (
                <Box sx={{ mt: 3, textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Please select a category, subcategory, and brand to view offers
                  </Typography>
                </Box>
              )}

              {/* Enhanced Blog Post Editor with Live Preview */}
              {((selectedElement && selectedOfferForElements) || isEditMode || isViewMode || getAllElements().length > 0) && (
                <Box sx={{ mt: 3, backgroundColor: '#f5f5f5', borderRadius: 1, overflow: 'hidden' }}>
                  {/* Editor Header */}
                  <Box sx={{
                    p: 3,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {isViewMode ? 'View Blog Post' : isEditMode ? 'Edit Blog Post' : 'Create Blog Post'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {editLoading ? 'Loading blog data...' : `${getAllElements().length} elements • ${isViewMode ? 'View Mode' : 'Mobile Editor Mode'}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!isViewMode && (
                        <Button
                          variant="contained"
                          size="small"
                          disabled
                        >
                          Mobile Editor
                        </Button>
                      )}
                      {isViewMode && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setIsViewMode(false);
                            setIsEditMode(true);
                          }}
                          size="small"
                          startIcon={<Edit />}
                        >
                          Edit
                        </Button>
                      )}
                      {(!isViewMode || isEditMode) && (
                        <>
                          <Button
                            variant="contained"
                            onClick={isEditMode ? handleUpdateBlog : handleCreateBlog}
                            disabled={!blogForm.title || getAllElements().length === 0 || creating || editing}
                            startIcon={creating || editing ? <CircularProgress size={20} /> : (isEditMode ? <Edit /> : <Add />)}
                            size="small"
                          >
                            {creating ? 'Creating...' : editing ? 'Updating...' : (isEditMode ? 'Update Blog' : 'Create Blog')}
                          </Button>
                          {createdBlogData && (
                            <Button
                              variant="outlined"
                              onClick={handlePublishBlog}
                              disabled={publishing}
                              startIcon={publishing ? <CircularProgress size={20} /> : <ContentCopy />}
                              size="small"
                              color="success"
                            >
                              {publishing ? 'Publishing...' : 'Publish Created Blog'}
                            </Button>
                          )}
                          {createdBlogData && (
                            <Button
                              variant="text"
                              onClick={() => {
                                setCreatedBlogData(null);
                                setBlogForm({
                                  title: '',
                                  content: '',
                                  featuredImage: '',
                                  tags: [],
                                  mediaTitle: '',
                                  mediaDescription: ''
                                });
                                setPages([{ id: 1, elements: [] }]);
                                setCurrentPageIndex(0);
                                setNextElementId(1);
                                setSelectedFile(null);
                                toast.info('Reset to create new blog');
                              }}
                              size="small"
                              color="secondary"
                            >
                              Reset
                            </Button>
                          )}
                        </>
                      )}

                      {/* Publish button for view mode when viewing unpublished blog */}
                      {isViewMode && !isEditMode && selectedBlogPost && !selectedBlogPost.published && (
                        <Button
                          variant="outlined"
                          onClick={handlePublishBlog}
                          disabled={publishing}
                          startIcon={publishing ? <CircularProgress size={20} /> : <ContentCopy />}
                          size="small"
                          color="success"
                        >
                          {publishing ? 'Publishing...' : 'Publish Blog'}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Main Content Area */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' }
                  }}>
                    {/* Left Panel - Editor */}
                    <Box sx={{
                      flex: 1,
                      minWidth: 0, // Allow flex shrinking
                      maxWidth: 'calc(100% - 600px - 24px)', // Constrain width (accounting for right panel and gap)
                      p: 3,
                      backgroundColor: 'white',
                      borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
                      borderBottom: { xs: '1px solid #e0e0e0', md: 'none' }
                    }}>
                      {/* Blog Name and Description Fields */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          BLOG NAME
                        </Typography>
                        <TextField
                          fullWidth
                          value={blogForm.title}
                          onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                          placeholder="How to get Protein in Your Body"
                          sx={{ mb: 2 }}
                          InputProps={{ readOnly: isViewMode }}
                        />

                      </Box>

                      {/* Page Navigation */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                          flexWrap: 'wrap',
                          gap: 1
                        }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>
                            PAGE {currentPageIndex + 1} OF {pages.length}
                          </Typography>
                          <Box sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            flexWrap: 'wrap', // Allow wrapping to multiple rows
                            flex: 1,
                            maxWidth: '100%'
                          }}>
                            {pages.map((page, index) => (
                              <Button
                                key={page.id}
                                size="small"
                                variant={index === currentPageIndex ? "contained" : "outlined"}
                                onClick={() => switchToPage(index)}
                                sx={{ minWidth: '40px' }}
                              >
                                {index + 1}
                              </Button>
                            ))}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={addPage}
                              startIcon={<Add />}
                            >
                              Add Page
                            </Button>
                          </Box>
                        </Box>

                      </Box>

                      {/* Blog Elements List */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          BLOG ELEMENTS - PAGE {currentPageIndex + 1}
                        </Typography>

                        {getCurrentPageElements().length === 0 ? (
                          <Box sx={{
                            textAlign: 'center',
                            py: 4,
                            border: '2px dashed #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#fafafa'
                          }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              No elements yet
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Add elements to build your blog
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#fafafa'
                          }}>
                            {getCurrentPageElements().map((element, index) => (
                              /* Render offer elements as unified blocks with controls */
                              element.type === 'offer' ? (
                                <Box key={element.id} sx={{
                                  p: 1.5,
                                  borderBottom: '1px solid #e0e0e0',
                                  backgroundColor: '#e3f2fd',
                                  border: '2px solid #1976d2',
                                  borderRadius: 1,
                                  '&:last-child': { borderBottom: 'none' }
                                }}>
                                  {/* Element Header with Controls */}
                                  <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1
                                  }}>
                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                      🛒 Offer Element
                                    </Typography>
                                    {!isViewMode && (
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => moveElement(index, 'up')}
                                          disabled={index === 0 && currentPageIndex === 0}
                                          sx={{ p: 0.5 }}
                                          title={index === 0 && currentPageIndex > 0 ? "Move to previous page" : "Move up"}
                                        >
                                          <ArrowUpward />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => moveElement(index, 'down')}
                                          disabled={index === getCurrentPageElements().length - 1 && currentPageIndex === pages.length - 1}
                                          sx={{ p: 0.5 }}
                                          title={index === getCurrentPageElements().length - 1 && currentPageIndex < pages.length - 1 ? "Move to next page" : "Move down"}
                                        >
                                          <ArrowDownward />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => removeElement(index)}
                                          sx={{ p: 0.5 }}
                                          color="error"
                                          title="Delete"
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>

                                  {(element.offerName || element.elementName) && (
                                    <Box sx={{ mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                                      {element.offerName && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                          Offer: {element.offerName}
                                        </Typography>
                                      )}
                                      {element.elementName && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                          Element: {element.elementName}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    Inserted offer element - content preview in main editor
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  key={element.id}
                                  onClick={() => setSelectedElementIndex(selectedElementIndex === index ? null : index)}
                                  sx={{
                                    p: 1.5,
                                    borderBottom: '1px solid #e0e0e0',
                                    backgroundColor: selectedElementIndex === index ? '#f0f7ff' : 'white',
                                    border: selectedElementIndex === index ? '2px solid #1976d2' : 'none',
                                    borderRadius: selectedElementIndex === index ? 1 : 0,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      backgroundColor: selectedElementIndex === index ? '#f0f7ff' : '#fafafa'
                                    },
                                    '&:last-child': { borderBottom: 'none' }
                                  }}
                                >
                                  {/* Element Header */}
                                  <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: selectedElementIndex === index ? 1 : 0
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                      {selectedElementIndex === index ? (
                                        <ExpandLess sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                                      ) : (
                                        <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                                      )}
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        {element.type.toUpperCase()}
                                      </Typography>
                                      {selectedElementIndex !== index && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                          {element.type === 'title' && element.content ? `- ${element.content.substring(0, 30)}${element.content.length > 30 ? '...' : ''}` : ''}
                                          {element.type === 'text' && element.content ? `- ${element.content.substring(0, 30)}${element.content.length > 30 ? '...' : ''}` : ''}
                                          {element.type === 'media' && element.mediaUrl ? '- Media uploaded' : element.type === 'media' ? '- No media' : ''}
                                          {element.type === 'bullets' && element.rows ? `- ${element.rows.length} items` : ''}
                                        </Typography>
                                      )}
                                    </Box>
                                    {!isViewMode && (
                                      <Box
                                        sx={{ display: 'flex', gap: 0.5 }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            moveElement(index, 'up');
                                          }}
                                          disabled={index === 0 && currentPageIndex === 0}
                                          sx={{ p: 0.5 }}
                                          title={index === 0 && currentPageIndex > 0 ? "Move to previous page" : "Move up"}
                                        >
                                          <ArrowUpward />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            moveElement(index, 'down');
                                          }}
                                          disabled={index === getCurrentPageElements().length - 1 && currentPageIndex === pages.length - 1}
                                          sx={{ p: 0.5 }}
                                          title={index === getCurrentPageElements().length - 1 && currentPageIndex < pages.length - 1 ? "Move to next page" : "Move down"}
                                        >
                                          <ArrowDownward />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            duplicateElement(index);
                                          }}
                                          sx={{ p: 0.5 }}
                                          title="Duplicate"
                                        >
                                          <ContentCopy />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeElement(index);
                                          }}
                                          sx={{ p: 0.5 }}
                                          color="error"
                                          title="Delete"
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Element Content - Only show when selected */}
                                  {selectedElementIndex === index && (
                                    <Box onClick={(e) => e.stopPropagation()}>
                                      {element.type === 'title' && (
                                        <TextField
                                          fullWidth
                                          value={element.content}
                                          onChange={(e) => updateElementContent(index, e.target.value)}
                                          placeholder="Enter title"
                                          size="small"
                                          sx={{ mb: 1 }}
                                          InputProps={{ readOnly: isViewMode }}
                                        />
                                      )}

                                      {element.type === 'text' && (
                                        <TextField
                                          fullWidth
                                          multiline
                                          rows={4}
                                          value={element.content}
                                          onChange={(e) => updateElementContent(index, e.target.value)}
                                          placeholder="Enter text content"
                                          size="small"
                                          sx={{ mb: 1 }}
                                          InputProps={{ readOnly: isViewMode }}
                                        />
                                      )}

                                      {element.type === 'bullets' && (
                                        <Box>
                                          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            Bullet List Items
                                          </Typography>

                                          {/* Apply icon to all button */}
                                          {element.rows && element.rows.length > 1 && !isViewMode && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  const iconToApply = element.rows[0].icon;
                                                  const newRows = element.rows.map(row => ({ ...row, icon: iconToApply }));
                                                  setPages(prev => prev.map((page, pageIdx) =>
                                                    pageIdx === currentPageIndex
                                                      ? {
                                                        ...page,
                                                        elements: page.elements.map((el, elIdx) =>
                                                          elIdx === index ? { ...el, rows: newRows } : el
                                                        )
                                                      }
                                                      : page
                                                  ));
                                                }}
                                                title="Apply this icon to all items"
                                              >
                                                <span role="img" aria-label="apply to all">🔄</span>
                                              </IconButton>
                                              <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', ml: 1 }}>
                                                Apply first icon to all items
                                              </Box>
                                            </Box>
                                          )}

                                          {element.rows && element.rows.map((row, rowIndex) => (
                                            <Box key={rowIndex} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                              <FormControl size="small" sx={{ minWidth: 80 }}>
                                                <Select
                                                  value={row.icon || 'check'}
                                                  onChange={(e) => {
                                                    const newRows = [...element.rows];
                                                    newRows[rowIndex] = { ...newRows[rowIndex], icon: e.target.value };
                                                    setPages(prev => prev.map((page, pageIdx) =>
                                                      pageIdx === currentPageIndex
                                                        ? {
                                                          ...page,
                                                          elements: page.elements.map((el, elIdx) =>
                                                            elIdx === index ? { ...el, rows: newRows } : el
                                                          )
                                                        }
                                                        : page
                                                    ));
                                                  }}
                                                  disabled={isViewMode}
                                                >
                                                  <MenuItem value="check">
                                                    <Box sx={{ color: '#b6ff3c', fontSize: '1.2rem' }}>✓</Box>
                                                  </MenuItem>
                                                  <MenuItem value="red-x">
                                                    <Box sx={{ color: '#e53e3e', fontWeight: 'bold', fontSize: '1.2rem' }}>✗</Box>
                                                  </MenuItem>
                                                  <MenuItem value="pink-check">
                                                    <Box sx={{
                                                      width: '24px', height: '24px', borderRadius: '50%',
                                                      backgroundColor: '#ff9a9e', display: 'flex',
                                                      alignItems: 'center', justifyContent: 'center',
                                                      fontSize: '1rem', color: 'white', fontWeight: 'bold'
                                                    }}>✓</Box>
                                                  </MenuItem>
                                                  <MenuItem value="dark-check">
                                                    <Box sx={{
                                                      width: '24px', height: '24px', borderRadius: '4px',
                                                      backgroundColor: '#2d3748', display: 'flex',
                                                      alignItems: 'center', justifyContent: 'center',
                                                      fontSize: '1rem', color: 'white', fontWeight: 'bold'
                                                    }}>✓</Box>
                                                  </MenuItem>
                                                  <MenuItem value="green-badge">
                                                    <Box sx={{
                                                      width: '28px', height: '28px', borderRadius: '50%',
                                                      backgroundColor: '#68d391', display: 'flex',
                                                      alignItems: 'center', justifyContent: 'center',
                                                      fontSize: '1.1rem', color: 'white', fontWeight: 'bold',
                                                      border: '2px solid #48bb78'
                                                    }}>✓</Box>
                                                  </MenuItem>
                                                  <MenuItem value="star-badge">
                                                    <Box sx={{
                                                      width: '28px', height: '28px', borderRadius: '50%',
                                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                      fontSize: '1.1rem', color: 'white', fontWeight: 'bold',
                                                      border: '2px solid #5a67d8'
                                                    }}>⭐</Box>
                                                  </MenuItem>
                                                  <MenuItem value="star">
                                                    <Box sx={{ color: '#ffe066', fontSize: '1.2rem' }}>★</Box>
                                                  </MenuItem>
                                                  <MenuItem value="info">
                                                    <Box sx={{ color: '#40c4ff', fontSize: '1.2rem' }}>ℹ</Box>
                                                  </MenuItem>
                                                  <MenuItem value="warning">
                                                    <Box sx={{ color: '#ffb347', fontSize: '1.2rem' }}>⚠</Box>
                                                  </MenuItem>
                                                  <MenuItem value="green-number">
                                                    <Box sx={{
                                                      width: 28, height: 28, borderRadius: '6px',
                                                      background: '#22c55e', display: 'flex',
                                                      alignItems: 'center', justifyContent: 'center',
                                                      color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                                                    }}>{rowIndex + 1}</Box>
                                                  </MenuItem>
                                                  <MenuItem value="circle-number">
                                                    <Box sx={{
                                                      width: 28, height: 28, borderRadius: '50%',
                                                      border: '2px solid #22c55e', display: 'flex',
                                                      alignItems: 'center', justifyContent: 'center',
                                                      color: '#22c55e', fontWeight: 'bold', fontSize: '1.1rem',
                                                      background: 'white'
                                                    }}>{rowIndex + 1}</Box>
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                value={row.text || ''}
                                                onChange={(e) => {
                                                  const newRows = [...element.rows];
                                                  newRows[rowIndex] = { ...newRows[rowIndex], text: e.target.value };
                                                  setPages(prev => prev.map((page, pageIdx) =>
                                                    pageIdx === currentPageIndex
                                                      ? {
                                                        ...page,
                                                        elements: page.elements.map((el, elIdx) =>
                                                          elIdx === index ? { ...el, rows: newRows } : el
                                                        )
                                                      }
                                                      : page
                                                  ));
                                                }}
                                                placeholder={`List item ${rowIndex + 1}`}
                                                InputProps={{ readOnly: isViewMode }}
                                              />
                                              {!isViewMode && (
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => {
                                                    const newRows = element.rows.filter((_, i) => i !== rowIndex);
                                                    setPages(prev => prev.map((page, pageIdx) =>
                                                      pageIdx === currentPageIndex
                                                        ? {
                                                          ...page,
                                                          elements: page.elements.map((el, elIdx) =>
                                                            elIdx === index ? { ...el, rows: newRows } : el
                                                          )
                                                        }
                                                        : page
                                                    ));
                                                  }}
                                                >
                                                  <Delete />
                                                </IconButton>
                                              )}
                                            </Box>
                                          ))}
                                          {!isViewMode && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<Add />}
                                              onClick={() => {
                                                const newRows = [...(element.rows || []), { icon: 'check', text: `List item ${(element.rows?.length || 0) + 1}` }];
                                                setPages(prev => prev.map((page, pageIdx) =>
                                                  pageIdx === currentPageIndex
                                                    ? {
                                                      ...page,
                                                      elements: page.elements.map((el, elIdx) =>
                                                        elIdx === index ? { ...el, rows: newRows } : el
                                                      )
                                                    }
                                                    : page
                                                ));
                                              }}
                                              sx={{ mt: 1, mb: 2 }}
                                            >
                                              Add Item
                                            </Button>
                                          )}

                                          {/* Styling Controls */}
                                          {!isViewMode && (
                                            <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
                                              <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                                🎨 Styling Options
                                              </Typography>

                                              {/* List Item Text Style */}
                                              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#ffffff' }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                  🎨 List Item Text Style
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                  Choose a preset style that applies to all list items
                                                </Typography>

                                                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                                                  𝐓𝐫 Preset Text Styles
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                  Click any style to quickly apply a complete typography set
                                                </Typography>

                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                  {['heading1', 'heading2', 'subheading1', 'subheading2', 'paragraph1'].map(presetName => (
                                                    <Button
                                                      key={presetName}
                                                      size="small"
                                                      variant={element.textStylePreset === presetName ? 'contained' : 'outlined'}
                                                      onClick={() => {
                                                        const preset = getPreset(presetName);
                                                        setPages(prev => prev.map((page, pageIdx) =>
                                                          pageIdx === currentPageIndex
                                                            ? {
                                                              ...page,
                                                              elements: page.elements.map((el, elIdx) =>
                                                                elIdx === index
                                                                  ? {
                                                                    ...el,
                                                                    textStylePreset: presetName,
                                                                    styles: { ...el.styles, ...preset }
                                                                  }
                                                                  : el
                                                              )
                                                            }
                                                            : page
                                                        ));
                                                      }}
                                                      sx={{ textTransform: 'capitalize' }}
                                                    >
                                                      {presetName}
                                                    </Button>
                                                  ))}
                                                </Box>
                                              </Box>

                                              {/* Background Color */}
                                              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#ffffff' }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                  🎨 Background Color
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                  Choose a preset color or enter a custom value
                                                </Typography>

                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1, mb: 2 }}>
                                                  {[
                                                    { name: 'Blue', value: '#2563eb' },
                                                    { name: 'Yellow', value: '#eab308' },
                                                    { name: 'Pink', value: '#ffc0cb' },
                                                    { name: 'Black', value: '#000000' },
                                                    { name: 'White', value: '#ffffff' },
                                                    { name: 'Transparent', value: 'transparent' }
                                                  ].map(color => (
                                                    <Box
                                                      key={color.name}
                                                      onClick={() => {
                                                        setPages(prev => prev.map((page, pageIdx) =>
                                                          pageIdx === currentPageIndex
                                                            ? {
                                                              ...page,
                                                              elements: page.elements.map((el, elIdx) =>
                                                                elIdx === index
                                                                  ? { ...el, styles: { ...el.styles, backgroundColor: color.value } }
                                                                  : el
                                                              )
                                                            }
                                                            : page
                                                        ));
                                                      }}
                                                      sx={{
                                                        width: '100%',
                                                        height: '40px',
                                                        backgroundColor: color.value === 'transparent' ? '#f5f5f5' : color.value,
                                                        border: (element.styles?.backgroundColor || '#ffffff') === color.value ? '3px solid #1976d2' : '1px solid #e0e0e0',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                          opacity: 0.8,
                                                          transform: 'scale(1.05)'
                                                        },
                                                        ...(color.value === 'transparent' && {
                                                          backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                                                          backgroundSize: '8px 8px',
                                                          backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                                                        })
                                                      }}
                                                      title={`${color.name} (${color.value})`}
                                                    />
                                                  ))}
                                                </Box>
                                              </Box>

                                              {/* Text Transform */}
                                              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#ffffff' }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                  🔤 Text Transform
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                  Text Transform
                                                </Typography>

                                                <FormControl fullWidth size="small">
                                                  <Select
                                                    value={element.styles?.textTransform || 'none'}
                                                    onChange={(e) => {
                                                      setPages(prev => prev.map((page, pageIdx) =>
                                                        pageIdx === currentPageIndex
                                                          ? {
                                                            ...page,
                                                            elements: page.elements.map((el, elIdx) =>
                                                              elIdx === index
                                                                ? { ...el, styles: { ...el.styles, textTransform: e.target.value } }
                                                                : el
                                                            )
                                                          }
                                                          : page
                                                      ));
                                                    }}
                                                  >
                                                    <MenuItem value="none">Normal</MenuItem>
                                                    <MenuItem value="uppercase">ALL CAPITAL</MenuItem>
                                                    <MenuItem value="capitalize">First Letter Capital</MenuItem>
                                                    <MenuItem value="lowercase">all lowercase</MenuItem>
                                                  </Select>
                                                </FormControl>
                                              </Box>

                                              {/* Text Color */}
                                              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#ffffff' }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                  🎨 Text Color
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                  Choose a preset color or enter a custom value
                                                </Typography>

                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2 }}>
                                                  {[
                                                    { name: 'Blue', value: '#2563eb' },
                                                    { name: 'Yellow', value: '#eab308' },
                                                    { name: 'Pink', value: '#ffc0cb' },
                                                    { name: 'Black', value: '#000000' }
                                                  ].map(color => (
                                                    <Box
                                                      key={color.name}
                                                      onClick={() => {
                                                        setPages(prev => prev.map((page, pageIdx) =>
                                                          pageIdx === currentPageIndex
                                                            ? {
                                                              ...page,
                                                              elements: page.elements.map((el, elIdx) =>
                                                                elIdx === index
                                                                  ? { ...el, styles: { ...el.styles, color: color.value } }
                                                                  : el
                                                              )
                                                            }
                                                            : page
                                                        ));
                                                      }}
                                                      sx={{
                                                        width: '100%',
                                                        height: '40px',
                                                        backgroundColor: color.value,
                                                        border: (element.styles?.color || '#101022') === color.value ? '3px solid #1976d2' : '1px solid #e0e0e0',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                          opacity: 0.8,
                                                          transform: 'scale(1.05)'
                                                        }
                                                      }}
                                                      title={`${color.name} (${color.value})`}
                                                    />
                                                  ))}
                                                </Box>
                                              </Box>

                                              {/* Spacing & Layout */}
                                              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#ffffff' }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                  📏 Spacing & Layout
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                  Padding
                                                </Typography>

                                                <TextField
                                                  fullWidth
                                                  size="small"
                                                  value={element.styles?.padding || '24px'}
                                                  onChange={(e) => {
                                                    setPages(prev => prev.map((page, pageIdx) =>
                                                      pageIdx === currentPageIndex
                                                        ? {
                                                          ...page,
                                                          elements: page.elements.map((el, elIdx) =>
                                                            elIdx === index
                                                              ? { ...el, styles: { ...el.styles, padding: e.target.value } }
                                                              : el
                                                          )
                                                        }
                                                        : page
                                                    ));
                                                  }}
                                                  placeholder="24px"
                                                  helperText="Space inside the element"
                                                />
                                              </Box>

                                              {/* Border & Shape */}
                                              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#ffffff' }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                  🔲 Border & Shape
                                                </Typography>

                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                  Border
                                                </Typography>
                                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                  <Select
                                                    value={element.styles?.border || 'none'}
                                                    onChange={(e) => {
                                                      setPages(prev => prev.map((page, pageIdx) =>
                                                        pageIdx === currentPageIndex
                                                          ? {
                                                            ...page,
                                                            elements: page.elements.map((el, elIdx) =>
                                                              elIdx === index
                                                                ? { ...el, styles: { ...el.styles, border: e.target.value } }
                                                                : el
                                                            )
                                                          }
                                                          : page
                                                      ));
                                                    }}
                                                  >
                                                    <MenuItem value="none">No Border</MenuItem>
                                                    <MenuItem value="1px solid #e0e0e0">Light Border</MenuItem>
                                                    <MenuItem value="2px solid #000000">Dark Border</MenuItem>
                                                  </Select>
                                                </FormControl>

                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                  Border Radius
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
                                                  Choose corner roundedness
                                                </Typography>
                                                <FormControl fullWidth size="small">
                                                  <Select
                                                    value={element.styles?.borderRadius || '12px'}
                                                    onChange={(e) => {
                                                      setPages(prev => prev.map((page, pageIdx) =>
                                                        pageIdx === currentPageIndex
                                                          ? {
                                                            ...page,
                                                            elements: page.elements.map((el, elIdx) =>
                                                              elIdx === index
                                                                ? { ...el, styles: { ...el.styles, borderRadius: e.target.value } }
                                                                : el
                                                            )
                                                          }
                                                          : page
                                                      ));
                                                    }}
                                                  >
                                                    <MenuItem value="0px">Sharp (0px)</MenuItem>
                                                    <MenuItem value="4px">Slightly Rounded (4px)</MenuItem>
                                                    <MenuItem value="8px">Rounded (8px)</MenuItem>
                                                    <MenuItem value="12px">Rounded (12px)</MenuItem>
                                                    <MenuItem value="16px">Very Rounded (16px)</MenuItem>
                                                    <MenuItem value="24px">Extra Rounded (24px)</MenuItem>
                                                  </Select>
                                                </FormControl>
                                              </Box>
                                            </Box>
                                          )}
                                        </Box>
                                      )}

                                      {element.type === 'media' && (
                                        <Box>
                                          <Typography variant="body2" gutterBottom>
                                            Media: {element.mediaUrl ? 'Uploaded' : 'Not uploaded'}
                                          </Typography>

                                          {/* Robust Media Rendering for Editor List */}
                                          <Box sx={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: '100%',
                                            mb: 2,
                                            border: '1px solid #ccc',
                                            borderRadius: element.styles?.mediaBorderRadius || (element.styles?.borderRadius && !element.showOverlayButton ? element.styles.borderRadius : '0px'),
                                            overflow: 'hidden'
                                          }}>
                                            <Box sx={{
                                              position: 'relative',
                                              width: '100%',
                                              backgroundColor: '#000',
                                              // Padding hack for aspect ratio
                                              pt: (() => {
                                                const ratio = element.styles?.aspectRatio || '16/9';
                                                const [w, h] = ratio.split('/').map(Number);
                                                return w && h ? `${(h / w) * 100}%` : '56.25%';
                                              })(),
                                              minHeight: '150px'
                                            }}>
                                              <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                {(() => {
                                                  const looksLikeLink = (s) => typeof s === 'string' && s.length > 5 && s.includes('.') && s !== 'Click Here' && !s.includes(' ');
                                                  const src = looksLikeLink(element.videoFile) ? element.videoFile :
                                                    (looksLikeLink(element.mediaUrl) ? element.mediaUrl :
                                                      (looksLikeLink(element.videoSrc) ? element.videoSrc : null));

                                                  const isVideo = src?.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i);
                                                  const isYouTube = src?.includes('youtube.com') || src?.includes('youtu.be');

                                                  if (!src) {
                                                    return (
                                                      <Box sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        backgroundColor: '#111',
                                                        color: '#444',
                                                        cursor: isEditMode ? 'pointer' : 'default'
                                                      }}
                                                        // Allow clicking placeholder to trigger upload if no media
                                                        onClick={() => {
                                                          if (!isViewMode && (!element.mediaUrl || isEditMode)) {
                                                            const fileInput = document.getElementById(`media-upload-${element.id}`);
                                                            if (fileInput) fileInput.click();
                                                          }
                                                        }}
                                                      >
                                                        <PlayArrow sx={{ fontSize: 50, opacity: 0.5 }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                                          No media uploaded
                                                        </Typography>
                                                        {!isViewMode && (
                                                          <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', mt: 1 }}>
                                                            Click to upload
                                                          </Typography>
                                                        )}
                                                      </Box>
                                                    );
                                                  }

                                                  if (isVideo) {
                                                    const isOverlayEl = element.type === 'video_with_button_overlay' || element.showOverlayButton;
                                                    const editorRatio = element.styles?.aspectRatio || '16/9';
                                                    const [ew, eh] = editorRatio.split('/').map(Number);
                                                    const isVertEl = ew && eh && eh > ew;
                                                    const isTallVert = ew && eh && (eh / ew) >= 1.5;
                                                    const hasPoster = !!element.thumbnailUrl;
                                                    const needsFill = isOverlayEl || (isTallVert && hasPoster);
                                                    return (
                                                      <CenteredVideoPlayer
                                                        src={src}
                                                        poster={element.thumbnailUrl}
                                                        fillParent={needsFill}
                                                        showControlsBar={!isOverlayEl}
                                                        style={{
                                                          width: '100%',
                                                          height: '100%',
                                                          display: 'block',
                                                          objectFit: needsFill && isVertEl ? 'cover' : (element.styles?.objectFit || 'contain')
                                                        }}
                                                      />
                                                    );
                                                  } else if (isYouTube) {
                                                    const getYouTubeID = (url) => {
                                                      if (!url) return '';
                                                      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                                                      const match = url.match(regExp);
                                                      return (match && match[2].length === 11) ? match[2] : null;
                                                    };
                                                    const videoId = getYouTubeID(src);
                                                    const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : src;
                                                    return (
                                                      <iframe
                                                        src={embedUrl}
                                                        width="100%"
                                                        height="100%"
                                                        frameBorder="0"
                                                        allowFullScreen
                                                        style={{
                                                          width: '100%',
                                                          height: '100%',
                                                          border: 'none'
                                                        }}
                                                      />
                                                    );
                                                  } else {
                                                    return (
                                                      <img
                                                        src={src}
                                                        alt="Blog media"
                                                        style={{
                                                          width: '100%',
                                                          height: '100%',
                                                          objectFit: element.styles?.objectFit || 'contain',
                                                          display: 'block'
                                                        }}
                                                      />
                                                    );
                                                  }
                                                })()}

                                                {/* Overlay Button (editor preview) */}
                                                {(element.type === 'video_with_button_overlay' || element.showOverlayButton) && (
                                                  <Box
                                                    sx={{
                                                      position: 'absolute',
                                                      bottom: getOverlayButtonBottomForEditor(element.styles),
                                                      left: element.styles?.textAlign === 'center' ? '50%' : (element.styles?.textAlign === 'right' ? 'auto' : '24px'),
                                                      right: element.styles?.textAlign === 'right' ? '24px' : 'auto',
                                                      transform: element.styles?.textAlign === 'center' ? 'translateX(-50%)' : 'none',
                                                      width: '55%',
                                                      zIndex: 100,
                                                      display: 'flex',
                                                      justifyContent: element.styles?.textAlign === 'center'
                                                        ? 'center'
                                                        : (element.styles?.textAlign === 'right' ? 'flex-end' : 'flex-start')
                                                    }}
                                                  >
                                                    <Button
                                                      variant="contained"
                                                      sx={{
                                                        background: element.styles?.background || (element.styles?.backgroundColor || '#1976d2'),
                                                        color: element.styles?.color || '#ffffff',
                                                        borderRadius: element.styles?.borderRadius || '8px',
                                                        fontWeight: element.styles?.fontWeight || 'bold',
                                                        fontSize: '14px', // Smaller for editor preview
                                                        padding: '8px 16px',
                                                        boxShadow: element.styles?.boxShadow || '0 4px 12px rgba(0,0,0,0.3)',
                                                        textTransform: element.styles?.textTransform || 'none',
                                                        width: '100%',
                                                        height: '44px', // Reduced fixed height for editor sidebar
                                                        maxHeight: '44px',
                                                        transition: 'all 0.2s cubic-bezier(.25,.8,.25,1)',
                                                        pointerEvents: 'none', // Disable clicks in editor preview to avoid navigation
                                                        ...element.styles // Apply all preset properties
                                                      }}
                                                    >
                                                      {element.content || 'Click Here'}
                                                    </Button>
                                                  </Box>
                                                )}
                                              </Box>
                                            </Box>
                                          </Box>

                                          {/* Upload Controls - Moved below Preview */}
                                          {(!element.mediaUrl || isEditMode) && !isViewMode && (
                                            <Box sx={{ mb: 1 }}>
                                              <input
                                                type="file"
                                                accept="image/*,video/*"
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    handleMediaUploadForElement(index, file);
                                                  }
                                                }}
                                                style={{ display: 'none' }}
                                                id={`media-upload-${element.id}`}
                                              />
                                              {!element.mediaUrl && (
                                                // Only show separate button if no media (redundant with click-to-upload placeholder but good for UX)
                                                <label htmlFor={`media-upload-${element.id}`}>
                                                  <Button
                                                    variant="outlined"
                                                    component="span"
                                                    size="small"
                                                    fullWidth
                                                    disabled={uploading}
                                                    startIcon={<Add />}
                                                  >
                                                    {uploading ? 'Uploading...' : 'Choose Media File'}
                                                  </Button>
                                                </label>
                                              )}
                                              {element.mediaUrl && isEditMode && (
                                                <Box sx={{ mt: 1 }}>
                                                  <label htmlFor={`media-upload-${element.id}`}>
                                                    <Button
                                                      variant="outlined"
                                                      component="span"
                                                      size="small"
                                                      fullWidth
                                                      disabled={uploading}
                                                    >
                                                      {uploading ? 'Uploading...' : 'Replace Media'}
                                                    </Button>
                                                  </label>
                                                </Box>
                                              )}
                                            </Box>
                                          )}

                                          {/* Video Thumbnail Upload - MANDATORY */}
                                          {element.mediaUrl && element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) && (
                                            <Box sx={{ mb: 1 }}>
                                              <Typography variant="body2" gutterBottom>
                                                Thumbnail (Required): {element.thumbnailUrl ? 'Uploaded' : 'Not uploaded'}
                                              </Typography>
                                              {!element.thumbnailUrl && (
                                                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                                                  ⚠️ Thumbnail is required for video elements. Please upload a thumbnail before saving.
                                                </Typography>
                                              )}

                                              {(!element.thumbnailUrl || isEditMode) && !isViewMode && (
                                                <Box sx={{ mb: 1 }}>
                                                  <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                      const file = e.target.files[0];
                                                      if (file) {
                                                        handleThumbnailUploadForElement(index, file);
                                                      }
                                                    }}
                                                    style={{ display: 'none' }}
                                                    id={`thumbnail-upload-${element.id}`}
                                                  />
                                                  <label htmlFor={`thumbnail-upload-${element.id}`}>
                                                    <Button
                                                      variant="outlined"
                                                      component="span"
                                                      size="small"
                                                      fullWidth
                                                      disabled={uploading}
                                                    >
                                                      {uploading ? 'Uploading...' : 'Choose Thumbnail Image'}
                                                    </Button>
                                                  </label>
                                                </Box>
                                              )}

                                              {element.thumbnailUrl && (
                                                <Box sx={{ textAlign: 'center', mb: 1 }}>
                                                  <img
                                                    src={element.thumbnailUrl}
                                                    alt="Video thumbnail"
                                                    style={{ maxWidth: '100%', maxHeight: '60px' }}
                                                  />

                                                  {/* Replace Thumbnail Button in Edit Mode */}
                                                  {isEditMode && (
                                                    <Box sx={{ mt: 1 }}>
                                                      <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                          const file = e.target.files[0];
                                                          if (file) {
                                                            handleThumbnailUploadForElement(index, file);
                                                          }
                                                        }}
                                                        style={{ display: 'none' }}
                                                        id={`thumbnail-replace-${element.id}`}
                                                      />
                                                      <label htmlFor={`thumbnail-replace-${element.id}`}>
                                                        <Button
                                                          variant="outlined"
                                                          component="span"
                                                          size="small"
                                                          disabled={uploading}
                                                        >
                                                          {uploading ? 'Uploading...' : 'Replace Thumbnail'}
                                                        </Button>
                                                      </label>
                                                    </Box>
                                                  )}
                                                </Box>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                      )}

                                      {element.type === 'offer' && (
                                        <Box sx={{
                                          border: '2px solid #1976d2',
                                          borderRadius: 1,
                                          p: 1,
                                          backgroundColor: '#e3f2fd'
                                        }}>
                                          <Typography variant="body2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            🛒 Offer Element
                                          </Typography>
                                          {(element.offerName || element.elementName) && (
                                            <Box sx={{ mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                                              {element.offerName && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  Offer: {element.offerName}
                                                </Typography>
                                              )}
                                              {element.elementName && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  Element: {element.elementName}
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                          <Box
                                            dangerouslySetInnerHTML={{
                                              __html: element.htmlContent || '<p>Offer element will be displayed here</p>'
                                            }}
                                          />
                                        </Box>
                                      )}

                                      {/* Element Styling Options */}
                                      <Box sx={{ mt: 1 }}>
                                        {(element.type === 'title' || element.type === 'text') && (
                                          <BlogPresetSelector
                                            type={element.type}
                                            currentStyle={element.presetName}
                                            currentAlignment={element.styles?.textAlign || 'center'}
                                            onStyleSelect={(presetName, preset) => applyTextPreset(index, presetName, preset)}
                                            onAlignmentSelect={(alignment) => applyTextAlignment(index, alignment)}
                                          />
                                        )}

                                        {(element.type === 'media' || element.type === 'video_with_button_overlay') && (
                                          <>
                                            <BlogPresetSelector
                                              type="media"
                                              currentFrame={element.framePresetName}
                                              onFrameSelect={(presetName, preset) => applyFramePreset(index, presetName, preset)}
                                            />

                                            {/* Video Overlay Button Toggle */}
                                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                              <Button
                                                variant={(element.type === 'video_with_button_overlay' || element.showOverlayButton) ? "contained" : "outlined"}
                                                color={(element.type === 'video_with_button_overlay' || element.showOverlayButton) ? "primary" : "inherit"}
                                                size="small"
                                                fullWidth
                                                startIcon={<SmartButton />}
                                                onClick={() => toggleVideoOverlay(index)}
                                                sx={{ mb: 2 }}
                                              >
                                                {(element.type === 'video_with_button_overlay' || element.showOverlayButton) ? "Remove Overlay Button" : "Insert Overlay Button"}
                                              </Button>

                                              {(element.type === 'video_with_button_overlay' || element.showOverlayButton) && (
                                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SmartButton fontSize="small" /> Button Settings
                                                  </Typography>

                                                  {/* Button Alignment */}
                                                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 80 }}>
                                                      Alignment
                                                    </Typography>
                                                    <ButtonGroup size="small" variant="outlined">
                                                      {['left', 'center', 'right'].map((align) => (
                                                        <Button
                                                          key={align}
                                                          variant={(element.styles?.textAlign || 'center') === align ? 'contained' : 'outlined'}
                                                          onClick={() => applyTextAlignment(index, align)}
                                                        >
                                                          {align === 'left' ? 'Left' : (align === 'center' ? 'Center' : 'Right')}
                                                        </Button>
                                                      ))}
                                                    </ButtonGroup>
                                                  </Box>

                                                  <TextField
                                                    label="Button Text"
                                                    size="small"
                                                    fullWidth
                                                    value={element.content}
                                                    onChange={(e) => {
                                                      setPages(prev => prev.map((page, pageIdx) =>
                                                        pageIdx === currentPageIndex
                                                          ? {
                                                            ...page,
                                                            elements: page.elements.map((el, elIdx) =>
                                                              elIdx === index ? { ...el, content: e.target.value } : el
                                                            )
                                                          }
                                                          : page
                                                      ));
                                                    }}
                                                    sx={{ mb: 2, bgcolor: '#fff' }}
                                                  />

                                                  <TextField
                                                    label="Button offer"
                                                    size="small"
                                                    fullWidth
                                                    value={getOfferNameForElement(currentPageIndex, index)}
                                                    disabled
                                                    sx={{ mb: 2, bgcolor: '#f0f0f0' }}
                                                  />

                                                  <ButtonPresetSelector
                                                    currentStyle={element.styles?.presetStyle}
                                                    onStyleSelect={(presetName, preset) => handleVideoOverlayStyleSelect(index, presetName, preset)}
                                                  />
                                                </Box>
                                              )}
                                            </Box>
                                          </>
                                        )}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              )
                            ))}
                          </Box>
                        )}

                        {/* Add Element Buttons */}
                        {!isViewMode && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => addElement('title')}
                              startIcon={<Title />}
                            >
                              Add Title
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => addElement('text')}
                              startIcon={<TextFields />}
                            >
                              Add Text
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => addElement('media')}
                              startIcon={<Image />}
                            >
                              Add Media
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => addElement('bullets')}
                              startIcon={<FormatListBulleted />}
                            >
                              Insert Bullets
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => addElement('offer')}
                              startIcon={<ShoppingCart />}
                              color="primary"
                            >
                              Insert Offer
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Right Panel - Preview */}
                    <Box sx={{
                      flexShrink: 0, // Prevent shrinking
                      p: 3,
                      backgroundColor: '#f8f9fa',
                      minHeight: { xs: '400px', md: 'auto' },
                      display: 'flex',
                      flexDirection: 'column',
                      width: '600px', // Fixed width for preview panel
                      maxWidth: '600px'
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Mobile Preview
                      </Typography>

                      {/* Overflow Alert */}
                      {showOverflowAlert && (
                        <Alert
                          severity="warning"
                          sx={{ mb: 2 }}
                          action={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleOverflowMoveToNewPage}
                              >
                                Move to Next Page
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={handleOverflowKeepOnCurrentPage}
                              >
                                Keep on Current Page
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={handleOverflowCancel}
                              >
                                Cancel
                              </Button>
                            </Box>
                          }
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Page Overflow Detected
                          </Typography>
                          <Typography variant="body2">
                            Adding this content may exceed the mobile page height limit.
                            Content may be cut off when viewed on mobile devices.
                          </Typography>
                        </Alert>
                      )}

                      <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flex: 1,
                        width: '100%',
                        overflowX: 'auto', // Allow horizontal scroll if needed
                        alignItems: 'flex-start'
                      }}>
                        {/* Main Mobile Preview */}
                        <Box sx={{
                          flexShrink: 0, // Prevent shrinking
                          width: '434px', // Fixed width: MOBILE_PHONE_WIDTH (414) + 20px padding
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start'
                        }}>
                          {/* Phone Frame */}
                          <Box sx={{
                            width: `${MOBILE_PHONE_WIDTH + 20}px`, // Add frame width
                            height: `${MOBILE_PHONE_HEIGHT + 40}px`, // Add frame height
                            backgroundColor: '#2c2c2c', // Dark phone frame color
                            borderRadius: '25px', // Rounded corners like iPhone
                            padding: '20px 10px', // Frame padding
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)', // Phone-like shadow
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}>
                            <Box sx={{
                              width: `${MOBILE_PHONE_WIDTH}px`,
                              height: `${MOBILE_PHONE_HEIGHT}px`,
                              position: 'relative',
                              borderRadius: '15px', // Inner screen radius
                              overflow: 'hidden',
                              backgroundColor: '#000' // Screen bezel color
                            }}>
                              <MobileBlogPreview
                                key={`blog-preview-${getAllElements().length}-${selectedBlogPost?._id || 'new'}`}
                                blogElements={getCurrentPageElements()}
                                onClose={() => { }}
                                blogPostId={selectedBlogPost?._id}
                                blogTitle={blogForm.title || selectedBlogPost?.title}
                                author={user}
                                showProfile={currentPageIndex === 0}
                                onUpdateElementPadding={(elementIndex, paddingType, value) => {
                                  setPages(prev => prev.map((page, pageIndex) =>
                                    pageIndex === currentPageIndex
                                      ? {
                                        ...page,
                                        elements: page.elements.map((element, i) =>
                                          i === elementIndex
                                            ? {
                                              ...element,
                                              styles: {
                                                ...element.styles,
                                                [`padding${paddingType.charAt(0).toUpperCase() + paddingType.slice(1)}`]: value
                                              }
                                            }
                                            : element
                                        )
                                      }
                                      : page
                                  ));
                                }}
                                onUpdateElementSize={(elementIndex, newSize) => {
                                  setPages(prev => prev.map((page, pageIndex) =>
                                    pageIndex === currentPageIndex
                                      ? {
                                        ...page,
                                        elements: page.elements.map((element, i) =>
                                          i === elementIndex
                                            ? {
                                              ...element,
                                              styles: {
                                                ...element.styles,
                                                size: newSize
                                              }
                                            }
                                            : element
                                        )
                                      }
                                      : page
                                  ));
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Thumbnail Panel */}
                        {pages.length > 1 && (
                          <Box sx={{
                            width: 100,
                            flexShrink: 0, // Prevent shrinking
                            backgroundColor: 'white',
                            borderRadius: 1,
                            p: 1,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            maxHeight: '800px', // Fixed max height to prevent growing too tall
                            overflowY: 'auto',
                            overflowX: 'hidden'
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textAlign: 'center' }}>
                              Pages (Drag to reorder)
                            </Typography>
                            <DragDropContext onDragEnd={handlePageDragEnd}>
                              <Droppable droppableId="pages-list">
                                {(provided, snapshot) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{
                                      backgroundColor: snapshot.isDraggingOver ? '#f5f5f5' : 'transparent',
                                      borderRadius: 1,
                                      minHeight: '50px'
                                    }}
                                  >
                                    {pages.map((page, pageIndex) => renderPageThumbnail(page, pageIndex))}
                                    {provided.placeholder}
                                  </Box>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  My Blog Posts
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setError('');
                    const loadBlogPosts = async () => {
                      try {
                        console.log('Manually reloading blog posts...');
                        const postsData = await apiService.getMyBlogPosts({ limit: 100 });
                        console.log('Manual reload response:', postsData);
                        setBlogPosts(postsData.posts || []);
                      } catch (error) {
                        console.error('Error manually reloading blog posts:', error);
                        setError('Failed to reload blog posts: ' + error.message);
                      }
                    };
                    loadBlogPosts();
                  }}
                >
                  Refresh
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Debug Info */}
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Debug Info: {blogPosts.length} posts loaded | User ID: {user?.id || 'Unknown'}
                </Typography>
              </Box>

              {blogPosts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  You haven't created any blog posts yet. Start by creating your first blog post!
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {blogPosts.map((post) => (
                    <Grid item xs={12} md={6} lg={4} key={post._id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
                              {post.title}
                            </Typography>
                            <Chip
                              size="small"
                              label={post.published ? 'Published' : 'Unpublished'}
                              color={post.published ? 'success' : 'default'}
                              variant={post.published ? 'filled' : 'outlined'}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {extractReadableContent(post.content)}...
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {post.tags?.map((tag, index) => (
                              <Chip key={index} label={tag} size="small" />
                            ))}
                          </Box>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleViewBlog(post)}
                          >
                            View
                          </Button>
                          {post.published && (
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<OpenInNew />}
                              onClick={() => {
                                const getPublicDomain = () => {
                                  if (process.env.REACT_APP_PUBLIC_DOMAIN) {
                                    return process.env.REACT_APP_PUBLIC_DOMAIN;
                                  }
                                  const currentOrigin = window.location.origin;
                                  if (process.env.NODE_ENV === 'production' && currentOrigin.startsWith('http:')) {
                                    return currentOrigin.replace('http:', 'https:');
                                  }
                                  return currentOrigin;
                                };
                                const publicUrl = `${getPublicDomain()}/blog/${post.slug}`;
                                window.open(publicUrl, '_blank');
                              }}
                              title="View published blog"
                            >
                              Public
                            </Button>
                          )}
                          <Button
                            size="small"
                            color={post.published ? 'warning' : 'success'}
                            type="button"
                            disabled={listPublishBusyId === post._id}
                            startIcon={
                              listPublishBusyId === post._id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : null
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleListPublishToggle(post);
                            }}
                          >
                            {post.published ? 'Unpublish' : 'Re-publish'}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            type="button"
                            startIcon={<Delete />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setBlogToDelete(post);
                              setDeleteBlogDialogOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && isAdmin && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Pre-built Blog
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select a category and subcategory to automatically create a merged blog containing all blogs from that subcategory.
                Blogs will be ordered by performance and automatically published.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={preBuiltCategory}
                      label="Category"
                      onChange={handlePreBuiltCategoryChange}
                    >
                      <MenuItem value="">
                        <em>Select a category</em>
                      </MenuItem>
                      <MenuItem value="all">
                        <em>All</em>
                      </MenuItem>
                      {categories.map((category) => (
                        category ? (
                          <MenuItem key={category._id || Math.random()} value={category._id}>
                            {category.name || 'Unnamed Category'}
                          </MenuItem>
                        ) : null
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth disabled={!preBuiltCategory || preBuiltCategory === ''}>
                    <InputLabel>Subcategory</InputLabel>
                    <Select
                      value={preBuiltSubcategory}
                      label="Subcategory"
                      onChange={handlePreBuiltSubcategoryChange}
                    >
                      <MenuItem value="">
                        <em>Select a subcategory</em>
                      </MenuItem>
                      {preBuiltCategory && (
                        <MenuItem value="all">
                          <em>All</em>
                        </MenuItem>
                      )}
                      {preBuiltCategory && preBuiltCategory !== 'all' && categories.find(c => c._id === preBuiltCategory)?.subcategories?.map((sub) => (
                        sub ? (
                          <MenuItem key={sub._id || Math.random()} value={sub._id}>
                            {sub.name || 'Unnamed Subcategory'}
                          </MenuItem>
                        ) : null
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Ranking Criteria</InputLabel>
                    <Select
                      value={rankingCriteria}
                      label="Ranking Criteria"
                      onChange={(e) => setRankingCriteria(e.target.value)}
                    >
                      <MenuItem value="views">Views</MenuItem>
                      <MenuItem value="revenuePerView">Revenue Per View</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!preBuiltCategory || !preBuiltSubcategory || preBuiltSubcategory === '' || preBuiltLoading}
                    onClick={generatePreBuiltBlog}
                    sx={{ height: '56px' }}
                  >
                    {preBuiltLoading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Creating Merged Blog...
                      </>
                    ) : (
                      'Create Merged Blog'
                    )}
                  </Button>
                </Grid>
              </Grid>


              {!preBuiltCategory && !preBuiltSubcategory && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Select a category and subcategory to generate a pre-built blog.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Merged Blogs Management Section */}
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">
                  Merged Blogs
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Date Range Filter - Calendar Picker */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      setDateRangePickerAnchor(e.currentTarget);
                      setDateRangePickerOpen(true);
                      setTempStartDate(mergedBlogsStartDate || null);
                      setTempEndDate(mergedBlogsEndDate || null);
                      if (mergedBlogsStartDate) {
                        setStartMonth(new Date(mergedBlogsStartDate));
                      }
                      if (mergedBlogsEndDate) {
                        setEndMonth(new Date(mergedBlogsEndDate));
                      }
                    }}
                    endIcon={<KeyboardArrowDown />}
                    sx={{ minWidth: 250, justifyContent: 'space-between' }}
                  >
                    {formatDateRange(mergedBlogsStartDate, mergedBlogsEndDate)}
                  </Button>

                  {(mergedBlogsStartDate || mergedBlogsEndDate) && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setMergedBlogsStartDate('');
                        setMergedBlogsEndDate('');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={fetchMergedBlogs}
                    disabled={loadingMergedBlogs}
                    startIcon={loadingMergedBlogs ? <CircularProgress size={16} /> : <ViewList />}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                View and manage all generated merged blogs. You can remove individual blogs from merged blogs or edit them.
              </Typography>

              {loadingMergedBlogs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (() => {
                // Filter merged blogs by date range
                let filteredMergedBlogs = mergedBlogs;

                if (mergedBlogsStartDate || mergedBlogsEndDate) {
                  filteredMergedBlogs = mergedBlogs.filter(blog => {
                    // Get blog date as YYYY-MM-DD string for comparison (avoid timezone issues)
                    const blogDateObj = new Date(blog.createdAt || blog._id);
                    const blogDateStr = formatDateForInput(blogDateObj);

                    // Compare date strings directly (YYYY-MM-DD format) - lexicographic comparison works for ISO dates
                    if (mergedBlogsStartDate && mergedBlogsEndDate) {
                      return blogDateStr >= mergedBlogsStartDate && blogDateStr <= mergedBlogsEndDate;
                    } else if (mergedBlogsStartDate) {
                      return blogDateStr >= mergedBlogsStartDate;
                    } else if (mergedBlogsEndDate) {
                      return blogDateStr <= mergedBlogsEndDate;
                    }
                    return true;
                  });
                }

                return filteredMergedBlogs.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {mergedBlogsStartDate || mergedBlogsEndDate
                      ? 'No merged blogs found for the selected date range.'
                      : 'No merged blogs found. Generate a pre-built blog to create one.'}
                  </Alert>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      overflowX: 'auto',
                      gap: 2,
                      pb: 2,
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#888',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: '#555',
                        },
                      },
                    }}
                  >
                    {filteredMergedBlogs.slice(0, mergedBlogsLimit).map((mergedBlog) => {
                      const mergedBlogIdStr = mergedBlog._id?.toString();
                      const sourceBlogIds = mergedBlog.sourceBlogIds || [];
                      const blogsLimit = blogsPerMergedBlogLimit[mergedBlogIdStr] || 5;
                      const displayedBlogs = sourceBlogIds.slice(0, blogsLimit);
                      const hasMoreBlogs = sourceBlogIds.length > blogsLimit;
                      console.log('Rendering merged blog:', {
                        mergedBlogId: mergedBlog._id,
                        mergedBlogTitle: mergedBlog.title,
                        sourceBlogIdsCount: sourceBlogIds.length,
                        sourceBlogIds: sourceBlogIds,
                        sourceBlogIdsType: typeof sourceBlogIds,
                        isArray: Array.isArray(sourceBlogIds)
                      });
                      const perfData = mergedBlogPerformanceData[mergedBlog._id];

                      // Get category and subcategory names
                      let categoryId = null;
                      let subcategoryId = null;

                      if (mergedBlog.categoryId) {
                        categoryId = typeof mergedBlog.categoryId === 'object' && mergedBlog.categoryId !== null
                          ? (mergedBlog.categoryId._id || mergedBlog.categoryId)
                          : mergedBlog.categoryId;
                      }

                      if (mergedBlog.subcategoryId) {
                        subcategoryId = typeof mergedBlog.subcategoryId === 'object' && mergedBlog.subcategoryId !== null
                          ? (mergedBlog.subcategoryId._id || mergedBlog.subcategoryId)
                          : mergedBlog.subcategoryId;
                      }

                      // If merged blog doesn't have category/subcategory, try to get from first source blog
                      if (!categoryId && sourceBlogIds.length > 0) {
                        const firstSourceBlogId = sourceBlogIds[0];
                        const firstSourceBlogIdStr = typeof firstSourceBlogId === 'object' && firstSourceBlogId !== null
                          ? (firstSourceBlogId._id || firstSourceBlogId)?.toString()
                          : firstSourceBlogId?.toString();

                        if (firstSourceBlogIdStr) {
                          const firstSourceBlog = blogPosts.find(bp => {
                            const bpId = bp?._id?.toString();
                            return bpId === firstSourceBlogIdStr;
                          });

                          if (firstSourceBlog) {
                            if (firstSourceBlog.categoryId) {
                              categoryId = typeof firstSourceBlog.categoryId === 'object' && firstSourceBlog.categoryId !== null
                                ? (firstSourceBlog.categoryId._id || firstSourceBlog.categoryId)
                                : firstSourceBlog.categoryId;
                            }
                            if (firstSourceBlog.subcategoryId) {
                              subcategoryId = typeof firstSourceBlog.subcategoryId === 'object' && firstSourceBlog.subcategoryId !== null
                                ? (firstSourceBlog.subcategoryId._id || firstSourceBlog.subcategoryId)
                                : firstSourceBlog.subcategoryId;
                            }
                          }
                        }
                      }

                      const category = categoryId ? categories.find(c => {
                        if (!c || !c._id) return false;
                        const catId = typeof c._id === 'object' && c._id !== null
                          ? (c._id._id || c._id)
                          : c._id;
                        return catId?.toString() === categoryId?.toString();
                      }) : null;

                      const subcategory = category && subcategoryId ? category.subcategories?.find(s => {
                        if (!s || !s._id) return false;
                        const subId = typeof s._id === 'object' && s._id !== null
                          ? (s._id._id || s._id)
                          : s._id;
                        return subId?.toString() === subcategoryId?.toString();
                      }) : null;

                      // Calculate metrics
                      const views = perfData?.count || 0;
                      const clicks = perfData?.clicks || 0;
                      const sales = perfData?.sales || 0;
                      const revenue = perfData?.totalRevenue || perfData?.revenue || 0;
                      const ctr = clicks > 0 ? ((clicks / views) * 100).toFixed(2) : '0.00';
                      const cvr = clicks > 0 ? ((sales / clicks) * 100).toFixed(2) : '0.00';
                      const rpv = views > 0 ? (revenue / views).toFixed(2) : '0.00';

                      return (
                        <Card
                          key={mergedBlog._id}
                          sx={{
                            minWidth: 400,
                            maxWidth: 400,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1.5 }}>
                            <Typography variant="h6" noWrap sx={{ mb: 0 }}>
                              {mergedBlog.title}
                            </Typography>

                            {/* Performance Metrics Section */}
                            <Box sx={{ mt: 0, pt: 0, borderTop: '1px solid #e0e0e0' }}>
                              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { border: '1px solid #e0e0e0', padding: '4px 6px', textAlign: 'center', fontSize: '0.7rem' } }}>
                                <TableBody>
                                  {/* Category and Sub Category - 2 columns in 2 rows */}
                                  <TableRow>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem', padding: '4px 6px' }}>Category</TableCell>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem', padding: '4px 6px' }}>Sub Category</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={{ fontSize: '0.7rem', padding: '4px 6px' }}>{category?.name || 'N/A'}</TableCell>
                                    <TableCell colSpan={2} sx={{ fontSize: '0.7rem', padding: '4px 6px' }}>{subcategory?.name || 'N/A'}</TableCell>
                                  </TableRow>

                                  {/* Engagement Metrics - 3 columns in 2 rows, spanning full width */}
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>Views</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>Clicks</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>CTR</TableCell>
                                    <TableCell sx={{ padding: 0, border: 'none', width: 0, minWidth: 0, maxWidth: 0 }}></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{views.toLocaleString()}</TableCell>
                                    <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{clicks.toLocaleString()}</TableCell>
                                    <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{ctr}%</TableCell>
                                    <TableCell sx={{ padding: 0, border: 'none', width: 0, minWidth: 0, maxWidth: 0 }}></TableCell>
                                  </TableRow>

                                  {/* Conversion and Revenue Metrics - 4 columns in 2 rows */}
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>Sales</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>CVR</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>Revenue</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>RPV</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>{sales.toLocaleString()}</TableCell>
                                    <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>{cvr}%</TableCell>
                                    <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>${revenue.toFixed(2)}</TableCell>
                                    <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>${rpv}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </Box>

                            {/* Containing Blogs List - Always Visible */}
                            <Box sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid #e0e0e0',
                              maxHeight: 400,
                              overflowY: 'auto',
                              '&::-webkit-scrollbar': {
                                width: '6px',
                              },
                              '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '3px',
                                '&:hover': {
                                  background: 'rgba(0, 0, 0, 0.3)',
                                },
                              },
                              // Firefox
                              scrollbarWidth: 'thin',
                              scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                            }}>

                              {sourceBlogIds.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  No blogs in this merged blog.
                                </Typography>
                              ) : (
                                <Box>
                                  {displayedBlogs.map((sourceBlogId, index) => {
                                    const blogId = typeof sourceBlogId === 'object' ? sourceBlogId._id || sourceBlogId : sourceBlogId;
                                    const blogIdStr = blogId?.toString();
                                    const perfData = blogIdStr ? blogPerformanceData[blogIdStr] : null;

                                    // Find the actual blog post if available
                                    const sourceBlog = blogPosts.find(bp => {
                                      const bpId = bp._id?.toString();
                                      return bpId === blogIdStr;
                                    });

                                    // Get blog thumbnail/image
                                    const blogThumbnail = sourceBlog?.featuredImage || null;

                                    // Try to extract first image from content if no featured image
                                    let contentImage = null;
                                    if (!blogThumbnail && sourceBlog?.content) {
                                      try {
                                        const content = typeof sourceBlog.content === 'string'
                                          ? JSON.parse(sourceBlog.content)
                                          : sourceBlog.content;
                                        if (Array.isArray(content)) {
                                          for (const page of content) {
                                            if (page.elements && Array.isArray(page.elements)) {
                                              const mediaElement = page.elements.find(el =>
                                                el.type === 'media' && el.mediaUrl
                                              );
                                              if (mediaElement) {
                                                contentImage = mediaElement.mediaUrl;
                                                break;
                                              }
                                            }
                                          }
                                        }
                                      } catch (e) {
                                        // Ignore parse errors
                                      }
                                    }

                                    const thumbnailUrl = blogThumbnail || contentImage;

                                    // Get affiliate information
                                    const affiliateId = sourceBlog?.author?._id || sourceBlog?.author || 'N/A';
                                    const affiliateName = sourceBlog?.author?.name || sourceBlog?.author?.email || 'N/A';
                                    const affiliateUsername = sourceBlog?.author?.username || 'N/A';

                                    const isPaused = pausedBlogs[mergedBlogIdStr]?.has(blogIdStr) || false;

                                    return (
                                      <Card
                                        key={index}
                                        variant="outlined"
                                        sx={{
                                          mb: 1,
                                          width: '100%',
                                          overflow: 'visible',
                                          opacity: isPaused ? 0.5 : 1,
                                          backgroundColor: isPaused ? '#f5f5f5' : 'transparent',
                                          '&:hover': { backgroundColor: isPaused ? '#f5f5f5' : '#fafafa' }
                                        }}
                                      >
                                        {/* Affiliate and Blog Info Table */}
                                        <Box sx={{ p: 0, borderBottom: '1px solid #e0e0e0' }}>
                                          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { border: '1px solid #e0e0e0', padding: '4px 6px', fontSize: '0.7rem' } }}>
                                            <TableBody>
                                              {/* Affiliate Information */}
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Affiliate ID</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Affiliate Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Username</TableCell>
                                              </TableRow>
                                              <TableRow>
                                                <TableCell sx={{ width: '33.33%', fontSize: '0.7rem' }}>{mapIdToShortString(affiliateId)}</TableCell>
                                                <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={affiliateName}>{affiliateName}</TableCell>
                                                <TableCell sx={{ width: '33.33%', fontSize: '0.7rem' }}>{affiliateUsername}</TableCell>
                                              </TableRow>

                                              {/* Blog Information */}
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem' }}>Blog ID</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem' }} colSpan={2}>Blog Name</TableCell>
                                              </TableRow>
                                              <TableRow>
                                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.7rem' }}>{mapIdToShortString(blogIdStr)}</TableCell>
                                                <TableCell colSpan={2} sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis' }} title={sourceBlog?.title || `Blog ${index + 1}`}>{sourceBlog?.title || `Blog ${index + 1}`}</TableCell>
                                              </TableRow>
                                            </TableBody>
                                          </Table>
                                        </Box>

                                        {/* Blog Preview and Stats Layout */}
                                        <Box sx={{ p: 1.5 }}>
                                          {(() => {
                                            // Extract first page from blog content
                                            let firstPageElements = [];

                                            // Try to get blog from multiple sources
                                            let blogToUse = sourceBlog;

                                            // First try sourceBlogMap (most reliable)
                                            if (!blogToUse || !blogToUse.content) {
                                              blogToUse = sourceBlogMap[blogIdStr] || null;
                                            }

                                            // If still no content, try blogPosts
                                            if (!blogToUse || !blogToUse.content) {
                                              const foundBlog = blogPosts.find(bp => {
                                                const bpId = bp._id?.toString();
                                                return bpId === blogIdStr;
                                              });
                                              if (foundBlog && foundBlog.content) {
                                                blogToUse = foundBlog;
                                              }
                                            }

                                            if (blogToUse?.content) {
                                              try {
                                                const content = typeof blogToUse.content === 'string'
                                                  ? JSON.parse(blogToUse.content)
                                                  : blogToUse.content;

                                                if (Array.isArray(content) && content.length > 0) {
                                                  const firstItem = content[0];

                                                  // New format: pages structure with id and elements
                                                  if (firstItem && firstItem.elements && Array.isArray(firstItem.elements)) {
                                                    firstPageElements = firstItem.elements;
                                                  }
                                                  // Check if first item is an element (old format: flat array of elements)
                                                  else if (firstItem && firstItem.type) {
                                                    // Old format: flat elements array - take first few elements as preview
                                                    firstPageElements = content.slice(0, 5); // Show first 5 elements as preview
                                                  }
                                                  // Check if first item is an array (nested array format)
                                                  else if (Array.isArray(firstItem)) {
                                                    firstPageElements = firstItem;
                                                  }
                                                }
                                              } catch (e) {
                                                console.error('Error parsing blog content for preview:', e);
                                              }
                                            }

                                            // Calculate overall performance stats
                                            const views = perfData?.count || perfData?.totalViews || 0;
                                            const clicks = perfData?.clicks || 0;
                                            const sales = perfData?.sales || 0;
                                            const revenue = perfData?.revenue || perfData?.totalRevenue || 0;

                                            // Aggregate offer-level stats from page-level data
                                            const pageStats = blogPageStats[blogIdStr] || [];
                                            const offerStatsMap = new Map();

                                            // Aggregate page stats by offer
                                            pageStats.forEach(page => {
                                              if (page.offerId && page.offerName) {
                                                const offerIdStr = page.offerId.toString();
                                                if (!offerStatsMap.has(offerIdStr)) {
                                                  offerStatsMap.set(offerIdStr, {
                                                    offerId: offerIdStr,
                                                    offerName: page.offerName,
                                                    views: 0,
                                                    clicks: 0,
                                                    sales: 0,
                                                    revenue: 0
                                                  });
                                                }
                                                const offerStat = offerStatsMap.get(offerIdStr);
                                                offerStat.views += page.views || 0;
                                                offerStat.clicks += page.clicks || 0;
                                                offerStat.sales += page.sales || 0;
                                                offerStat.revenue += page.revenue || page.totalRevenue || 0;
                                              }
                                            });

                                            // Convert to array and calculate metrics
                                            const offerStats = Array.from(offerStatsMap.values()).map(offer => {
                                              const ctr = offer.views > 0 ? ((offer.clicks / offer.views) * 100).toFixed(2) : '0.00';
                                              const cvr = offer.clicks > 0 ? ((offer.sales / offer.clicks) * 100).toFixed(2) : '0.00';
                                              const rpv = offer.views > 0 ? (offer.revenue / offer.views).toFixed(2) : '0.00';
                                              return {
                                                ...offer,
                                                ctr,
                                                cvr,
                                                rpv
                                              };
                                            });

                                            return (
                                              <Box>
                                                {/* Layout: Preview on left, Stats on right */}
                                                <Box sx={{
                                                  display: 'flex',
                                                  gap: 1,
                                                  mt: 1,
                                                  width: '100%',
                                                  overflowX: 'hidden',
                                                  overflowY: 'visible'
                                                }}>
                                                  {/* Left: First Page Preview - Video or Image in square frame */}
                                                  {(() => {
                                                    const cardWidth = 400; // Card maxWidth
                                                    const previewWidth = Math.round(cardWidth / 4); // 1/4 of card width = 100px
                                                    const previewHeight = previewWidth; // Square frame (1:1 aspect ratio)

                                                    // Find first media element (video or image)
                                                    const mediaElement = firstPageElements.find(el =>
                                                      el.type === 'media' && el.mediaUrl
                                                    );

                                                    const isVideo = mediaElement?.mediaUrl?.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i);

                                                    return (
                                                      <Box sx={{
                                                        width: `${previewWidth}px`,
                                                        minWidth: `${previewWidth}px`,
                                                        flexShrink: 0,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 0,
                                                        backgroundColor: '#000',
                                                        height: `${previewHeight}px`,
                                                        maxHeight: `${previewHeight}px`,
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                      }}>
                                                        {mediaElement ? (
                                                          isVideo ? (
                                                            <video
                                                              src={mediaElement.mediaUrl}
                                                              style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                              }}
                                                              controls
                                                            />
                                                          ) : (
                                                            <img
                                                              src={mediaElement.mediaUrl}
                                                              alt="Blog media"
                                                              style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                              }}
                                                            />
                                                          )
                                                        ) : (
                                                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontSize: '0.6rem', color: '#fff' }}>
                                                            No media available
                                                          </Typography>
                                                        )}
                                                      </Box>
                                                    );
                                                  })()}

                                                  {/* Right: Performance Stats Table - Fits card width with truncation */}
                                                  <Box sx={{
                                                    flex: 1,
                                                    overflowX: 'hidden',
                                                    overflowY: 'visible',
                                                    maxWidth: 'calc(100% - 110px)' // Account for preview width + gap
                                                  }}>
                                                    <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { fontSize: '0.5rem', padding: '3px 2px', textAlign: 'center' } }}>
                                                      <TableHead>
                                                        <TableRow>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '20%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Offer</TableCell>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Views</TableCell>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Clicks</TableCell>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '12%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>CTR</TableCell>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Sales</TableCell>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '12%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>CVR</TableCell>
                                                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '11%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Rev</TableCell>
                                                        </TableRow>
                                                      </TableHead>
                                                      <TableBody>
                                                        {/* Overall Blog Stats Row */}
                                                        <TableRow>
                                                          <TableCell sx={{ fontWeight: 'medium', fontSize: '0.5rem', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>content</TableCell>
                                                          <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{views.toLocaleString()}</TableCell>
                                                          <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                          <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                          <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                          <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                          <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                        </TableRow>
                                                        {/* Offer-level stats rows - show first 2 rows */}
                                                        {offerStats.slice(0, 2).map((offer, offerIndex) => (
                                                          <TableRow key={offerIndex}>
                                                            <TableCell sx={{ fontWeight: 'medium', fontSize: '0.5rem', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, textAlign: 'center' }} title={offer.offerName}>{offer.offerName}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.views.toLocaleString()}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.clicks.toLocaleString()}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.ctr}%</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.sales.toLocaleString()}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.cvr}%</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>${offer.revenue.toFixed(2)}</TableCell>
                                                          </TableRow>
                                                        ))}
                                                      </TableBody>
                                                    </Table>
                                                    {/* Expand Button to show full table in popup - Always show if there's any data */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                      <Button
                                                        size="small"
                                                        onClick={() => {
                                                          setExpandedStatsDialog({
                                                            blogId: blogIdStr,
                                                            blogTitle: blogToUse?.title || sourceBlog?.title || `Blog ${index + 1}`,
                                                            offerStats,
                                                            views,
                                                            clicks,
                                                            sales,
                                                            revenue
                                                          });
                                                        }}
                                                        endIcon={<OpenInNew />}
                                                        sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                                      >
                                                        View Full Stats
                                                      </Button>
                                                    </Box>

                                                    {/* Pause, Remove, and Up/Down Buttons */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                                                      {/* Pause and Remove Buttons */}
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={async () => {
                                                          const mergedBlogIdStr = mergedBlog._id?.toString();
                                                          const isPaused = pausedBlogs[mergedBlogIdStr]?.has(blogIdStr) || false;

                                                          // Update local state first
                                                          setPausedBlogs(prev => {
                                                            const newState = { ...prev };
                                                            if (!newState[mergedBlogIdStr]) {
                                                              newState[mergedBlogIdStr] = new Set();
                                                            }

                                                            if (isPaused) {
                                                              // Unpause: remove from paused set
                                                              newState[mergedBlogIdStr] = new Set(newState[mergedBlogIdStr]);
                                                              newState[mergedBlogIdStr].delete(blogIdStr);
                                                            } else {
                                                              // Pause: add to paused set
                                                              newState[mergedBlogIdStr] = new Set(newState[mergedBlogIdStr]);
                                                              newState[mergedBlogIdStr].add(blogIdStr);
                                                            }

                                                            return newState;
                                                          });

                                                          // Persist paused state to backend
                                                          try {
                                                            const mergedBlog = mergedBlogs.find(mb => mb._id?.toString() === mergedBlogIdStr);
                                                            if (!mergedBlog) {
                                                              toast.error('Merged blog not found');
                                                              return;
                                                            }

                                                            // Get current paused blog IDs
                                                            const currentPausedSet = pausedBlogs[mergedBlogIdStr] || new Set();
                                                            const newPausedSet = new Set(currentPausedSet);

                                                            if (isPaused) {
                                                              newPausedSet.delete(blogIdStr);
                                                            } else {
                                                              newPausedSet.add(blogIdStr);
                                                            }

                                                            // Convert Set to array of ObjectIds
                                                            const pausedBlogIdsArray = Array.from(newPausedSet);

                                                            // Update merged blog with paused blog IDs
                                                            await apiService.updateBlogPost(mergedBlogIdStr, {
                                                              pausedBlogIds: pausedBlogIdsArray
                                                            });

                                                            toast.success(isPaused ? 'Blog resumed' : 'Blog paused');

                                                            // Reload only this merged blog to sync state
                                                            await reloadMergedBlog(mergedBlogIdStr);
                                                          } catch (error) {
                                                            console.error('Error updating paused state:', error);
                                                            toast.error('Failed to update paused state');
                                                            // Revert local state on error
                                                            setPausedBlogs(prev => {
                                                              const revertedState = { ...prev };
                                                              if (!revertedState[mergedBlogIdStr]) {
                                                                revertedState[mergedBlogIdStr] = new Set();
                                                              }
                                                              if (isPaused) {
                                                                revertedState[mergedBlogIdStr] = new Set(revertedState[mergedBlogIdStr]);
                                                                revertedState[mergedBlogIdStr].add(blogIdStr);
                                                              } else {
                                                                revertedState[mergedBlogIdStr] = new Set(revertedState[mergedBlogIdStr]);
                                                                revertedState[mergedBlogIdStr].delete(blogIdStr);
                                                              }
                                                              return revertedState;
                                                            });
                                                          }
                                                        }}
                                                        sx={{
                                                          textTransform: 'none',
                                                          minWidth: '70px',
                                                          fontSize: '0.7rem',
                                                          backgroundColor: pausedBlogs[mergedBlog._id?.toString()]?.has(blogIdStr) ? 'warning.light' : 'transparent'
                                                        }}
                                                      >
                                                        {pausedBlogs[mergedBlog._id?.toString()]?.has(blogIdStr) ? 'Resume' : 'Pause'}
                                                      </Button>
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleRemoveBlogFromMerged(mergedBlog._id, sourceBlogId)}
                                                        sx={{ textTransform: 'none', minWidth: '70px', fontSize: '0.7rem' }}
                                                      >
                                                        Remove
                                                      </Button>

                                                      {/* Up/Down Arrows */}
                                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <IconButton
                                                          size="small"
                                                          color="error"
                                                          disabled={index === 0}
                                                          onClick={async () => {
                                                            if (index > 0) {
                                                              const newOrder = [...sourceBlogIds];
                                                              [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                                              await handleReorderBlogsInMerged(mergedBlog._id, newOrder);
                                                            }
                                                          }}
                                                          sx={{ p: 0.5 }}
                                                        >
                                                          <ArrowUpward fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                          size="small"
                                                          color="error"
                                                          disabled={index === sourceBlogIds.length - 1}
                                                          onClick={async () => {
                                                            if (index < sourceBlogIds.length - 1) {
                                                              const newOrder = [...sourceBlogIds];
                                                              [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                                                              await handleReorderBlogsInMerged(mergedBlog._id, newOrder);
                                                            }
                                                          }}
                                                          sx={{ p: 0.5 }}
                                                        >
                                                          <ArrowDownward fontSize="small" />
                                                        </IconButton>
                                                      </Box>
                                                    </Box>
                                                  </Box>
                                                </Box>
                                              </Box>
                                            );
                                          })()}
                                        </Box>
                                      </Card>
                                    );
                                  })}

                                  {/* Load More Blogs Button */}
                                  {hasMoreBlogs && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
                                      <Button
                                        size="small"
                                        onClick={() => {
                                          setBlogsPerMergedBlogLimit(prev => ({
                                            ...prev,
                                            [mergedBlogIdStr]: (prev[mergedBlogIdStr] || 5) + 5
                                          }));
                                        }}
                                        endIcon={<ExpandMore />}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                      >
                                        Load {Math.min(5, sourceBlogIds.length - blogsLimit)} More Blogs
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}

                    {/* Load More Merged Blogs Button */}
                    {filteredMergedBlogs.length > mergedBlogsLimit && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setMergedBlogsLimit(prev => prev + 5);
                          }}
                          endIcon={<ExpandMore />}
                          sx={{ textTransform: 'none' }}
                        >
                          Load {Math.min(5, filteredMergedBlogs.length - mergedBlogsLimit)} More Merged Blogs
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Paper>
          </Grid>
        </Grid>
      )}

      <BlogPostDialog
        open={dialogOpen}
        mode={dialogMode}
        onClose={handleCloseDialog}
        blogForm={blogForm}
        setBlogForm={setBlogForm}
        selectedOffer={selectedOffer}
        selectedBlogPost={selectedBlogPost}
        selectedElementHtml={selectedElementHtml}
        selectedFile={selectedFile}
        uploading={uploading}
        editing={editing}
        onFileSelect={handleFileSelect}
        onFileUpload={handleFileUpload}
        onUpdateBlog={handleUpdateBlog}
        onCreateNew={handleCreateNewFromDialog}
        onSwitchToEdit={handleSwitchToEdit}
        generateAffiliateLink={generateAffiliateLink}
      />

      <DeletePageDialog
        open={deletePageDialogOpen}
        pageToDelete={pageToDelete}
        onCancel={handleDeletePageCancel}
        onConfirm={handleDeletePageConfirm}
      />

      {/* Date Range Picker Popover */}
      <Popover
        open={dateRangePickerOpen}
        anchorEl={dateRangePickerAnchor}
        onClose={() => setDateRangePickerOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { p: 2, minWidth: 700 }
        }}
      >
        <Box>
          {/* Header with Include Today and Quick Select */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 1.5, bgcolor: '#ffeb3b', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeToday}
                  onChange={(e) => setIncludeToday(e.target.checked)}
                  size="small"
                />
              }
              label="Include today"
              sx={{ m: 0 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const startOfYear = new Date(today.getFullYear(), 0, 1);
                setTempStartDate(formatDateForInput(startOfYear));
                setTempEndDate(formatDateForInput(today));
                setStartMonth(startOfYear);
                setEndMonth(today);
              }}
              sx={{ bgcolor: '#fff', borderColor: '#000', '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              This year to date
              <KeyboardArrowDown sx={{ ml: 0.5 }} />
            </Button>
          </Box>

          {/* Two Calendar Grids */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Start Date Calendar */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Start Date</Typography>
              {(() => {
                const daysInMonth = getDaysInMonth(startMonth);
                const firstDay = getFirstDayOfMonth(startMonth);
                const days = [];
                const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

                // Empty cells for days before month starts
                for (let i = 0; i < firstDay; i++) {
                  days.push(null);
                }

                // Days of the month
                for (let i = 1; i <= daysInMonth; i++) {
                  days.push(i);
                }

                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {monthNames[startMonth.getMonth()]} {startMonth.getFullYear()}
                      </Typography>
                      <Box>
                        <IconButton size="small" onClick={() => {
                          const newMonth = new Date(startMonth);
                          newMonth.setMonth(newMonth.getMonth() - 1);
                          setStartMonth(newMonth);
                        }}>
                          <ChevronLeft fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => {
                          const newMonth = new Date(startMonth);
                          newMonth.setMonth(newMonth.getMonth() + 1);
                          setStartMonth(newMonth);
                        }}>
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                        <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold', p: 0.5 }}>
                          {day}
                        </Typography>
                      ))}
                      {days.map((day, idx) => {
                        if (day === null) {
                          return <Box key={idx} sx={{ height: 32 }} />;
                        }
                        const dateStr = formatDateForInput(new Date(startMonth.getFullYear(), startMonth.getMonth(), day));
                        const isSelected = tempStartDate && isSameDay(dateStr, tempStartDate);
                        const isInRange = tempStartDate && tempEndDate && isDateInRange(dateStr, tempStartDate, tempEndDate);

                        return (
                          <Button
                            key={idx}
                            size="small"
                            onClick={() => {
                              if (!tempStartDate || (tempStartDate && tempEndDate)) {
                                // Start new selection
                                setTempStartDate(dateStr);
                                setTempEndDate(null);
                              } else if (tempStartDate && !tempEndDate) {
                                // Complete selection
                                if (new Date(dateStr) >= new Date(tempStartDate)) {
                                  setTempEndDate(dateStr);
                                } else {
                                  setTempEndDate(tempStartDate);
                                  setTempStartDate(dateStr);
                                }
                              }
                            }}
                            sx={{
                              minWidth: 32,
                              height: 32,
                              p: 0,
                              bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.light' : 'transparent',
                              color: isSelected ? 'white' : 'text.primary',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                              },
                            }}
                          >
                            {day}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Box>

            {/* End Date Calendar */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>End Date</Typography>
              {(() => {
                const daysInMonth = getDaysInMonth(endMonth);
                const firstDay = getFirstDayOfMonth(endMonth);
                const days = [];
                const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

                // Empty cells for days before month starts
                for (let i = 0; i < firstDay; i++) {
                  days.push(null);
                }

                // Days of the month
                for (let i = 1; i <= daysInMonth; i++) {
                  days.push(i);
                }

                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {monthNames[endMonth.getMonth()]} {endMonth.getFullYear()}
                      </Typography>
                      <Box>
                        <IconButton size="small" onClick={() => {
                          const newMonth = new Date(endMonth);
                          newMonth.setMonth(newMonth.getMonth() - 1);
                          setEndMonth(newMonth);
                        }}>
                          <ChevronLeft fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => {
                          const newMonth = new Date(endMonth);
                          newMonth.setMonth(newMonth.getMonth() + 1);
                          setEndMonth(newMonth);
                        }}>
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                        <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold', p: 0.5 }}>
                          {day}
                        </Typography>
                      ))}
                      {days.map((day, idx) => {
                        if (day === null) {
                          return <Box key={idx} sx={{ height: 32 }} />;
                        }
                        const dateStr = formatDateForInput(new Date(endMonth.getFullYear(), endMonth.getMonth(), day));
                        const isSelected = tempEndDate && isSameDay(dateStr, tempEndDate);
                        const isInRange = tempStartDate && tempEndDate && isDateInRange(dateStr, tempStartDate, tempEndDate);

                        return (
                          <Button
                            key={idx}
                            size="small"
                            onClick={() => {
                              if (!tempStartDate || (tempStartDate && tempEndDate)) {
                                // Start new selection
                                setTempStartDate(dateStr);
                                setTempEndDate(null);
                              } else if (tempStartDate && !tempEndDate) {
                                // Complete selection
                                if (new Date(dateStr) >= new Date(tempStartDate)) {
                                  setTempEndDate(dateStr);
                                } else {
                                  setTempEndDate(tempStartDate);
                                  setTempStartDate(dateStr);
                                }
                              }
                            }}
                            sx={{
                              minWidth: 32,
                              height: 32,
                              p: 0,
                              bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.light' : 'transparent',
                              color: isSelected ? 'white' : 'text.primary',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                              },
                            }}
                          >
                            {day}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setDateRangePickerOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                let finalStartDate = tempStartDate;
                let finalEndDate = tempEndDate;

                // Apply "Include today" if checked
                if (includeToday && finalEndDate) {
                  const today = formatDateForInput(new Date());
                  if (new Date(today) > new Date(finalEndDate)) {
                    finalEndDate = today;
                  }
                }

                setMergedBlogsStartDate(finalStartDate || '');
                setMergedBlogsEndDate(finalEndDate || '');
                setDateRangePickerOpen(false);
              }}
              sx={{ bgcolor: '#ffeb3b', color: '#000', '&:hover': { bgcolor: '#fdd835' } }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <ExpandedStatsDialog
        stats={expandedStatsDialog}
        onClose={() => setExpandedStatsDialog(null)}
      />

      <DeleteBlogDialog
        open={deleteBlogDialogOpen}
        onClose={() => setDeleteBlogDialogOpen(false)}
        blogToDelete={blogToDelete}
        onConfirm={handleDeleteBlog}
      />
    </Container>
  );
};

export default BlogsPage;
